/* global Highcharts:false */
import { markRaw } from 'vue';
import { mapState } from 'vuex';
import watermarkSvg from '@/assets/branding/logo-grey.svg?raw';
import watermarkDarkSvg from '@/assets/images/logo-white.svg?raw';
import constants from '@/constants';
import log from '@/logger';
import api from '@/services/api';
import { applyChartTheme } from '@/utils/chart-theme';
import { getLeadingTradedContract, hasLastTradePrice } from '@/utils/helpers';

const PINCH_HINT_STORAGE_KEY = 'cms-map-widget-pinch-hint-dismissed';
const DEFAULT_EMPTY_COLOR = '#f2f2f2';
const EMPTY_STATE_LABEL_COLOR = '#a9a9a9';
const MAP_BORDER_COLOR = '#ffffff';
// Dark theme: the map's own colors (SVG attributes cannot use the CSS theme variables).
const DARK_THEME = 'dark';
const DARK_EMPTY_COLOR = '#2b3846';
const DARK_EMPTY_STATE_LABEL_COLOR = '#8095aa';
const DARK_MAP_BORDER_COLOR = '#1b2530';
const DARK_EXPORT_BACKGROUND = '#1b2530';
// Toss-up states (leader at or under the toss-up price, or a split state whose markets are led by
// different parties) share one neutral color whatever the leading party.
const TOSS_UP_COLOR = '#7c3fb6';
const NEUTRAL_KEYLINE_COLOR = '#a9a9a9';
const NEUTRAL_RING_COLOR = '#c8c8c8';
const CHANGE_UP_COLOR = '#2A8856';
const CHANGE_DOWN_COLOR = '#D9534F';
const TOOLTIP_MARKET_ID_ATTRIBUTE = 'data-tooltip-market-id';
const TOOLTIP_CONTRACT_ID_ATTRIBUTE = 'data-tooltip-contract-id';
// The reader's percent/cents choice (when the map shows the toggle), shared by every map widget.
const PRICE_UNIT_STORAGE_KEY = 'cmsMapWidgetPriceUnit';
const PRICE_UNIT_PERCENT = 'percent';
const PRICE_UNIT_CENTS = 'cents';
// Shown in place of the price on a state whose market has settled (a called race).
const CALLED_LABEL_MARK = '&#10003;';
// Exports only contain the chart's SVG (not the HTML overview), so the watermark is drawn into
// the exported chart. A data URI keeps offline PNG/JPEG/PDF export from dropping the image.
const EXPORT_WATERMARK_URI = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(watermarkSvg)}`;
const EXPORT_WATERMARK_DARK_URI = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(watermarkDarkSvg)}`;
const EXPORT_WATERMARK_WIDTH = 62;
const EXPORT_WATERMARK_HEIGHT = 16;
const EXPORT_WATERMARK_INSET = 12;

// Zooming in: the small-state, Hawaii, DC and district boxes are their own layer over the states and
// fade out over the zoom where Rhode Island's own shape gets room for its code and price; the small
// states then carry their labels on their shapes and DC gets a callout tile off the coast. District
// maps also mark each Maine/Nebraska district with a pip on its state and list the districts in the
// state's card.
// Rhode Island's own room (the radius of the largest circle inside its shape, in px): the boxes
// start fading at 11px and are gone at 14px (about 2.9x -> 3.7x on a full-width card).
const BOX_FADE_KEY = 'us-ri';
const BOX_FADE_START_PX = 11;
const BOX_FADE_END_PX = 14;
// A small state's own shape shows its code and price from 14px of room, the code alone from 8px.
const SHAPE_LABEL_FULL_PX = 14;
const SHAPE_LABEL_CODE_PX = 8;
// Those labels (and DC's tile) appear once they fit, then ease in over this time (matches the
// `cms-map-label-in` animation in the stylesheet).
const MAP_LABEL_FADE_IN_MS = 150;
const DISTRICT_STATES = { ME: ['ME01', 'ME02'], NE: ['NE01', 'NE02', 'NE03'] };
// DC's leader line ends this far below DC (more if needed to pass under Delaware's label, which
// reaches about DC_LABEL_HALF_PX either side of its anchor).
const DC_TILE_DROP_PX = 20;
const DC_LABEL_HALF_PX = 12;
// District pips: roughly where each district is, as fractions of the state's box (x from the
// left, y from the top): Maine's 1st in the south, 2nd in the north; Nebraska's 3rd across the
// west, 1st around Lincoln, 2nd at Omaha.
const DISTRICT_PIPS = {
  ME01: { state: 'ME', x: 0.3, y: 0.82 },
  // High in northern Maine, clear of the state's label (code + price).
  ME02: { state: 'ME', x: 0.55, y: 0.2 },
  NE01: { state: 'NE', x: 0.82, y: 0.68 },
  NE02: { state: 'NE', x: 0.93, y: 0.42 },
  NE03: { state: 'NE', x: 0.4, y: 0.45 },
};
const DISTRICT_PIP_RADIUS = 5;
// Clear space between a pip (outside its white edge) and the state's label.
const DISTRICT_PIP_LABEL_GAP_PX = 3;

// The chart-pixel rectangle of a point's HTML data label text, or null when it has none showing.
function getHtmlLabelRect(chart, point) {
  const label = point.dataLabel && (point.dataLabel.div || point.dataLabel.element);
  if (!label || !chart.container) {
    return null;
  }
  const rects = [...label.querySelectorAll('span')]
    .map((span) => span.getBoundingClientRect())
    .filter((rect) => rect.width > 0 && rect.height > 0);
  if (!rects.length) {
    return null;
  }
  const origin = chart.container.getBoundingClientRect();
  return {
    left: Math.min(...rects.map((rect) => rect.left)) - origin.left,
    right: Math.max(...rects.map((rect) => rect.right)) - origin.left,
    top: Math.min(...rects.map((rect) => rect.top)) - origin.top,
    bottom: Math.max(...rects.map((rect) => rect.bottom)) - origin.top,
  };
}

// Reshapes the V2 MapWidget response ({ map, overview, legend, locations }) into the V1 per-location
// rows the renderer reads: map settings and overview on every row, plus the first linked market's
// fields. Locations without a linked market are returned separately (V1 never sent them).
function adaptMapWidgetResponse(data) {
  const settings = (data && data.map) || {};
  // The map's own name/urlName stay off the rows: V1 rows had neither, and the renderer falls
  // back to `item.name` when a market name is missing.
  const rowSettings = Object.keys(settings)
    .filter((key) => key !== 'name' && key !== 'urlName')
    .reduce((acc, key) => ({ ...acc, [key]: settings[key] }), {});
  const overview = data && Array.isArray(data.overview) ? data.overview : [];
  const locations = data && Array.isArray(data.locations) ? data.locations : [];
  const mapData = [];
  const unlinkedLocations = [];

  locations.forEach((location) => {
    const markets = Array.isArray(location.markets) ? location.markets : [];
    if (markets.length === 0) {
      unlinkedLocations.push(location);
      return;
    }

    const primary = markets[0];
    mapData.push({
      ...rowSettings,
      overviewContracts: overview,
      marketId: primary.marketId,
      marketType: primary.marketType,
      isSingleOutcome: primary.isSingleOutcome,
      totalContractCount: primary.totalContractCount,
      marketName: primary.marketName,
      totalSharesTraded: markets.reduce(
        (sum, market) => sum + (Number(market.totalSharesTraded) || 0),
        0,
      ),
      marketImageUrl: primary.marketImageUrl,
      contracts: primary.contracts,
      marketUrl: primary.marketUrl,
      status: primary.status,
      locationCode: location.locationCode,
      overrideColor: location.overrideColor,
      displayType: location.displayType,
      stateValue: location.stateValue,
      markets,
    });
  });

  return {
    mapData,
    legendData: data && Array.isArray(data.legend) ? data.legend : [],
    mapSettings: { ...settings, overviewContracts: overview },
    unlinkedLocations,
  };
}

function addChartWatermark(chart, isDarkTheme) {
  return chart.renderer
    .image(
      isDarkTheme ? EXPORT_WATERMARK_DARK_URI : EXPORT_WATERMARK_URI,
      chart.chartWidth - EXPORT_WATERMARK_WIDTH - EXPORT_WATERMARK_INSET,
      chart.chartHeight - EXPORT_WATERMARK_HEIGHT - EXPORT_WATERMARK_INSET,
      EXPORT_WATERMARK_WIDTH,
      EXPORT_WATERMARK_HEIGHT,
    )
    .attr({ zIndex: 10 })
    .add();
}
const MAP_WIDGET_CONTEXT_MENU_ITEMS = [
  'viewFullscreen',
  'printChart',
  'separator',
  'downloadPNG',
  'downloadJPEG',
  'downloadSVG',
  'downloadPDF',
  'separator',
  'downloadCSV',
  'downloadXLS',
  'separator',
  'viewMapDataTable',
];
const DEFAULT_CONTEXT_MENU_ITEMS = MAP_WIDGET_CONTEXT_MENU_ITEMS.filter(
  (item) => item !== 'viewMapDataTable',
);
const RENDERER_TYPE_ELECTION_ELECTORAL = 'electionelectoral';
const RENDERER_TYPE_ELECTION_SENATE = 'electionsenate';
const RENDERER_TYPE_COLLECTION = 'collection';
const CONTEXT_MENU_OVERFLOW_CLASS = 'cms-map-widget-menu-overflow-open';
const CONTEXT_MENU_VIEWPORT_PADDING = 12;
const CONTEXT_MENU_BOTTOM_UI_SELECTORS = ['.app-privacy-statement', '.app-layout__bottom-nav'];
const PARTY_DEMOCRATIC = 'democratic';
const PARTY_REPUBLICAN = 'republican';
const PARTY_INDEPENDENT = 'independent';
const PARTY_ACCENT_COLORS = {
  [PARTY_DEMOCRATIC]: '#5381e4',
  [PARTY_REPUBLICAN]: '#b3282d',
  [PARTY_INDEPENDENT]: '#c9a227',
};
const WIDTH_PERCENTAGE_THRESHOLD = 5;
const EXPORT_MENU_ANCHOR_SIZE = 28;
const EXPORT_MENU_BUTTON_GAP = 8;
// MapSize (map setting): 0 / missing = the chart's height follows the card's width (a wide card
// gets a larger map), 1 = small, 2 = extra small (fixed height).
const MAP_SIZE_SMALL = 1;
const MAP_SIZE_EXTRA_SMALL = 2;
// PreviewLayout (map setting): 1 = Compact in a story's homepage previews (desktop and mobile),
// which cut the story off at 475px behind a Read More button. The map fills the room left above
// that cutoff: on wide cards the Battle Summary sits beside the map (a seat arc on election maps)
// or in a row above it; narrow cards stack it over the map.
const PREVIEW_LAYOUT_COMPACT = 1;
// The homepage carousel's and the embedded carousel's (CMS pages) story area.
const PREVIEW_CONTENT_SELECTOR = '.preview-carousel__content, .preview-carousel-embed__content';
const PREVIEW_STORY_SELECTOR = '.preview-carousel__story';
const PREVIEW_STORY_ROW_SELECTOR = '.browse-story-content-region__items';
const PREVIEW_CUTOFF_HEIGHT = 475;
const PREVIEW_CUTOFF_MARGIN = 4;
// The height used until the widget has measured where it starts in the preview.
const COMPACT_FALLBACK_HEIGHT = 440;
// Wide enough for the summary column beside the map; narrower compact maps stack.
const COMPACT_SIDE_MIN_WIDTH = 720;
const COMPACT_LAYOUT_SIDE = 'side';
const COMPACT_LAYOUT_ROW = 'row';
const COMPACT_LAYOUT_STACK = 'stack';
// Never shorter than this, even when content above the map leaves less room (the preview then
// shows its Read More, as for any long story): below it the map or tiles get too small to read.
const COMPACT_MIN_HEIGHTS = {
  [COMPACT_LAYOUT_SIDE]: 320,
  [COMPACT_LAYOUT_ROW]: 280,
  [COMPACT_LAYOUT_STACK]: 400,
};
// A short side layout tightens its summary column until it fits: 1 = the key moves to the info
// popover, 2 = the counts' notes go and the arc gets smaller.
const COMPACT_TIGHT_MAX = 2;
// Seat arc: a half ring in a 300x160 box, Democrats from the left and Republicans from the right
// (the seat bar's segments, in its order and widths).
const ARC_CENTER_X = 150;
const ARC_CENTER_Y = 148;
const ARC_OUTER_RADIUS = 140;
const ARC_INNER_RADIUS = 94;
// The tapped tile's card sits over the tiles inside a compact map; for a tile in the lower rows
// it moves to the top so it doesn't cover that tile.
const COMPACT_TILE_CARD_TOP_ROW = 4;
// Compact phone tiles with room to spare above the cutoff: a short list under the map (closest
// races, leading states or most markets) takes the rows that fit, then the tiles grow taller into
// the rest, up to this many times their width. The list's heights match the stylesheet.
const COMPACT_TILE_STRETCH_MAX = 1.2;
const COMPACT_LIST_BASE_HEIGHT = 32;
const COMPACT_LIST_ROW_HEIGHT = 24;
// DisplayMode (map setting): which parts of the widget show.
const DISPLAY_MODE_MAP_AND_SUMMARY = 0;
const DISPLAY_MODE_SUMMARY_ONLY = 1;
const DISPLAY_MODE_MAP_ONLY = 2;
// Battle Summary wording. Party contracts ("Democratic", "Republican") read as the party's
// members; any other leading contract reads as its own name.
const PARTY_PLURAL_NAMES = {
  [PARTY_DEMOCRATIC]: 'Democrats',
  [PARTY_REPUBLICAN]: 'Republicans',
};
const PARTY_CONTRACT_NAME = /^(democrat|republican)/i;
// "an 8%, 11%, 18%, 80-89% chance": the percents that are read aloud with a leading vowel sound.
const AN_PERCENT = /^(8\d?|11|18)$/;
const DEFAULT_RACE_LABEL = 'Senate';
const DEFAULT_BALLOT_NOUN = 'seats';
const ELECTION_TIME_ZONE = 'America/New_York';
const SUMMARY_INFO_TEXT = 'Chances are based on PredictIt market prices.';
const DAY_MS = 24 * 60 * 60 * 1000;
// LEADER pills follow the avatar ring colour: a light tint behind text shaded 20% darker.
const LEADER_PILL_TINT_ALPHA = 0.16;
const LEADER_PILL_TEXT_SHADE = 0.2;
// Scroll-wheel zoom is off (it hijacked page scrolling); the bottom-left control zooms
// from 1x up to this factor, alongside pinch zoom and panning.
const MAP_ZOOM_MAX_FACTOR = 8;
const MAP_ZOOMED_THRESHOLD = 1.05;
// Map tooltip hover intent: the tooltip is pinned beside the point where the pointer
// settled, and only switches to another state after the pointer rests on it briefly,
// so users can cross neighbouring states to reach the tooltip.
const TOOLTIP_INTENT_DELAY = 120;
// Generic tooltips list up to this many contracts, in the order the map sets them (election tooltips show
// the top 2); the footer counts the rest.
const GENERIC_TOOLTIP_ROW_LIMIT = 5;
// Collection maps: a card lists up to 5 markets in their configured order and 8 contract rows in all,
// fewer when the map is too short for them (the card can't hang past the map); markets that don't fit
// are counted in its footer. A market lists its chosen contracts (up to 5) or, with none chosen, its
// top 1 or 2 (the API sends a market's top 2).
const COLLECTION_MARKET_LIMIT = 5;
const COLLECTION_ROW_LIMIT = 8;
const COLLECTION_AUTOMATIC_LIMIT = 2;
const COLLECTION_PICKED_LIMIT = 5;
const DEFAULT_COLLECTION_COLOR = '#2a9d8f';
// Card height estimate for the fit: keyline, header and bottom margin; a market question line (12px
// text, up to 2 lines of about 40 characters); a contract line; a row's padding and gap.
const COLLECTION_CARD_FRAME_PX = 60;
const COLLECTION_FOOTER_PX = 36;
const COLLECTION_QUESTION_LINE_PX = 16;
const COLLECTION_QUESTION_LINE_CHARS = 40;
const COLLECTION_CONTRACT_LINE_PX = 18;
const COLLECTION_ROW_PADDING_PX = 14;
const COLLECTION_HEADING_PADDING_PX = 10;
const TOOLTIP_OFFSET_X = 24;
const TOOLTIP_OFFSET_Y = 16;
// The card opens on the side the pointer came from (moving right -> card on the left), so it
// doesn't cover the states the pointer is heading toward. Direction = net horizontal travel over
// the last TOOLTIP_TRAIL_PATH_PX of the pointer's path (within TOOLTIP_TRAIL_MS); under
// TOOLTIP_TRAIL_MIN_PX, or mostly vertical, keeps right-first.
const TOOLTIP_TRAIL_MS = 600;
const TOOLTIP_TRAIL_PATH_PX = 60;
const TOOLTIP_TRAIL_MIN_PX = 12;
// The beside-the-shape spot on the preferred side is used when it is at most this much farther from
// the pointer than the nearest clear spot that isn't ahead of it.
const TOOLTIP_SIDE_TIE_PX = 80;
// Room between the card and the chart's edges for the card's drop shadow (0 12px 28px), which
// the chart container would otherwise clip.
const TOOLTIP_EDGE_PADDING_X = 20;
const TOOLTIP_EDGE_PADDING_TOP = 10;
const TOOLTIP_EDGE_PADDING_BOTTOM = 30;
// Space kept clear around a state's map shape when its small-state box is hovered.
const TOOLTIP_AVOID_MARGIN = 8;
const BODY_FONT_FAMILY = 'Roboto, sans-serif';
const NUMBERS_FONT_FAMILY = '"Roboto Mono", monospace';
// Election map tiers by the leading contract's price in whole cents, inclusive at the top of each
// tier: toss-up up to 55, slight lean ("low") up to 65, lean ("medium") up to 75, likely ("high")
// above. Called races are always "high". The bucket names are also the toolbar baseline buckets.
const PRICE_TIER_CENTS = {
  TOSS_UP: 55,
  SLIGHT_LEAN: 65,
  LEAN: 75,
};
const BUCKET_TOSS_UP = 'tossup';
// Seat bar: seats already settled (called, decided without a market, baselines), in the "full"
// shade; markets over the Lean tier stay in "high" (Likely).
const BUCKET_FULL = 'full';
// Seat bar segments between the parties, counted for neither: toss-ups and independent-led states.
const MIDDLE_SEGMENT_LABELS = {
  [BUCKET_TOSS_UP]: 'Toss-up',
  [PARTY_INDEPENDENT]: 'Independent',
};
// A party segment's name in its tooltip (as in the hint beside the party's total).
const PARTY_SEGMENT_LABELS = {
  [BUCKET_FULL]: 'Safe',
  high: 'Likely',
  medium: 'Lean',
  low: 'Slight lean',
};
const TOOLBAR_MIDDLE_LABEL_GAP = 12;
// "full": a party's settled seats, darker than its Likely tier: called races, and in the seat
// bar also seats not up this cycle and races decided without a market.
const DEFAULT_PALETTE = {
  gray: { low: '#dddddd', medium: '#bbbbbb', high: '#888888' },
  democratic: { low: '#8aafff', medium: '#5c80dc', high: '#244999', full: '#17306b' },
  republican: { low: '#ff8b98', medium: '#ff5865', high: '#d22532', full: '#9e1a24' },
  independent: { low: '#e8d48f', medium: '#d8bb5b', high: '#c9a227' },
};
// Light fills (slight lean, independents) take dark label text in place of white.
const MAP_LABEL_DARK_TEXT_LUMINANCE = 0.35;
const MAP_LABEL_DARK_TEXT_WEIGHT = 0.35;
// Manual label-anchor overrides (fractions of the shape's bounding box, x from the left,
// y from the top). Anchors are normally computed from the geometry (see getMapDefinition);
// add an entry here only when a computed position needs a nudge.
const MAP_LABEL_ANCHORS = {
  KS: { x: 0.49, y: 0.5 },
  // Computed (0.55, 0.48) reads slightly right of centre.
  IA: { x: 0.52, y: 0.48 },
  // Computed (0.80, 0.56) leaves the two-line label overhanging the peninsula's west coast;
  // raised a little so the price clears the southern border.
  FL: { x: 0.83, y: 0.535 },
  // Nudges from Holden's review (2026-10-07); computed anchors in brackets.
  ND: { x: 0.56, y: 0.516 }, // (0.638) sat right of centre
  OR: { x: 0.54, y: 0.568 }, // (0.574)
  CO: { x: 0.553, y: 0.511 }, // (0.571)
  IN: { x: 0.526, y: 0.44 }, // (y 0.511) sat low
  TN: { x: 0.455, y: 0.55 }, // (0.421) sat left of centre
};

// The us-small map draws the smallest north-eastern states as a stack of boxes on the
// right (VT at the top, DC at the bottom). Hawaii is too small for a label, so it gets
// a synthetic box one slot below DC, merged into its own geometry the way the map does
// for MA/DC (islands + box on one point).
const SMALL_STATE_STACK_KEY = 'us-dc';
const SMALL_STATE_STACK_PITCH = 558;
const HAWAII_KEY = 'us-hi';
// MapType=2: Maine and Nebraska electoral-vote districts, drawn as a column of boxes (same size
// and pitch as the small-state stack) in the open space west of the Pacific coast, top level with
// the stack's NH slot. Location codes follow the legacy election map (`ME-01` ...); the widget
// normalizes them to `ME01`.
const DISTRICT_MAP_TYPE = 2;
const DISTRICT_BOXES = [
  { code: 'ME01', label: 'ME-1', name: 'Maine 1st District' },
  { code: 'ME02', label: 'ME-2', name: 'Maine 2nd District' },
  { code: 'NE01', label: 'NE-1', name: 'Nebraska 1st District', groupGap: true },
  { code: 'NE02', label: 'NE-2', name: 'Nebraska 2nd District' },
  { code: 'NE03', label: 'NE-3', name: 'Nebraska 3rd District' },
];
const DISTRICT_COAST_KEYS = ['us-wa', 'us-or', 'us-ca'];
const DISTRICT_COAST_GAP = 300;
const DISTRICT_GROUP_GAP = 260;
const DISTRICT_EDGE_PADDING = 8;
// DC is the ninth (bottom) stack slot, so NH (second from the top) is seven slots above it.
const DISTRICT_TOP_SLOTS_ABOVE_STACK_BOX = 7;
const SMALL_STATE_STACK_SLOTS_ABOVE_DC = 8;
// The map's stack boxes are 457,436 units and the rebuilt row boxes ~291,000; no island is
// an axis-aligned rectangle.
const STACK_BOX_MIN_AREA = 150000;
// Phone tile map (Jordan's mobile design): narrow widgets show one square tile per state instead
// of the geographic map, whose labels and small-state stack don't fit at that size.
const TILE_MAP_MAX_WIDTH = 600;
const TILE_MAP_COLUMNS = 11;
const TILE_MAP_ROWS = 8;
// [code, row, column]
const TILE_MAP_LAYOUT = [
  ['AK', 0, 0],
  ['ME', 0, 10],
  ['WI', 1, 5],
  ['VT', 1, 9],
  ['NH', 1, 10],
  ['WA', 2, 0],
  ['ID', 2, 1],
  ['MT', 2, 2],
  ['ND', 2, 3],
  ['MN', 2, 4],
  ['IL', 2, 5],
  ['MI', 2, 6],
  ['NY', 2, 8],
  ['MA', 2, 9],
  ['OR', 3, 0],
  ['NV', 3, 1],
  ['WY', 3, 2],
  ['SD', 3, 3],
  ['IA', 3, 4],
  ['IN', 3, 5],
  ['OH', 3, 6],
  ['PA', 3, 7],
  ['NJ', 3, 8],
  ['CT', 3, 9],
  ['RI', 3, 10],
  ['CA', 4, 0],
  ['UT', 4, 1],
  ['CO', 4, 2],
  ['NE', 4, 3],
  ['MO', 4, 4],
  ['KY', 4, 5],
  ['WV', 4, 6],
  ['VA', 4, 7],
  ['MD', 4, 8],
  ['DE', 4, 9],
  ['AZ', 5, 1],
  ['NM', 5, 2],
  ['KS', 5, 3],
  ['AR', 5, 4],
  ['TN', 5, 5],
  ['NC', 5, 6],
  ['SC', 5, 7],
  ['DC', 5, 8],
  ['OK', 6, 3],
  ['LA', 6, 4],
  ['MS', 6, 5],
  ['AL', 6, 6],
  ['GA', 6, 7],
  ['HI', 7, 0],
  ['TX', 7, 3],
  ['FL', 7, 8],
];
// MapType=2 district tiles: Maine's beside ME, Nebraska's in the open corner below-left of NE.
const TILE_MAP_DISTRICT_LAYOUT = [
  ['ME01', 0, 8],
  ['ME02', 0, 9],
  ['NE01', 6, 0],
  ['NE02', 6, 1],
  ['NE03', 6, 2],
];
// Below this plot width (after zoom) a box is too narrow for its price, so it shows the code only.
const BOX_PRICE_MIN_PLOT_WIDTH = 520;
// A compact map is often short rather than narrow: its boxes need this much label room (px) for
// the price ("NH 70%" is about 37px).
const BOX_PRICE_MIN_LABEL_ROOM = 32;
const LABEL_ANCHOR_GRID = 24;
const LABEL_ANCHOR_REFINEMENTS = 2;
const labelAnchorCache = {};

function ringArea(ring) {
  let area = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    area += ring[j][0] * ring[i][1] - ring[i][0] * ring[j][1];
  }
  return Math.abs(area) / 2;
}

function isPointInRing(x, y, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    const crosses = yi > y !== yj > y;
    if (crosses && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

function distanceToRing(x, y, ring) {
  let best = Infinity;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const [x1, y1] = ring[j];
    const [x2, y2] = ring[i];
    const dx = x2 - x1;
    const dy = y2 - y1;
    const lengthSquared = dx * dx + dy * dy;
    const along =
      lengthSquared === 0
        ? 0
        : Math.max(0, Math.min(1, ((x - x1) * dx + (y - y1) * dy) / lengthSquared));
    const px = x1 + along * dx;
    const py = y1 + along * dy;
    best = Math.min(best, Math.sqrt((x - px) * (x - px) + (y - py) * (y - py)));
  }
  return best;
}

// Pole of inaccessibility: the interior point farthest from every edge, which is the
// most natural "visual centre" for a label. Coarse grid search, then refined twice.
function poleOfInaccessibility(ring) {
  const xs = ring.map((point) => point[0]);
  const ys = ring.map((point) => point[1]);
  let minX = Math.min(...xs);
  let maxX = Math.max(...xs);
  let minY = Math.min(...ys);
  let maxY = Math.max(...ys);
  let best = { x: (minX + maxX) / 2, y: (minY + maxY) / 2, distance: -1 };

  for (let pass = 0; pass <= LABEL_ANCHOR_REFINEMENTS; pass += 1) {
    const stepX = (maxX - minX) / LABEL_ANCHOR_GRID;
    const stepY = (maxY - minY) / LABEL_ANCHOR_GRID;
    for (let i = 0; i < LABEL_ANCHOR_GRID; i += 1) {
      for (let j = 0; j < LABEL_ANCHOR_GRID; j += 1) {
        const x = minX + (i + 0.5) * stepX;
        const y = minY + (j + 0.5) * stepY;
        if (isPointInRing(x, y, ring)) {
          const distance = distanceToRing(x, y, ring);
          if (distance > best.distance) {
            best = { x, y, distance };
          }
        }
      }
    }
    minX = best.x - stepX;
    maxX = best.x + stepX;
    minY = best.y - stepY;
    maxY = best.y + stepY;
  }

  return best;
}

// A stack box is a closed, axis-aligned rectangle (5 points, two distinct x and two
// distinct y values) of meaningful size; tiny 5-vertex islands must not qualify.
function isStackBox(ring) {
  if (ring.length !== 5) {
    return false;
  }
  const xs = [...new Set(ring.map((point) => point[0]))];
  const ys = [...new Set(ring.map((point) => point[1]))];
  if (xs.length !== 2 || ys.length !== 2) {
    return false;
  }
  return Math.abs(xs[0] - xs[1]) * Math.abs(ys[0] - ys[1]) >= STACK_BOX_MIN_AREA;
}

// The small-state stack rebuilt with shorter, slightly wider boxes (same gap between boxes,
// widened to the left), its top moved down one row so VT is level with the district column's
// ME-1 and clears Maine. `box(slot)` gives slot 0 (VT) to 8 (DC); 9 is Hawaii.
function getRowTileLayout(stackBox) {
  const xs = stackBox.map((point) => point[0]);
  const ys = stackBox.map((point) => point[1]);
  const right = Math.max(...xs);
  const originalHeight = Math.max(...ys) - Math.min(...ys);
  const width = (right - Math.min(...xs)) * ROW_TILE_WIDTH_RATIO;
  const height = originalHeight * ROW_TILE_HEIGHT_RATIO;
  const pitch = height + (SMALL_STATE_STACK_PITCH - originalHeight);
  const stackTop = Math.max(...ys) + SMALL_STATE_STACK_SLOTS_ABOVE_DC * SMALL_STATE_STACK_PITCH;
  const rowsTop = stackTop - pitch;
  const box = (slot) => {
    const top = rowsTop - slot * pitch;
    const bottom = top - height;
    const left = right - width;
    return [
      [left, bottom],
      [right, bottom],
      [right, top],
      [left, top],
      [left, bottom],
    ];
  };
  const slotOf = (ring) =>
    Math.round((stackTop - Math.max(...ring.map((point) => point[1]))) / SMALL_STATE_STACK_PITCH);
  return {
    pitch,
    box,
    slotOf,
    dcBox: box(SMALL_STATE_STACK_SLOTS_ABOVE_DC),
    districtTopSlots: SMALL_STATE_STACK_SLOTS_ABOVE_DC,
  };
}

function reshapeStackBox(geometry, layout) {
  if (geometry.type !== 'MultiPolygon') {
    return geometry;
  }
  return {
    ...geometry,
    coordinates: geometry.coordinates.map((polygon) =>
      isStackBox(polygon[0]) ? [layout.box(layout.slotOf(polygon[0]))] : polygon,
    ),
  };
}

function getDistrictBoxFeatures(
  stackBox,
  coastX,
  pitch = SMALL_STATE_STACK_PITCH,
  topSlots = DISTRICT_TOP_SLOTS_ABOVE_STACK_BOX,
) {
  const xs = stackBox.map((point) => point[0]);
  const ys = stackBox.map((point) => point[1]);
  const width = Math.max(...xs) - Math.min(...xs);
  const height = Math.max(...ys) - Math.min(...ys);
  const right = coastX - DISTRICT_COAST_GAP;
  const left = right - width;
  // Laid out top to bottom (map y increases upwards).
  let top = Math.max(...ys) + topSlots * pitch;
  return DISTRICT_BOXES.map((box) => {
    if (box.groupGap) {
      top -= DISTRICT_GROUP_GAP;
    }
    const bottom = top - height;
    const feature = {
      type: 'Feature',
      properties: {
        'hc-key': `us-${box.code.toLowerCase()}`,
        'hc-a2': box.code,
        name: box.code,
        'hc-middle-x': 0.5,
        'hc-middle-y': 0.5,
      },
      geometry: {
        type: 'Polygon',
        coordinates: [
          [
            [left, bottom],
            [right, bottom],
            [right, top],
            [left, top],
            [left, bottom],
          ],
        ],
      },
    };
    top -= pitch;
    return feature;
  });
}

// Westernmost x of the mainland Pacific coast (WA, OR, CA).
function getCoastX(features) {
  const xs = features
    .filter((feature) =>
      DISTRICT_COAST_KEYS.includes(feature.properties && feature.properties['hc-key']),
    )
    .flatMap((feature) => polygonRingsOf(feature.geometry))
    .flatMap((ring) => ring.map((point) => point[0]));
  return xs.length > 0 ? Math.min(...xs) : null;
}

function polygonRingsOf(geometry) {
  if (!geometry) {
    return [];
  }
  if (geometry.type === 'Polygon') {
    return [geometry.coordinates[0]];
  }
  if (geometry.type === 'MultiPolygon') {
    return geometry.coordinates.map((polygon) => polygon[0]);
  }
  return [];
}

// Highcharts reads hc-middle-x / hc-middle-y as fractions of the feature's bounding box
// (x from the left, y from the top). Stack boxes anchor at their exact centre; everything
// else anchors at the pole of inaccessibility of its largest polygon.
function computeLabelAnchor(geometry) {
  const rings = polygonRingsOf(geometry);
  if (rings.length === 0) {
    return null;
  }

  const box = rings.find(isStackBox);
  let target;
  if (box) {
    const boxXs = box.map((point) => point[0]);
    const boxYs = box.map((point) => point[1]);
    target = {
      x: (Math.min(...boxXs) + Math.max(...boxXs)) / 2,
      y: (Math.min(...boxYs) + Math.max(...boxYs)) / 2,
    };
  } else {
    const largest = rings.reduce((candidate, ring) =>
      ringArea(ring) > ringArea(candidate) ? ring : candidate,
    );
    target = poleOfInaccessibility(largest);
  }

  const allPoints = [].concat(...rings);
  const minX = Math.min(...allPoints.map((point) => point[0]));
  const maxX = Math.max(...allPoints.map((point) => point[0]));
  const minY = Math.min(...allPoints.map((point) => point[1]));
  const maxY = Math.max(...allPoints.map((point) => point[1]));
  return {
    x: maxX === minX ? 0.5 : (target.x - minX) / (maxX - minX),
    y: maxY === minY ? 0.5 : (maxY - target.y) / (maxY - minY),
  };
}

const roundToTwo = (value) => Math.round(value * 100) / 100;
const HTML_ESCAPE_CHARACTERS = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};
const SAFE_TOOLTIP_COLOR = /^#[0-9a-f]{6}$/i;

function escapeTooltipHtml(value) {
  return String(value === null || typeof value === 'undefined' ? '' : value).replace(
    /[&<>"']/g,
    (character) => HTML_ESCAPE_CHARACTERS[character],
  );
}

function getSafeTooltipImageUrl(value) {
  if (typeof value !== 'string' || !value.trim()) {
    return '';
  }

  try {
    const baseUrl =
      typeof window !== 'undefined' && window.location
        ? window.location.href
        : 'https://www.predictit.org/';
    const imageUrl = new URL(value.trim(), baseUrl);
    const isHttps = imageUrl.protocol === 'https:';
    const isSameOriginHttp =
      imageUrl.protocol === 'http:' &&
      typeof window !== 'undefined' &&
      imageUrl.origin === window.location.origin;
    return isHttps || isSameOriginHttp ? imageUrl.href : '';
  } catch (error) {
    log.info('cms-map-widget: malformed tooltip image URL from CMS content.', value, error);
    return '';
  }
}

const RGBA_COLOR =
  /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*(?:,\s*(\d*\.?\d+)\s*)?\)$/i;

// Solid hex for tooltip accents. Generic maps fill states with rgba gradient shades, so
// blend those onto white (the map background) to get the colour the reader actually sees.
function getSolidTooltipColor(value) {
  if (typeof value === 'string' && SAFE_TOOLTIP_COLOR.test(value)) {
    return value;
  }

  const match = typeof value === 'string' ? value.trim().match(RGBA_COLOR) : null;
  if (!match) {
    return DEFAULT_EMPTY_COLOR;
  }

  const alpha = match[4] === undefined ? 1 : Math.min(1, Math.max(0, Number(match[4])));
  const toHex = (channel) => {
    const blended = Math.round(Math.min(255, Number(channel)) * alpha + 255 * (1 - alpha));
    return `0${blended.toString(16)}`.slice(-2);
  };
  return `#${toHex(match[1])}${toHex(match[2])}${toHex(match[3])}`;
}

function getLeaderPillColors(ringColor) {
  if (!SAFE_TOOLTIP_COLOR.test(ringColor || '') || ringColor === NEUTRAL_RING_COLOR) {
    return null;
  }

  const channels = [1, 3, 5].map((index) => parseInt(ringColor.substr(index, 2), 16));
  const shaded = channels
    .map((channel) =>
      `0${Math.round(channel * (1 - LEADER_PILL_TEXT_SHADE)).toString(16)}`.slice(-2),
    )
    .join('');
  return {
    background: getSolidTooltipColor(`rgba(${channels.join(', ')}, ${LEADER_PILL_TINT_ALPHA})`),
    color: `#${shaded}`,
  };
}

function getSafeTooltipColor(value) {
  return typeof value === 'string' && SAFE_TOOLTIP_COLOR.test(value) ? value : DEFAULT_EMPTY_COLOR;
}

const TOOLTIP_CHANGE_ARROW_PATH =
  'M6.57574 2.16475C6.81005 1.94508 7.18995 1.94508 7.42426 2.16475L9.82426 4.41475C10.0586 4.63442 10.0586 4.99058 9.82426 5.21025C9.58995 5.42992 9.21005 5.42992 8.97574 5.21025L7.6 3.9205V10.4375C7.6 10.7482 7.33137 11 7 11C6.66863 11 6.4 10.7482 6.4 10.4375V3.9205L5.02426 5.21025C4.78995 5.42992 4.41005 5.42992 4.17574 5.21025C3.94142 4.99058 3.94142 4.63442 4.17574 4.41475L6.57574 2.16475Z';
const TOOLTIP_CHEVRON_HTML =
  '<span class="cms-map-widget__tooltip-chevron"><svg width="7" height="12" viewBox="0 0 7 12" fill="none" aria-hidden="true"><path d="M1 1L6 6L1 11" stroke="#949494" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></span>';

function getTooltipChangeArrowSvg(direction) {
  const fill = direction === 'down' ? CHANGE_DOWN_COLOR : CHANGE_UP_COLOR;
  return `<svg class="cms-map-widget__tooltip-change-arrow cms-map-widget__tooltip-change-arrow--${direction}" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path fill-rule="evenodd" clip-rule="evenodd" d="${TOOLTIP_CHANGE_ARROW_PATH}" fill="${fill}"/></svg>`;
}

// States without a market step back so the races with markets stand out: decided ("safe") states
// take a light tint of the party color and no-election states the empty gray; a key explains it.
const SAFE_TINT_WEIGHT = 0.25;
// Hover: the state darkens slightly - immediately when no tooltip is open, otherwise only the state
// whose tooltip is shown (it keeps it while the pointer travels to that tooltip).
const MAP_HOVER_DARKEN = 0.1;
const MAP_HOVER_MS = 150;
// Small-state, Hawaii and district boxes: shorter and a little wider than the map's own stack
// boxes, labelled on one line (code left, price right).
const ROW_TILE_HEIGHT_RATIO = 0.6;
const ROW_TILE_WIDTH_RATIO = 1.0625;
const ROW_TILE_LABEL_INSET_PX = 6;
const SAFE_LABEL_COLORS = {
  light: { democratic: '#244999', republican: '#b3282d' },
  dark: { democratic: '#9db5f4', republican: '#f58d94' },
};

function getArcPoint(radius, fraction) {
  const angle = Math.PI * (1 - fraction);
  return [ARC_CENTER_X + radius * Math.cos(angle), ARC_CENTER_Y - radius * Math.sin(angle)];
}

// One segment of the seat arc, from `from` to `to` (fractions of the half ring, left to right).
function getArcSegmentPath(from, to) {
  const point = (radius, fraction) =>
    getArcPoint(radius, fraction)
      .map((value) => Math.round(value * 10) / 10)
      .join(' ');
  const outer = ARC_OUTER_RADIUS;
  const inner = ARC_INNER_RADIUS;
  return (
    `M${point(outer, from)}A${outer} ${outer} 0 0 1 ${point(outer, to)}` +
    `L${point(inner, to)}A${inner} ${inner} 0 0 0 ${point(inner, from)}Z`
  );
}

function mixHexColors(color, base, weight) {
  if (!SAFE_TOOLTIP_COLOR.test(color || '') || !SAFE_TOOLTIP_COLOR.test(base || '')) {
    return color;
  }

  const channel = (hex, index) => parseInt(hex.substr(1 + index * 2, 2), 16);
  return `#${[0, 1, 2]
    .map((index) => {
      const mixed = Math.round(
        channel(color, index) * weight + channel(base, index) * (1 - weight),
      );
      return `0${mixed.toString(16)}`.slice(-2);
    })
    .join('')}`;
}

// WCAG relative luminance of a hex color (0 = black, 1 = white).
function getRelativeLuminance(color) {
  if (!SAFE_TOOLTIP_COLOR.test(color || '')) {
    return 0;
  }

  const [red, green, blue] = [1, 3, 5].map((index) => {
    const channel = parseInt(color.substr(index, 2), 16) / 255;
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
}

// State label text on a filled state: white, or a dark shade of the fill on light fills.
function getMapLabelColor(fill) {
  return getRelativeLuminance(fill) > MAP_LABEL_DARK_TEXT_LUMINANCE
    ? mixHexColors(fill, '#000000', MAP_LABEL_DARK_TEXT_WEIGHT)
    : '#ffffff';
}

// Today's date in US Eastern time, as a UTC midnight timestamp (election days are counted there).
function getEasternToday() {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: ELECTION_TIME_ZONE,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    }).formatToParts(new Date());
    const value = (type) => Number((parts.find((part) => part.type === type) || {}).value);
    return Date.UTC(value('year'), value('month') - 1, value('day'));
  } catch {
    // No Intl time zone support: fall back to the browser's local date.
    const now = new Date();
    return Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  }
}

export default {
  props: {
    mapName: {
      type: String,
      required: true,
    },
    // 'desktop' or 'mobile' in a story's homepage preview, where the map's Compact preview
    // layout applies; empty everywhere else.
    previewSurface: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      isLoading: true,
      hasError: false,
      mapData: [],
      legendData: [],
      chart: null,
      showPinchHint: false,
      showDataTable: false,
      mapSettings: null,
      unlinkedLocations: [],
      priceUnit: null,
      isContextMenuOpen: false,
      showSummaryInfo: false,
      widgetWidth: 0,
      compactHeight: 0,
      compactTight: 0,
      compactTilesTop: 0,
      compactListRows: 0,
      compactTileStretch: false,
      activeTileCode: '',
      zoomFactor: 1,
      isZoomPanelOpen: false,
      zoomPanelDismissHandler: null,
      isWidgetFullscreen: false,
      fullscreenChangeHandler: null,
      shouldShowPinchHint: false,
      interactionDismissHandler: null,
      tooltipClickHandler: null,
      contextMenuObserver: null,
      contextMenuOverflowContainers: [],
    };
  },
  computed: {
    ...mapState('app', ['resolvedTheme']),
    mapConfig() {
      if (this.mapData && this.mapData.length > 0) {
        return this.mapData[0];
      }
      // V2 still has the map's settings when no location has a linked market.
      return this.mapSettings || {};
    },
    rendererType() {
      return ((this.mapConfig && this.mapConfig.rendererType) || '').toLowerCase();
    },
    isElectionMap() {
      return (
        this.rendererType === RENDERER_TYPE_ELECTION_ELECTORAL ||
        this.rendererType === RENDERER_TYPE_ELECTION_SENATE
      );
    },
    isSenateMap() {
      return this.rendererType === RENDERER_TYPE_ELECTION_SENATE;
    },
    // A state's most interesting markets (any mix per state), listed in its card.
    isCollectionMap() {
      return this.rendererType === RENDERER_TYPE_COLLECTION;
    },
    collectionColor() {
      const color = ((this.mapConfig && this.mapConfig.collectionColor) || '')
        .trim()
        .replace(/^#([0-9a-f])([0-9a-f])([0-9a-f])$/i, '#$1$1$2$2$3$3');
      return SAFE_TOOLTIP_COLOR.test(color) ? color : DEFAULT_COLLECTION_COLOR;
    },
    mapTitle() {
      return (this.mapConfig && this.mapConfig.title) || '';
    },
    showPriceToggle() {
      return this.getMapFlag('showPriceToggle', false);
    },
    showCalledRaces() {
      return this.getMapFlag('showCalledRaces', true);
    },
    isDarkTheme() {
      return this.resolvedTheme === DARK_THEME;
    },
    isDistrictMap() {
      return (
        this.isElectionMap && Number(this.mapConfig && this.mapConfig.mapType) === DISTRICT_MAP_TYPE
      );
    },
    activePriceUnit() {
      // The reader's choice applies only while the map offers the toggle; otherwise the map's
      // configured display ("Percent" by default).
      if (this.showPriceToggle && this.priceUnit) {
        return this.priceUnit;
      }
      const configured = ((this.mapConfig && this.mapConfig.priceDisplay) || '').toLowerCase();
      return configured === PRICE_UNIT_CENTS ? PRICE_UNIT_CENTS : PRICE_UNIT_PERCENT;
    },
    priceUnitSymbol() {
      return this.activePriceUnit === PRICE_UNIT_CENTS ? '¢' : '%';
    },
    classModifiers() {
      return {
        'cms-map-widget--menu-open': this.isContextMenuOpen,
        'cms-map-widget--fullscreen': this.isWidgetFullscreen,
        'cms-map-widget--election': this.isElectionMap,
        'cms-map-widget--summary-only': !this.showMapArea,
        'cms-map-widget--map-size-small': this.mapSize === MAP_SIZE_SMALL,
        'cms-map-widget--map-size-extra-small': this.mapSize === MAP_SIZE_EXTRA_SMALL,
        'cms-map-widget--popover-open': !!this.activeTileCode || this.showSummaryInfo,
        'cms-map-widget--with-legend': this.showLegend,
        'cms-map-widget--compact': !!this.compactLayout,
        [`cms-map-widget--compact-${this.compactLayout}`]: !!this.compactLayout,
        'cms-map-widget--compact-plain': !!this.compactLayout && !this.showSummary,
        'cms-map-widget--compact-tight':
          this.compactLayout === COMPACT_LAYOUT_SIDE && this.compactTight >= COMPACT_TIGHT_MAX,
        'cms-map-widget--compact-stretch': this.compactTilesStretched,
      };
    },
    // The map's Compact preview layout, in a story's homepage preview only (fullscreen always
    // shows the full layout).
    isCompactPreview() {
      return (
        !!this.previewSurface &&
        Number(this.mapConfig && this.mapConfig.previewLayout) === PREVIEW_LAYOUT_COMPACT &&
        this.showMapArea &&
        !this.isWidgetFullscreen
      );
    },
    compactLayout() {
      if (!this.isCompactPreview) {
        return '';
      }
      if (this.widgetWidth > 0 && this.widgetWidth < COMPACT_SIDE_MIN_WIDTH) {
        return COMPACT_LAYOUT_STACK;
      }
      // Election maps get the summary column with the seat arc; maps without a seat bar (generic
      // and collection maps) keep a single row of summary and key above the map.
      return this.showSummary && this.showElectionToolbar
        ? COMPACT_LAYOUT_SIDE
        : COMPACT_LAYOUT_ROW;
    },
    compactStyle() {
      return this.compactLayout
        ? {
            height: `${this.compactHeight || COMPACT_FALLBACK_HEIGHT}px`,
            '--cmw-tiles-top': `${this.compactTilesTop}px`,
          }
        : null;
    },
    showSeatArc() {
      return this.compactLayout === COMPACT_LAYOUT_SIDE && this.showElectionToolbar;
    },
    // Neighbors in the same shade (a party's no-election and other settled seats) are one piece of
    // the ring, so no divider runs between two identical colors.
    seatArcSegments() {
      const total = this.toolbarSegments.reduce((sum, segment) => sum + segment.width, 0) || 1;
      let start = 0;
      const pieces = [];
      this.toolbarSegments.forEach((segment) => {
        const from = start / total;
        start += segment.width;
        const to = Math.min(start / total, 1);
        const title = this.getToolbarSegmentTitle(segment);
        const previous = pieces[pieces.length - 1];
        if (previous && previous.color === segment.color) {
          previous.to = to;
          previous.titles.push(title);
          return;
        }
        pieces.push({ key: segment.key, color: segment.color, from, to, titles: [title] });
      });
      return pieces.map((piece) => ({
        key: piece.key,
        color: piece.color,
        title: piece.titles.join(' · '),
        path: getArcSegmentPath(piece.from, piece.to),
      }));
    },
    seatArcLabel() {
      const middle = this.toolbarMiddleLabels.map((item) => `${item.value} ${item.label}`);
      return [
        `${this.democraticToolbarCounter.total} Democratic`,
        ...middle,
        `${this.republicanToolbarCounter.total} Republican`,
      ].join(', ');
    },
    showLegend() {
      return (
        this.showMapArea &&
        this.getMapFlag('showLegend', !this.isElectionMap) &&
        this.legendItems.length > 0
      );
    },
    // Generic maps: one entry per color the states are filled with (the leading contract's name;
    // its price when only one state has it), so the legend always explains the map. Election maps
    // keep the configured legend market's contracts (they use the price-tier key instead).
    legendItems() {
      if (this.isElectionMap) {
        return this.legendData.map((item) => ({
          key: `${item.contractId}`,
          color: item.color || '#cccccc',
          name: item.shortName,
          detail: this.formatPrice(item.lastTradePrice),
        }));
      }

      // Collection maps: one entry for the states with markets.
      if (this.isCollectionMap) {
        const counts = (this.mapData || [])
          .map((location) => this.getCollectionMarkets(location).length)
          .filter((count) => count > 0);
        const marketCount = counts.reduce((total, count) => total + count, 0);
        return counts.length === 0
          ? []
          : [
              {
                key: 'collection',
                color: this.collectionColor,
                name: `${counts.length} state${counts.length === 1 ? '' : 's'}`,
                detail: this.getCollectionCountText(marketCount),
              },
            ];
      }

      const groups = [];
      (this.mapData || []).forEach((location) => {
        const leader = this.getLeadingContract(location);
        if (!leader) {
          return;
        }
        const color = location.overrideColor || leader.color || this.resolveBaseColor(leader);
        const name = (leader.shortName || leader.contractName || leader.name || '').trim();
        const group = groups.find((item) => item.color === color && item.name === name);
        if (group) {
          group.count += 1;
          return;
        }
        const stateCode = this.normalizeLocationCode(location.locationCode);
        groups.push({
          color,
          name,
          count: 1,
          price: leader.lastTradePrice,
          stateName: this.getStateName(stateCode),
        });
      });
      // A yes/no market's contract reads "Yes"; one state's entry names the state instead.
      return groups.map((group) => ({
        key: `${group.color}-${group.name}`,
        color: group.color,
        name: group.count === 1 && /^yes$/i.test(group.name) ? group.stateName : group.name,
        detail: group.count > 1 ? `${group.count} states` : this.formatPrice(group.price),
      }));
    },
    legendShadeNote() {
      return !this.isElectionMap &&
        !this.isCollectionMap &&
        this.legendItems.length > 0 &&
        (this.mapData || []).some((location) => Number(location.gradientSetting) === 2)
        ? 'Darker shade = higher price'
        : '';
    },
    canShowDataTable() {
      return this.getMapFlag('showDataTable', true);
    },
    showElectionToolbar() {
      // Summary only is just the headline from the parent market: no seat bar (Jordan, 2026-10-09).
      return (
        this.isElectionMap &&
        this.displayMode !== DISPLAY_MODE_SUMMARY_ONLY &&
        this.getMapFlag('showToolbar', true) &&
        this.toolbarSegments.length > 0
      );
    },
    showMajorityLabel() {
      return this.isElectionMap && this.getMapFlag('showMajorityLabel', false);
    },
    displayMode() {
      const mode = Number(this.mapConfig && this.mapConfig.displayMode);
      return mode === DISPLAY_MODE_SUMMARY_ONLY || mode === DISPLAY_MODE_MAP_ONLY
        ? mode
        : DISPLAY_MODE_MAP_AND_SUMMARY;
    },
    mapSize() {
      const size = Number(this.mapConfig && this.mapConfig.mapSize);
      return size === MAP_SIZE_SMALL || size === MAP_SIZE_EXTRA_SMALL ? size : 0;
    },
    showMapArea() {
      return this.displayMode !== DISPLAY_MODE_SUMMARY_ONLY;
    },
    showSummary() {
      // Summary-only always shows it (falling back to the title); the default mode shows it
      // only when it has more than the title, otherwise the title sits beside the map's controls.
      if (this.displayMode === DISPLAY_MODE_SUMMARY_ONLY) {
        return true;
      }

      return (
        this.displayMode === DISPLAY_MODE_MAP_AND_SUMMARY &&
        (!!this.summaryHeadlineParts || !!this.summarySubheader || this.showElectionToolbar)
      );
    },
    showMapTitle() {
      return this.showMapArea && !this.showSummary && !!this.mapTitle;
    },
    // Compact tile maps have no room under the tiles for the watermark: it sits in the tile map's
    // empty top row instead, clear of the title and the tiles.
    showTileWatermark() {
      return !!this.compactLayout && this.isTileMap;
    },
    isTileMap() {
      return this.showMapArea && this.widgetWidth > 0 && this.widgetWidth < TILE_MAP_MAX_WIDTH;
    },
    tileGridStyle() {
      return {
        gridTemplateColumns: `repeat(${TILE_MAP_COLUMNS}, minmax(0, 1fr))`,
        gridTemplateRows: `repeat(${TILE_MAP_ROWS}, ${this.compactTilesStretched ? 'minmax(0, 1fr)' : 'auto'})`,
      };
    },
    // Compact phone tiles grown taller into the room the list leaves (set in fitCompactHeight).
    compactTilesStretched() {
      return this.compactLayout === COMPACT_LAYOUT_STACK && this.compactTileStretch;
    },
    // The list under compact phone tiles: the closest races (lowest leading price) on election
    // maps, the leading states (highest leading price) on generic maps, the states with the most
    // markets on collection maps. States without a market and called races are left out.
    compactListItems() {
      if (this.compactLayout !== COMPACT_LAYOUT_STACK) {
        return [];
      }

      const items = [];
      this.mapTiles.forEach((tile) => {
        const item = {
          key: tile.stateCode,
          color: tile.color,
          name: this.getStateName(tile.stateCode, tile.stateName),
        };
        if (this.isCollectionMap) {
          const count = this.getCollectionMarkets(tile.marketData).length;
          if (count > 0) {
            items.push({ ...item, sort: -count, value: this.getCollectionCountText(count) });
          }
          return;
        }
        const leader = this.getLeadingContract(tile.marketData);
        if (!leader || this.isCalledContract(leader) || !hasLastTradePrice(leader)) {
          return;
        }
        const price = Number(leader.lastTradePrice);
        items.push({
          ...item,
          sort: this.isElectionMap ? price : -price,
          value: this.formatPrice(leader.lastTradePrice),
        });
      });
      return items.sort((a, b) => a.sort - b.sort || a.name.localeCompare(b.name));
    },
    compactListShown() {
      return this.compactListItems.slice(0, this.compactListRows);
    },
    compactListTitle() {
      if (this.isElectionMap) {
        return 'Closest races';
      }
      return this.isCollectionMap ? 'Most markets' : 'Leading states';
    },
    mapTiles() {
      if (!this.isTileMap) {
        return [];
      }

      const layout = this.isDistrictMap
        ? TILE_MAP_LAYOUT.concat(TILE_MAP_DISTRICT_LAYOUT)
        : TILE_MAP_LAYOUT;
      const shapes = layout.map(([code]) => ({
        name: this.getStateName(code, code),
        properties: { 'hc-a2': code },
      }));
      // Same per-state data and colors as the geographic map.
      return this.getChartData(shapes).map((point, index) => ({
        ...point,
        row: layout[index][1],
        column: layout[index][2],
        label: this.getLocationLabel(point.stateCode),
        price: this.getTilePrice(point),
        textColor: point.dataLabels ? point.dataLabels.style.color : '#ffffff',
      }));
    },
    noMarketKeyItems() {
      if (!this.isElectionMap) {
        return [];
      }

      const democratic = this.getBucketColor(PARTY_DEMOCRATIC, 'high');
      const republican = this.getBucketColor(PARTY_REPUBLICAN, 'high');
      const surface = this.isDarkTheme ? DARK_MAP_BORDER_COLOR : '#ffffff';
      const split = (left, right) => `linear-gradient(90deg, ${left} 50%, ${right} 50%)`;
      const tier = (bucket, label) => ({
        key: bucket,
        label,
        swatch: split(
          this.getBucketColor(PARTY_DEMOCRATIC, bucket),
          this.getBucketColor(PARTY_REPUBLICAN, bucket),
        ),
      });
      const items = [
        tier('high', 'Likely'),
        tier('medium', 'Lean'),
        tier('low', 'Slight lean'),
        { key: BUCKET_TOSS_UP, label: 'Toss-up', swatch: TOSS_UP_COLOR },
      ];
      if (this.electionRows.some((row) => row.isCalled)) {
        items.unshift(tier(BUCKET_FULL, 'Called'));
      }
      const hasIndependent = (this.mapData || []).some((location) =>
        this.getContractsForLocation(location).some(
          (contract) => this.getContractParty(contract) === PARTY_INDEPENDENT,
        ),
      );
      if (hasIndependent) {
        items.push({
          key: PARTY_INDEPENDENT,
          label: 'Independent',
          swatch: split(DEFAULT_PALETTE.independent.high, DEFAULT_PALETTE.independent.low),
        });
      }
      if (this.predefinedGroups.some((group) => group && group.isDecided)) {
        items.push({
          key: 'safe',
          label: 'Safe, no market',
          swatch: split(
            mixHexColors(democratic, surface, SAFE_TINT_WEIGHT),
            mixHexColors(republican, surface, SAFE_TINT_WEIGHT),
          ),
        });
      }
      if (this.predefinedGroups.some((group) => group && !group.isDecided)) {
        items.push({ key: 'none', label: 'No election', swatch: this.getEmptyColor() });
      }
      return items;
    },
    keyInSummaryInfo() {
      // Summary only has no map for the key to explain. The compact summary column has room for
      // the key under the seat arc, unless the map is short.
      return (
        (this.compactLayout !== COMPACT_LAYOUT_SIDE || this.compactTight > 0) &&
        this.showSummary &&
        this.showMapArea &&
        !!this.summaryHeadlineParts &&
        !!this.summarySubheader
      );
    },
    activeTile() {
      return this.mapTiles.find((tile) => tile.stateCode === this.activeTileCode) || null;
    },
    activeTileHtml() {
      return this.activeTile ? this.getTooltipHtml(this.activeTile) : '';
    },
    tileCardAtTop() {
      return (
        !!this.compactLayout &&
        !!this.activeTile &&
        this.activeTile.row >= COMPACT_TILE_CARD_TOP_ROW
      );
    },
    raceLabel() {
      return ((this.mapConfig && this.mapConfig.raceLabel) || '').trim() || DEFAULT_RACE_LABEL;
    },
    overviewContracts() {
      const itemWithOverview = (this.mapData || []).find(
        (item) => Array.isArray(item.overviewContracts) && item.overviewContracts.length > 0,
      );
      // A summary-only map may have no linked locations; V2 keeps the overview with its settings.
      const source = itemWithOverview || this.mapSettings || {};
      return Array.isArray(source.overviewContracts) ? source.overviewContracts : [];
    },
    summaryLeader() {
      const contracts = this.overviewContracts;
      const winner = this.showCalledRaces
        ? contracts.find((contract) => contract && contract.winner === true)
        : null;
      return winner || getLeadingTradedContract(contracts);
    },
    summaryHeadlineParts() {
      // "Democrats have a 65% chance to control the Senate", from the parent (overview) market.
      const outcome = ((this.mapConfig && this.mapConfig.summaryOutcome) || '').trim();
      const leader = this.summaryLeader;
      if (!outcome || !leader) {
        return null;
      }

      const isCalled = this.isCalledContract(leader);
      if (!isCalled && !hasLastTradePrice(leader)) {
        return null;
      }

      const name = (leader.shortName || leader.contractName || '').trim();
      const party = this.getContractParty(this.getOverviewContract(leader));
      const partyPlural = PARTY_CONTRACT_NAME.test(name) ? PARTY_PLURAL_NAMES[party] || '' : '';
      // An overview contract can word its own headline (map editor): a subject, has/have and an
      // outcome, for markets with more than two outcomes (e.g. balance of power).
      const subject = (leader.headlineSubject || '').trim() || partyPlural || name;
      const isPlural =
        typeof leader.headlinePlural === 'boolean'
          ? leader.headlinePlural
          : Object.values(PARTY_PLURAL_NAMES).includes(subject);
      const leaderOutcome = (leader.headlineOutcome || '').trim() || outcome;
      const accent = {
        accent: true,
        party,
        color: !party && SAFE_TOOLTIP_COLOR.test(leader.color || '') ? leader.color : '',
      };
      const leaderPart = { text: subject, ...accent };
      if (isCalled) {
        return [leaderPart, { text: ` will ${leaderOutcome}` }];
      }

      const percent = Math.round(Number(leader.lastTradePrice) * 100);
      const tossUp = { text: `The race to ${outcome} is a toss-up` };
      // Election maps: a leader at or under the toss-up price, as on the map, in a two-way market
      // (with more outcomes, 50% is a clear lead). Generic maps (often many candidates): only an
      // exact tie at the shown percent.
      if (
        this.isElectionMap &&
        this.overviewContracts.length <= 2 &&
        percent <= PRICE_TIER_CENTS.TOSS_UP
      ) {
        return [tossUp];
      }
      const runnerUp = getLeadingTradedContract(
        this.overviewContracts.filter((contract) => contract !== leader),
      );
      if (
        runnerUp &&
        hasLastTradePrice(runnerUp) &&
        Math.round(Number(runnerUp.lastTradePrice) * 100) === percent
      ) {
        return [tossUp];
      }

      return [
        leaderPart,
        { text: ` ${isPlural ? 'have' : 'has'} ${AN_PERCENT.test(String(percent)) ? 'an' : 'a'} ` },
        { text: `${percent}% chance`, ...accent },
        { text: ` to ${leaderOutcome}` },
      ];
    },
    summaryHeadlineUrl() {
      const leader = this.summaryLeader;
      return leader && Number(leader.marketId) > 0
        ? this.getMarketDetailUrl(leader.marketId, leader.contractId)
        : '';
    },
    // Senate-style maps only: electoral and generic maps have no ballot count, whatever is stored.
    summarySeatsOnBallot() {
      if (!this.isSenateMap) {
        return 0;
      }
      const override = Number(this.mapConfig && this.mapConfig.seatsOnBallot);
      if (override > 0) {
        return override;
      }

      // Each linked race counts once (Georgia's regular + special = 2; a state's yes/no markets,
      // one per candidate, are one race) and decided races add their seats; no-election states
      // don't count.
      const grouped = this.groupLocationsByStateCode();
      const raceSeats = Object.keys(grouped).reduce((total, stateCode) => {
        const group = this.getPredefinedGroupForState(stateCode);
        if (group && group.isLocked) {
          return total;
        }

        const marketIds = new Set();
        let hasYesNoRace = false;
        grouped[stateCode].forEach((location) => {
          const markets =
            Array.isArray(location.markets) && location.markets.length > 0
              ? location.markets
              : [location];
          markets.forEach((market) => {
            if (this.isMarketSingleOutcome(market)) {
              hasYesNoRace = true;
            } else if (Number(market.marketId) > 0) {
              marketIds.add(Number(market.marketId));
            }
          });
        });
        return total + marketIds.size + (hasYesNoRace ? 1 : 0);
      }, 0);
      const decidedSeats = this.predefinedGroups
        .filter((group) => group && group.isDecided)
        .reduce((total, group) => total + (Number(group.seatCount) || 0), 0);
      return raceSeats + decidedSeats;
    },
    summaryBallotText() {
      const count = this.summarySeatsOnBallot;
      if (!(count > 0)) {
        return '';
      }

      const noun =
        ((this.mapConfig && this.mapConfig.ballotNoun) || '').trim() || DEFAULT_BALLOT_NOUN;
      return `${count} ${count === 1 ? noun.replace(/s$/i, '') : noun} on the ballot`;
    },
    summaryDaysText() {
      const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(
        ((this.mapConfig && this.mapConfig.electionDate) || '').trim(),
      );
      if (!match) {
        return '';
      }

      const electionDay = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
      const days = Math.round((electionDay - getEasternToday()) / DAY_MS);
      if (days < 0) {
        return '';
      }
      if (days === 0) {
        return 'Election Day';
      }

      return `${days} day${days === 1 ? '' : 's'} to Election`;
    },
    summarySubheader() {
      return [this.summaryBallotText, this.summaryDaysText].filter(Boolean).join(' · ');
    },
    summaryInfoText() {
      return SUMMARY_INFO_TEXT;
    },
    currentSeats() {
      // CurrentSeats is keyed by party name; the API camel-cases the keys ("democratic"). Senate-style
      // maps only: an electoral or generic map ignores stored values.
      const seats = (this.isSenateMap && this.mapConfig && this.mapConfig.currentSeats) || {};
      return Object.keys(seats).reduce(
        (result, key) => {
          const party = this.getContractParty({ party: key });
          const count = Number(seats[key]);
          if (party && Number.isFinite(count)) {
            result[party] = count;
          }
          return result;
        },
        { [PARTY_DEMOCRATIC]: null, [PARTY_REPUBLICAN]: null },
      );
    },
    hasCurrentSeats() {
      return (
        this.currentSeats[PARTY_DEMOCRATIC] !== null || this.currentSeats[PARTY_REPUBLICAN] !== null
      );
    },
    showExportButton() {
      return !this.hasError && this.getMapFlag('showExports', true);
    },
    democraticToolbarHint() {
      return this.getToolbarHint(this.democraticToolbarCounter);
    },
    republicanToolbarHint() {
      return this.getToolbarHint(this.republicanToolbarCounter);
    },
    majorityCaption() {
      const label = this.majorityLabel;
      return /\d/.test(label) ? `${label} to win` : label;
    },
    zoomSliderMax() {
      return Math.log2(MAP_ZOOM_MAX_FACTOR);
    },
    zoomSliderValue() {
      return Math.log2(Math.max(1, this.zoomFactor));
    },
    isMapZoomed() {
      return this.zoomFactor >= MAP_ZOOMED_THRESHOLD;
    },
    zoomSliderFill() {
      return `${Math.round((this.zoomSliderValue / this.zoomSliderMax) * 100)}%`;
    },
    zoomReadout() {
      return `${this.isMapZoomed ? Number(this.zoomFactor.toFixed(1)) : 1}×`;
    },
    predefinedGroups() {
      const groups = (this.mapConfig && this.mapConfig.predefinedGroups) || [];
      return Array.isArray(groups) ? groups : [];
    },
    toolbarBaselines() {
      const baselines = (this.mapConfig && this.mapConfig.toolbarBaselines) || [];
      return Array.isArray(baselines) ? baselines : [];
    },
    electionRows() {
      const groupedByStateCode = this.groupLocationsByStateCode();
      return Object.keys(groupedByStateCode).reduce((rows, stateCode) => {
        const predefinedGroup = this.getPredefinedGroupForState(stateCode);
        if (predefinedGroup && predefinedGroup.isLocked) {
          return rows;
        }

        const representative = this.getRepresentativeLocation(groupedByStateCode[stateCode]);
        const leadingContract = this.getLeadingContract(representative);
        rows.push({
          stateCode,
          locationData: representative,
          leadingContract,
          isCalled: this.isCalledContract(leadingContract),
          isTossUp: this.isTossUpLocation(representative),
          party: this.getContractParty(leadingContract),
          stateValue: this.getStateValue(stateCode, representative),
        });
        return rows;
      }, []);
    },
    democraticToolbarCounter() {
      return this.buildToolbarCounter(PARTY_DEMOCRATIC);
    },
    republicanToolbarCounter() {
      return this.buildToolbarCounter(PARTY_REPUBLICAN);
    },
    tossUpToolbarCounter() {
      return this.buildMiddleToolbarCounter(BUCKET_TOSS_UP);
    },
    independentToolbarCounter() {
      return this.buildMiddleToolbarCounter(PARTY_INDEPENDENT);
    },
    toolbarTotalWidth() {
      const total =
        this.democraticToolbarCounter.widthTotal +
        this.tossUpToolbarCounter.widthTotal +
        this.independentToolbarCounter.widthTotal +
        this.republicanToolbarCounter.widthTotal;
      return total > 0 ? total : 1;
    },
    // The middle segments' counts, each centered over its slice of the bar (positionMiddleLabels
    // nudges them clear of the party labels).
    toolbarMiddleLabels() {
      let start = 0;
      return this.toolbarSegments.reduce((labels, segment) => {
        if (MIDDLE_SEGMENT_LABELS[segment.bucket]) {
          labels.push({
            key: segment.key,
            bucket: segment.bucket,
            value: segment.value,
            label: MIDDLE_SEGMENT_LABELS[segment.bucket],
            center: start + segment.width / 2,
          });
        }
        start += segment.width;
        return labels;
      }, []);
    },
    majorityLabel() {
      if (this.mapConfig && this.mapConfig.majorityLabel) {
        return this.mapConfig.majorityLabel;
      }

      if (this.mapConfig && this.mapConfig.majorityTarget) {
        return this.isSenateMap
          ? `${this.mapConfig.majorityTarget} Seats`
          : `${this.mapConfig.majorityTarget}`;
      }

      return this.isSenateMap ? '50 Seats' : 'Majority';
    },
    toolbarSegments() {
      const democratic = this.democraticToolbarCounter;
      const republican = this.republicanToolbarCounter;
      const segments = [
        this.createToolbarSegment(democratic, 'predefined', PARTY_DEMOCRATIC),
        this.createToolbarSegment(democratic, BUCKET_FULL, PARTY_DEMOCRATIC),
        this.createToolbarSegment(democratic, 'high', PARTY_DEMOCRATIC),
        this.createToolbarSegment(democratic, 'medium', PARTY_DEMOCRATIC),
        this.createToolbarSegment(democratic, 'low', PARTY_DEMOCRATIC),
        this.createToolbarSegment(this.tossUpToolbarCounter, BUCKET_TOSS_UP, BUCKET_TOSS_UP),
        this.createToolbarSegment(
          this.independentToolbarCounter,
          PARTY_INDEPENDENT,
          PARTY_INDEPENDENT,
        ),
        this.createToolbarSegment(republican, 'low', PARTY_REPUBLICAN),
        this.createToolbarSegment(republican, 'medium', PARTY_REPUBLICAN),
        this.createToolbarSegment(republican, 'high', PARTY_REPUBLICAN),
        this.createToolbarSegment(republican, BUCKET_FULL, PARTY_REPUBLICAN),
        this.createToolbarSegment(republican, 'predefined', PARTY_REPUBLICAN),
      ];

      return segments.filter((segment) => segment.value > 0 && segment.width > 0);
    },
    mapTableRows() {
      // Collection maps: one row per market, with its leading listed contract.
      if (this.isCollectionMap) {
        return (this.mapData || []).reduce((rows, item, locationIndex) => {
          const locationCode = this.normalizeLocationCode(item.locationCode);
          this.getCollectionMarkets(item).forEach((market) => {
            const contract = getLeadingTradedContract(market.contracts) || market.contracts[0];
            rows.push({
              key: `${locationCode || 'location'}-${locationIndex}-${market.key}`,
              stateCode: locationCode,
              marketName: market.name || 'Market',
              contractName: market.isSingleOutcome
                ? ''
                : contract.contractName || contract.name || 'Contract',
              lastTradePrice: hasLastTradePrice(contract) ? contract.lastTradePrice : null,
              status: market.status || item.status || '',
              color: item.overrideColor || this.collectionColor,
            });
          });
          return rows;
        }, []);
      }

      return (this.mapData || []).reduce((rows, item, locationIndex) => {
        const locationCode = this.normalizeLocationCode(item.locationCode);
        const markets =
          Array.isArray(item.markets) && item.markets.length > 0 ? item.markets : [item];

        markets.forEach((market, marketIndex) => {
          const contracts = Array.isArray(market.contracts) ? market.contracts : [];
          if (contracts.length === 0) {
            rows.push({
              key: `${locationCode || 'location'}-${locationIndex}-${market.marketId || marketIndex}`,
              stateCode: locationCode,
              marketName: market.marketName || market.name || 'Market',
              contractName: '',
              lastTradePrice: null,
              status: market.status || item.status || '',
              color: item.overrideColor || '',
            });
            return;
          }

          contracts.forEach((contract, contractIndex) => {
            rows.push({
              key: `${locationCode || 'location'}-${locationIndex}-${market.marketId || marketIndex}-${contract.contractId || contractIndex}`,
              stateCode: locationCode,
              marketName: market.marketName || market.name || 'Market',
              contractName: contract.contractName || contract.name || 'Contract',
              lastTradePrice: hasLastTradePrice(contract) ? contract.lastTradePrice : null,
              status: market.status || item.status || '',
              color: this.getTableRowColor(contract, item),
            });
          });
        });

        return rows;
      }, []);
    },
  },
  methods: {
    toPercent(value) {
      const normalized = Number(value);
      if (isNaN(normalized)) {
        return 0;
      }
      return Math.round(normalized * 100);
    },
    formatPrice(value) {
      return `${this.toPercent(value)}${this.priceUnitSymbol}`;
    },
    formatPriceChange(change) {
      return `${Math.abs(change)}${this.priceUnitSymbol}`;
    },
    setPriceUnit(unit) {
      if (unit === this.priceUnit) {
        return;
      }

      this.priceUnit = unit;
      try {
        localStorage.setItem(PRICE_UNIT_STORAGE_KEY, unit);
      } catch (error) {
        log.info('cms-map-widget: localStorage unavailable, could not save the price unit.', error);
      }
      this.refreshMapLabels();
    },
    refreshMapLabels() {
      // Data labels are formatted when drawn; updating the series makes the redraw re-run the
      // formatter (tooltips and the HTML panels re-read the unit on their own).
      if (this.chart && this.chart.series) {
        this.chart.series.forEach((series) => series.update({}, false));
        this.chart.redraw(false);
      }
    },
    hasTradePrice(contract) {
      return hasLastTradePrice(contract);
    },
    getMapZoomFactor() {
      const view = this.chart && this.chart.mapView;
      return view ? 2 ** (view.zoom - view.minZoom) : 1;
    },
    syncMapZoom() {
      this.zoomFactor = this.getMapZoomFactor();
    },
    applyMapZoomLimit() {
      const view = this.chart && this.chart.mapView;
      if (view && typeof view.minZoom === 'number') {
        // Pinch zoom has no upper bound by default; MapView.setView clamps to options.maxZoom.
        view.options.maxZoom = view.minZoom + Math.log2(MAP_ZOOM_MAX_FACTOR);
      }
    },
    setMapZoomFactor(factor) {
      const view = this.chart && this.chart.mapView;
      if (!view) {
        return;
      }

      // No animation: the HTML state labels jump straight to their final positions, so an
      // animated zoom leaves them over the wrong states until the map catches up.
      const target = Math.min(MAP_ZOOM_MAX_FACTOR, Math.max(1, factor));
      if (target < MAP_ZOOMED_THRESHOLD) {
        view.zoomBy(undefined, undefined, undefined, false);
      } else {
        view.setView(undefined, view.minZoom + Math.log2(target), true, false);
      }
      this.syncMapZoom();
    },
    onZoomSliderInput(event) {
      this.setMapZoomFactor(2 ** Number(event.target.value));
    },
    zoomMapIn() {
      this.setMapZoomFactor(this.getMapZoomFactor() * 2);
    },
    zoomMapOut() {
      this.setMapZoomFactor(this.getMapZoomFactor() / 2);
    },
    resetMapZoom() {
      this.setMapZoomFactor(1);
    },
    getFullscreenElement() {
      if (typeof document === 'undefined') {
        return null;
      }

      return document.fullscreenElement || document.webkitFullscreenElement || null;
    },
    toggleWidgetFullscreen() {
      if (!this.$el) {
        return;
      }

      if (this.getFullscreenElement() === this.$el) {
        const exit = document.exitFullscreen || document.webkitExitFullscreen;
        if (exit) {
          exit.call(document);
        }
        return;
      }

      // Fullscreen the whole card (header, menu, zoom control, overview), not just the chart.
      const request = this.$el.requestFullscreen || this.$el.webkitRequestFullscreen;
      if (!request) {
        return;
      }

      const result = request.call(this.$el);
      if (result && typeof result.catch === 'function') {
        // Some embedded browsers refuse element fullscreen; the widget simply stays inline.
        result.catch(() => {});
      }
    },
    onFullscreenChange() {
      this.isWidgetFullscreen = this.getFullscreenElement() === this.$el;
      this.closeZoomPanel();
      this.syncFullscreenMenuText();
      this.$nextTick(() => {
        if (this.chart) {
          this.chart.reflow();
        }
      });
    },
    syncFullscreenMenuText() {
      const exporting = this.chart && this.chart.exporting;
      const items = this.canShowDataTable
        ? MAP_WIDGET_CONTEXT_MENU_ITEMS
        : DEFAULT_CONTEXT_MENU_ITEMS;
      const element =
        exporting &&
        exporting.divElements &&
        exporting.divElements[items.indexOf('viewFullscreen')];
      if (!element) {
        return;
      }

      const lang = (this.chart.options && this.chart.options.lang) || {};
      element.textContent = this.isWidgetFullscreen
        ? lang.exitFullscreen || 'Exit from full screen'
        : lang.viewFullscreen || 'View in full screen';
    },
    toggleZoomPanel() {
      this.isZoomPanelOpen = !this.isZoomPanelOpen;
    },
    closeZoomPanel() {
      this.isZoomPanelOpen = false;
    },
    dismissPinchHint() {
      this.showPinchHint = false;
      this.shouldShowPinchHint = false;
      try {
        localStorage.setItem(PINCH_HINT_STORAGE_KEY, 'true');
      } catch (error) {
        log.info(
          'cms-map-widget: localStorage unavailable, could not set PINCH_HINT_STORAGE_KEY state.',
          error,
        );
      }
    },
    toggleDataTable() {
      if (!this.canShowDataTable) {
        return;
      }

      this.showDataTable = !this.showDataTable;
      if (this.showDataTable) {
        this.closeZoomPanel();
      }
    },
    hideDataTable() {
      this.showDataTable = false;
    },
    syncDataTableOutsideClose(isOpen) {
      // A press anywhere outside the open table closes it. It runs on pointerdown, before the
      // menu button's click, so the menu never opens (raising the map) over the open table.
      if (typeof document === 'undefined') {
        return;
      }

      if (isOpen && !this.dataTableOutsideHandler) {
        this.dataTableOutsideHandler = (event) => {
          const panel = this.$refs.tablePanel;
          if (panel && event.target instanceof Node && !panel.contains(event.target)) {
            this.hideDataTable();
          }
        };
        document.addEventListener('pointerdown', this.dataTableOutsideHandler, true);
      } else if (!isOpen && this.dataTableOutsideHandler) {
        document.removeEventListener('pointerdown', this.dataTableOutsideHandler, true);
        this.dataTableOutsideHandler = null;
      }
    },
    clearContextMenuOverflowContainers() {
      (this.contextMenuOverflowContainers || []).forEach((element) => {
        if (element && element.classList) {
          element.classList.remove(CONTEXT_MENU_OVERFLOW_CLASS);
        }
      });
      this.contextMenuOverflowContainers = [];
    },
    syncContextMenuOverflowContainers(isOpen) {
      this.clearContextMenuOverflowContainers();

      if (!isOpen || !this.$el || typeof window === 'undefined') {
        return;
      }

      let element = this.$el.parentElement;
      while (element && element !== document.body) {
        const style = window.getComputedStyle(element);
        if (style.overflowX !== 'visible' || style.overflowY !== 'visible') {
          element.classList.add(CONTEXT_MENU_OVERFLOW_CLASS);
          this.contextMenuOverflowContainers.push(element);
        }
        element = element.parentElement;
      }
    },
    positionContextMenu() {
      if (!this.$el || typeof window === 'undefined') {
        return;
      }

      const contextMenu = this.$el.querySelector('.highcharts-contextmenu');
      if (!contextMenu) {
        return;
      }

      // Keep the visible list on screen, not Highcharts' transparent padding box around it (28px
      // of hover buffer on the right), and measure against the width without the scrollbar;
      // otherwise narrow screens pushed the menu left of the button.
      const list = contextMenu.querySelector('.highcharts-menu') || contextMenu;
      const bounds = list.getBoundingClientRect();
      const currentTop = parseFloat(contextMenu.style.top || '0') || contextMenu.offsetTop || 0;
      const currentLeft = parseFloat(contextMenu.style.left || '0') || contextMenu.offsetLeft || 0;
      let maxBottom = window.innerHeight - CONTEXT_MENU_VIEWPORT_PADDING;
      const viewportWidth =
        (document.documentElement && document.documentElement.clientWidth) || window.innerWidth;
      const maxRight = viewportWidth - CONTEXT_MENU_VIEWPORT_PADDING;
      let nextTop = currentTop;
      let nextLeft = currentLeft;

      CONTEXT_MENU_BOTTOM_UI_SELECTORS.forEach((selector) => {
        const element = document.querySelector(selector);
        if (!element) {
          return;
        }
        const style = window.getComputedStyle(element);
        const elementBounds = element.getBoundingClientRect();
        const isVisible =
          style.display !== 'none' &&
          style.visibility !== 'hidden' &&
          elementBounds.width > 0 &&
          elementBounds.height > 0;

        if (isVisible && elementBounds.bottom >= window.innerHeight - 1) {
          maxBottom = Math.min(maxBottom, elementBounds.top - CONTEXT_MENU_VIEWPORT_PADDING);
        }
      });

      if (bounds.bottom > maxBottom) {
        nextTop -= bounds.bottom - maxBottom;
      }
      if (bounds.top + (nextTop - currentTop) < CONTEXT_MENU_VIEWPORT_PADDING) {
        nextTop += CONTEXT_MENU_VIEWPORT_PADDING - (bounds.top + (nextTop - currentTop));
      }
      if (bounds.right > maxRight) {
        nextLeft -= bounds.right - maxRight;
      }
      if (bounds.left + (nextLeft - currentLeft) < CONTEXT_MENU_VIEWPORT_PADDING) {
        nextLeft += CONTEXT_MENU_VIEWPORT_PADDING - (bounds.left + (nextLeft - currentLeft));
      }

      contextMenu.style.top = `${Math.round(nextTop)}px`;
      contextMenu.style.left = `${Math.round(nextLeft)}px`;
      contextMenu.style.maxHeight = `${Math.max(180, Math.round(window.innerHeight - CONTEXT_MENU_VIEWPORT_PADDING * 2))}px`;
      contextMenu.style.overflowY = 'auto';
    },
    syncContextMenuState() {
      if (!this.$el || typeof window === 'undefined') {
        return;
      }

      const contextMenu = this.$el.querySelector('.highcharts-contextmenu');
      let isOpen = false;
      if (contextMenu) {
        const style = window.getComputedStyle(contextMenu);
        const bounds = contextMenu.getBoundingClientRect();
        isOpen =
          style.display !== 'none' &&
          style.visibility !== 'hidden' &&
          bounds.width > 0 &&
          bounds.height > 0;
      }

      if (this.isContextMenuOpen !== isOpen) {
        this.isContextMenuOpen = isOpen;
        this.syncContextMenuOverflowContainers(isOpen);
        this.syncContextMenuScrollClose(isOpen);
      }
      if (isOpen) {
        this.positionContextMenu();
      }
    },
    syncContextMenuScrollClose(isOpen) {
      // While the menu is open the card is raised (z-index 3, under the fixed site header) so the
      // menu can overflow it over the content below; the menu still closes on scroll.
      if (typeof window === 'undefined') {
        return;
      }

      if (isOpen && !this.contextMenuScrollHandler) {
        this.contextMenuScrollHandler = (event) => {
          const contextMenu = this.$el && this.$el.querySelector('.highcharts-contextmenu');
          // Scrolling the (tall) menu list itself must not close it.
          if (contextMenu && event.target instanceof Node && contextMenu.contains(event.target)) {
            return;
          }
          this.closeExportMenu();
        };
        window.addEventListener('scroll', this.contextMenuScrollHandler, {
          capture: true,
          passive: true,
        });
      } else if (!isOpen && this.contextMenuScrollHandler) {
        window.removeEventListener('scroll', this.contextMenuScrollHandler, { capture: true });
        this.contextMenuScrollHandler = null;
      }
    },
    closeExportMenu() {
      const contextMenu = this.$el && this.$el.querySelector('.highcharts-contextmenu');
      if (contextMenu && typeof contextMenu.hideMenu === 'function') {
        contextMenu.hideMenu();
      }
    },
    stopContextMenuObserver() {
      if (this.contextMenuObserver) {
        this.contextMenuObserver.disconnect();
        this.contextMenuObserver = null;
      }
      this.clearContextMenuOverflowContainers();
      this.syncContextMenuScrollClose(false);
      this.isContextMenuOpen = false;
    },
    startContextMenuObserver() {
      this.stopContextMenuObserver();
      if (!this.$el || typeof MutationObserver === 'undefined') {
        return;
      }

      this.contextMenuObserver = new MutationObserver(() => this.syncContextMenuState());
      this.contextMenuObserver.observe(this.$el, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class'],
      });
      this.syncContextMenuState();
    },
    isMobileLayout() {
      if (typeof window === 'undefined' || typeof window.matchMedia === 'undefined') {
        return false;
      }
      return window.matchMedia('(max-width: 767px)').matches;
    },
    getMapFlag(fieldName, defaultValue) {
      if (
        !this.mapConfig ||
        this.mapConfig[fieldName] === null ||
        typeof this.mapConfig[fieldName] === 'undefined'
      ) {
        return defaultValue;
      }

      return this.mapConfig[fieldName] !== false;
    },
    normalizeLocationCode(code) {
      const normalized = (code || '').toUpperCase().replace(/-/g, '');
      if (!normalized) {
        return '';
      }

      // District maps keep districts ("ME-01" -> "ME01"); elsewhere they fold into their state.
      const district = this.isDistrictMap ? normalized.match(/^([A-Z]{2})(\d{1,2})$/) : null;
      if (district) {
        return `${district[1]}${district[2].padStart(2, '0')}`;
      }

      if (normalized.length >= 2) {
        return normalized.substring(0, 2);
      }

      return normalized;
    },
    getContractsForLocation(marketData) {
      if (!marketData) {
        return [];
      }

      const linkedMarkets = Array.isArray(marketData.markets) ? marketData.markets : [];
      if (linkedMarkets.length > 0) {
        return linkedMarkets.reduce(
          (contracts, market) =>
            contracts.concat(Array.isArray(market.contracts) ? market.contracts : []),
          [],
        );
      }

      return Array.isArray(marketData.contracts) ? marketData.contracts : [];
    },
    getLeadingMarketSelection(marketData) {
      if (!marketData) {
        return { market: null, contract: null, marketIndex: -1 };
      }

      const linkedMarkets = Array.isArray(marketData.markets) ? marketData.markets : [];
      const markets = linkedMarkets.length > 0 ? linkedMarkets : [marketData];
      const selection = {
        market: markets[0],
        contract: null,
        marketIndex: 0,
      };

      markets.forEach((market, marketIndex) => {
        const contracts = Array.isArray(market.contracts) ? market.contracts : [];
        const leadingContract = getLeadingTradedContract(contracts);
        if (!leadingContract) {
          return;
        }

        if (
          !selection.contract ||
          Number(leadingContract.lastTradePrice) > Number(selection.contract.lastTradePrice)
        ) {
          selection.market = market;
          selection.contract = leadingContract;
          selection.marketIndex = marketIndex;
        }
      });

      return selection;
    },
    getTooltipContractEntries(marketData) {
      if (!marketData) {
        return [];
      }

      const linkedMarkets = Array.isArray(marketData.markets) ? marketData.markets : [];
      if (linkedMarkets.length === 0) {
        return (Array.isArray(marketData.contracts) ? marketData.contracts : []).map(
          (contract) => ({
            contract,
            marketId: marketData.marketId || null,
            marketName: marketData.marketName || '',
          }),
        );
      }

      let orderedMarkets = linkedMarkets;
      if (linkedMarkets.length > 1 && this.getDistinctLinkedMarketCount(marketData) > 1) {
        const selection = this.getLeadingMarketSelection(marketData);
        if (selection.marketIndex > 0) {
          orderedMarkets = linkedMarkets.slice();
          orderedMarkets.splice(selection.marketIndex, 1);
          orderedMarkets.unshift(selection.market);
        }
      }

      return orderedMarkets.reduce((entries, market) => {
        const marketId = market.marketId || marketData.marketId || null;
        const marketName = market.marketName || market.name || '';
        return entries.concat(
          (Array.isArray(market.contracts) ? market.contracts : []).map((contract) => ({
            contract,
            marketId,
            marketName,
          })),
        );
      }, []);
    },
    getTooltipContractsForLocation(marketData) {
      return this.getTooltipContractEntries(marketData).map((entry) => entry.contract);
    },
    getLeadingMarketName(marketData, fallbackName) {
      const selection = this.getLeadingMarketSelection(marketData);
      const leadingMarket = selection.market || marketData || {};
      return (
        leadingMarket.marketName ||
        leadingMarket.name ||
        (marketData && (marketData.marketName || marketData.name)) ||
        fallbackName
      );
    },
    getDistinctLinkedMarketCount(marketData) {
      if (!marketData) {
        return 0;
      }

      const linkedMarkets = Array.isArray(marketData.markets) ? marketData.markets : [];
      if (linkedMarkets.length === 0) {
        return 1;
      }

      const marketIds = linkedMarkets.reduce((ids, market, marketIndex) => {
        const key =
          market.marketId === null || typeof market.marketId === 'undefined'
            ? `market-${marketIndex}`
            : `market-${market.marketId}`;
        ids[key] = true;
        return ids;
      }, {});

      return Object.keys(marketIds).length;
    },
    isSingleOutcomeMarket(marketData) {
      const selection = this.getLeadingMarketSelection(marketData);
      const selectedMarket = selection.market || marketData || {};
      if (typeof selectedMarket.isSingleOutcome === 'boolean') {
        return selectedMarket.isSingleOutcome;
      }
      if (selectedMarket.marketType !== null && typeof selectedMarket.marketType !== 'undefined') {
        return Number(selectedMarket.marketType) === 0;
      }
      if (marketData && typeof marketData.isSingleOutcome === 'boolean') {
        return marketData.isSingleOutcome;
      }
      if (
        marketData &&
        marketData.marketType !== null &&
        typeof marketData.marketType !== 'undefined'
      ) {
        return Number(marketData.marketType) === 0;
      }

      const contracts = Array.isArray(selectedMarket.contracts) ? selectedMarket.contracts : [];
      const fallbackName =
        contracts.length === 1
          ? (contracts[0].contractName || contracts[0].name || '').toLowerCase()
          : '';
      return fallbackName === 'yes';
    },
    isMarketSingleOutcome(market) {
      if (!market) {
        return false;
      }
      if (typeof market.isSingleOutcome === 'boolean') {
        return market.isSingleOutcome;
      }
      if (market.marketType !== null && typeof market.marketType !== 'undefined') {
        return Number(market.marketType) === 0;
      }

      const contracts = Array.isArray(market.contracts) ? market.contracts : [];
      return (
        contracts.length === 1 &&
        (contracts[0].contractName || contracts[0].name || '').toLowerCase() === 'yes'
      );
    },
    hasMultipleSingleOutcomeMarkets(marketData) {
      const linkedMarkets =
        marketData && Array.isArray(marketData.markets) ? marketData.markets : [];
      return (
        this.getDistinctLinkedMarketCount(marketData) > 1 &&
        linkedMarkets.every((market) => this.isMarketSingleOutcome(market))
      );
    },
    isSupportedSingleOutcomeLocation(marketData) {
      return (
        this.getDistinctLinkedMarketCount(marketData) <= 1 && this.isSingleOutcomeMarket(marketData)
      );
    },
    getContractDisplayName(contract, marketData) {
      if (this.isSupportedSingleOutcomeLocation(marketData)) {
        return '';
      }

      return (contract && (contract.contractName || contract.name)) || 'Contract';
    },
    hasMultiContractMarket(marketData) {
      if (!marketData) {
        return false;
      }

      const linkedMarkets = Array.isArray(marketData.markets) ? marketData.markets : [];
      if (linkedMarkets.length === 0) {
        return Array.isArray(marketData.contracts) && marketData.contracts.length > 1;
      }

      const contractCountByMarket = linkedMarkets.reduce((counts, market, index) => {
        const key =
          market.marketId === null || typeof market.marketId === 'undefined'
            ? `market-${index}`
            : `market-${market.marketId}`;
        const contracts = Array.isArray(market.contracts) ? market.contracts : [];
        counts[key] = (counts[key] || 0) + contracts.length;
        return counts;
      }, {});

      return Object.keys(contractCountByMarket).some((key) => contractCountByMarket[key] > 1);
    },
    getLeadingContract(marketData) {
      const contracts = this.getContractsForLocation(marketData);
      // A settled market's winning contract is the state's result, whatever its last trade.
      const winner = this.showCalledRaces
        ? contracts.find((contract) => contract && contract.winner === true)
        : null;
      return winner || getLeadingTradedContract(contracts);
    },
    isCalledContract(contract) {
      return this.showCalledRaces && !!contract && contract.winner === true;
    },
    getContractName(contract) {
      return (
        (contract && (contract.contractName || contract.shortName || contract.name)) ||
        ''
      ).toLowerCase();
    },
    getContractParty(contract) {
      const party = ((contract && contract.party) || '').toLowerCase();
      if (party.includes('dem')) {
        return PARTY_DEMOCRATIC;
      }
      if (party.includes('rep') || party.includes('gop')) {
        return PARTY_REPUBLICAN;
      }
      if (party.includes('ind')) {
        return PARTY_INDEPENDENT;
      }

      const name = this.getContractName(contract);
      if (name.includes('republican') || name.includes('trump') || name.includes('rubio')) {
        return PARTY_REPUBLICAN;
      }
      if (
        name.includes('democrat') ||
        name.includes('biden') ||
        name.includes('shapiro') ||
        name.includes('whitmer') ||
        name.includes('buttigieg')
      ) {
        return PARTY_DEMOCRATIC;
      }

      return '';
    },
    getPartyAccentColor(contract) {
      return PARTY_ACCENT_COLORS[this.getContractParty(contract)] || '';
    },
    getTableRowColor(contract, location) {
      // Election rows show the contract's own party color (as in the tooltip), not the
      // state's fill, so a trailing contract isn't marked with the leader's color.
      if (this.isElectionMap) {
        return (
          this.getPartyAccentColor(contract) ||
          contract.color ||
          location.overrideColor ||
          this.resolveElectionColor(location)
        );
      }

      return contract.color || location.overrideColor || '';
    },
    getSplitLeaderColor(marketData) {
      if (!marketData || this.getDistinctLinkedMarketCount(marketData) <= 1) {
        return '';
      }

      const linkedMarkets = Array.isArray(marketData.markets) ? marketData.markets : [];
      const parties = linkedMarkets.reduce((acc, market) => {
        const leadingContract = getLeadingTradedContract(
          Array.isArray(market.contracts) ? market.contracts : [],
        );
        const party = this.getContractParty(leadingContract);
        if (party) {
          acc[party] = true;
        }
        return acc;
      }, {});

      return parties[PARTY_DEMOCRATIC] && parties[PARTY_REPUBLICAN] ? TOSS_UP_COLOR : '';
    },
    getRepresentativeLocation(locations) {
      if (!Array.isArray(locations) || locations.length === 0) {
        return null;
      }

      const sorted = locations.slice().sort((a, b) => {
        const contractA = this.getLeadingContract(a);
        const contractB = this.getLeadingContract(b);
        const scoreA = contractA ? Number(contractA.lastTradePrice) || 0 : 0;
        const scoreB = contractB ? Number(contractB.lastTradePrice) || 0 : 0;
        return scoreB - scoreA;
      });

      return sorted[0];
    },
    // The tiers are fixed (Holden, 2026-10-08); a map's PaletteThresholds setting is not used.
    getPaletteColor(party, price) {
      const bucket = this.getPaletteBucket(price);
      if (bucket === BUCKET_TOSS_UP) {
        return TOSS_UP_COLOR;
      }

      return (DEFAULT_PALETTE[party] || DEFAULT_PALETTE.gray)[bucket];
    },
    // A bucket's color; the no-election ("predefined") segment uses the full (settled) shade.
    getBucketColor(party, bucket) {
      if (bucket === BUCKET_TOSS_UP) {
        return TOSS_UP_COLOR;
      }

      const shades = DEFAULT_PALETTE[party] || DEFAULT_PALETTE.gray;
      if (bucket === 'predefined') {
        return shades[BUCKET_FULL] || shades.high;
      }
      return shades[bucket] || shades.high;
    },
    getPaletteBucket(price) {
      const cents = Math.round(Math.min(Math.max(Number(price) || 0, 0), 1) * 100);
      if (cents <= PRICE_TIER_CENTS.TOSS_UP) {
        return BUCKET_TOSS_UP;
      }
      if (cents <= PRICE_TIER_CENTS.SLIGHT_LEAN) {
        return 'low';
      }
      if (cents <= PRICE_TIER_CENTS.LEAN) {
        return 'medium';
      }
      return 'high';
    },
    // A linked state counts as a toss-up: its leader is at or under the toss-up price, or its
    // markets are led by different parties (a split state). Called races never are.
    isTossUpLocation(locationData) {
      const leader = this.getLeadingContract(locationData);
      if (!leader || this.isCalledContract(leader)) {
        return false;
      }

      return (
        !!this.getSplitLeaderColor(locationData) ||
        this.getPaletteBucket(leader.lastTradePrice) === BUCKET_TOSS_UP
      );
    },
    resolveBaseColor(contract) {
      const party = this.getContractParty(contract);
      if (party === PARTY_REPUBLICAN) {
        return '#bc1500';
      }
      if (party === PARTY_DEMOCRATIC) {
        return '#2f88dc';
      }
      if (party === PARTY_INDEPENDENT) {
        return DEFAULT_PALETTE.independent.high;
      }
      return '#9a9a9a';
    },
    applyGradient(color, contract, gradientSetting) {
      if (gradientSetting !== 2 || !contract) {
        return color;
      }

      const normalized = Math.min(Math.max(Number(contract.lastTradePrice) || 0, 0), 1);
      if (typeof color !== 'string' || !color.startsWith('#') || color.length !== 7) {
        return color;
      }

      const red = parseInt(color.substring(1, 3), 16);
      const green = parseInt(color.substring(3, 5), 16);
      const blue = parseInt(color.substring(5, 7), 16);
      const alpha = Math.max(0.35, Math.min(1, 0.35 + normalized * 0.65));

      return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
    },
    resolveColor(locationData, emptyColor) {
      if (!locationData) {
        return emptyColor;
      }

      if (this.isElectionMap) {
        return this.resolveElectionColor(locationData);
      }

      if (locationData.overrideColor) {
        return locationData.overrideColor;
      }

      // Collection maps: one color for states with markets (no single price to shade by).
      if (this.isCollectionMap) {
        return this.collectionColor;
      }

      const leadingContract = this.getLeadingContract(locationData);
      const contractColor = (leadingContract && leadingContract.color) || '';
      const baseColor = contractColor || this.resolveBaseColor(leadingContract);

      return this.applyGradient(
        baseColor,
        leadingContract,
        Number(locationData.gradientSetting) || 0,
      );
    },
    resolveElectionColor(locationData) {
      if (!locationData) {
        return this.getEmptyColor();
      }

      if (locationData.overrideColor) {
        return locationData.overrideColor;
      }

      const splitColor = this.getSplitLeaderColor(locationData);
      if (splitColor) {
        return splitColor;
      }

      const leadingContract = this.getLeadingContract(locationData);
      const contractColor = (leadingContract && leadingContract.color) || '';
      if (contractColor && (this.mapConfig.paletteMode || '').toLowerCase() !== 'partythreshold') {
        return contractColor;
      }

      const party = this.getContractParty(leadingContract);
      if (!party) {
        return this.getEmptyColor();
      }

      if (this.isCalledContract(leadingContract)) {
        return this.getBucketColor(party, BUCKET_FULL);
      }

      return this.getPaletteColor(party, leadingContract.lastTradePrice);
    },
    getEmptyColor() {
      // A map's configured EmptyColor is a light-theme value; dark mode uses its own neutral.
      if (this.isDarkTheme) {
        return DARK_EMPTY_COLOR;
      }
      const itemWithEmptyColor = (this.mapData || []).find((item) => !!item.emptyColor);
      return itemWithEmptyColor ? itemWithEmptyColor.emptyColor : DEFAULT_EMPTY_COLOR;
    },
    groupLocationsByStateCode() {
      return (this.mapData || []).reduce((acc, item) => {
        const stateCode = this.normalizeLocationCode(item.locationCode);
        if (!stateCode) {
          return acc;
        }

        if (!acc[stateCode]) {
          acc[stateCode] = [];
        }
        acc[stateCode].push(item);
        return acc;
      }, {});
    },
    getPredefinedGroupForState(stateCode) {
      return (
        this.predefinedGroups.find((group) => {
          const codes = Array.isArray(group.locationCodes) ? group.locationCodes : [];
          return codes.map((code) => this.normalizeLocationCode(code)).includes(stateCode);
        }) || null
      );
    },
    getPredefinedGroupColor(group) {
      if (!group) {
        return this.getEmptyColor();
      }
      if (group.color) {
        return group.color;
      }

      const party = (group.party || '').toLowerCase().includes('rep')
        ? PARTY_REPUBLICAN
        : PARTY_DEMOCRATIC;
      return this.getPaletteColor(party, 1);
    },
    getStateName(stateCode, fallbackName) {
      const district = DISTRICT_BOXES.find((box) => box.code === stateCode);
      if (district) {
        return district.name;
      }
      return constants.statesCodeToNameMap[stateCode] || fallbackName || stateCode;
    },
    getLocationLabel(stateCode) {
      const district = DISTRICT_BOXES.find((box) => box.code === stateCode);
      return district ? district.label : stateCode;
    },
    getStateValue(stateCode, locationData) {
      if (locationData && locationData.stateValue) {
        return Number(locationData.stateValue) || 1;
      }

      if (this.isSenateMap) {
        return 1;
      }

      return constants.statesCodeToElectoralVotesMap[stateCode] || 1;
    },
    getTooltipHtml(point) {
      if (this.isElectionMap) {
        // On district maps, the Maine/Nebraska cards list their districts above the footer.
        const html = this.getElectionTooltipHtml(point);
        const districts = this.getDistrictSectionHtml(point.stateCode);
        if (!districts) {
          return html;
        }
        const footerAt = html.indexOf('<div class="cms-map-widget__tooltip-footer');
        const at = footerAt >= 0 ? footerAt : html.lastIndexOf('</div>');
        return `${html.slice(0, at)}${districts}${html.slice(at)}`;
      }

      const marketData = point.marketData;
      if (!marketData) {
        return `<div class="cms-map-widget__tooltip-empty">${escapeTooltipHtml(point.stateName)}</div>`;
      }
      if (this.isCollectionMap) {
        return this.getCollectionTooltipHtml(point);
      }

      const entries = this.getTooltipContractEntries(marketData).slice(
        0,
        GENERIC_TOOLTIP_ROW_LIMIT,
      );
      // Generic maps have no party, so the card takes the colour the state is filled with;
      // each row's dot uses the contract's own colour (as in the legend), falling back to it.
      const stateColor = getSolidTooltipColor(point.color);
      const leader = getLeadingTradedContract(entries.map((entry) => entry.contract));
      // Other rows fade only once the market is settled (a winner); the leader's row tint marks it.
      const hasWinner = entries.some((entry) => this.isCalledContract(entry.contract));
      const rowsHtml = entries
        .map((entry) => {
          const contract = entry.contract;
          const isLeader = !!leader && contract === leader;
          const price = hasLastTradePrice(contract)
            ? this.formatPrice(contract.lastTradePrice)
            : '–';
          const rowClass = isLeader ? ' cms-map-widget__tooltip-generic-row--leader' : '';
          const priceClass =
            hasWinner && !this.isCalledContract(contract)
              ? ' cms-map-widget__tooltip-generic-price--secondary'
              : '';
          const dotColor =
            contract && contract.color ? getSolidTooltipColor(contract.color) : stateColor;
          const name = escapeTooltipHtml(contract.contractName || contract.name || 'Contract');
          return `<div class="cms-map-widget__tooltip-generic-row${rowClass}"${this.getTooltipMarketAttribute(entry.marketId, contract && contract.contractId)}><span class="cms-map-widget__tooltip-generic-dot" style="background:${dotColor}"></span><span class="cms-map-widget__tooltip-generic-name">${name}</span><span class="cms-map-widget__tooltip-generic-data"><span class="cms-map-widget__tooltip-inline-price cms-map-widget__tooltip-generic-price${priceClass}">${price}</span></span>${TOOLTIP_CHEVRON_HTML}</div>`;
        })
        .join('');
      const marketName = escapeTooltipHtml(this.getLeadingMarketName(marketData, point.stateName));
      const stateName = escapeTooltipHtml(this.getStateName(point.stateCode, point.stateName));
      const footerHtml = this.getTooltipFooterHtml(
        this.getTooltipContractCountText(marketData, entries.length),
        this.getPointMarketId(point),
      );

      return `<div class="cms-map-widget__tooltip cms-map-widget__tooltip--generic"><div class="cms-map-widget__tooltip-keyline" style="background:${stateColor}"></div><div class="cms-map-widget__tooltip-header"><div class="cms-map-widget__tooltip-title">${stateName}</div><div class="cms-map-widget__tooltip-question">${marketName}</div></div><div class="cms-map-widget__tooltip-generic-rows">${rowsHtml}</div>${footerHtml}</div>`;
    },
    // Collection maps: a location's markets in their configured order (grouped by market: chosen
    // contracts come back as one linked market each), with the contracts the card lists - the chosen
    // ones in order, or a market's top 1 or 2 when none were chosen.
    getCollectionMarkets(marketData) {
      if (!marketData) {
        return [];
      }

      const linkedMarkets =
        Array.isArray(marketData.markets) && marketData.markets.length > 0
          ? marketData.markets
          : [marketData];
      const groups = [];
      linkedMarkets.forEach((market, index) => {
        const key =
          Number(market.marketId) > 0 ? `market-${market.marketId}` : `market-index-${index}`;
        let group = groups.find((item) => item.key === key);
        if (!group) {
          group = {
            key,
            marketId: market.marketId || null,
            name: (market.marketName || market.name || '').trim(),
            isSingleOutcome: this.isMarketSingleOutcome(market),
            contractsShown: Number(market.contractsShown) || 1,
            status: market.status || '',
            linkedCount: 0,
            contracts: [],
          };
          groups.push(group);
        }
        group.linkedCount += 1;
        group.contracts = group.contracts.concat(
          Array.isArray(market.contracts) ? market.contracts : [],
        );
      });

      return groups
        .map((group) => {
          // One linked market with several contracts is a market with none chosen (the API's top 2).
          const isAutomatic = group.linkedCount === 1 && group.contracts.length > 1;
          const limit = isAutomatic
            ? Math.min(Math.max(group.contractsShown, 1), COLLECTION_AUTOMATIC_LIMIT)
            : COLLECTION_PICKED_LIMIT;
          return { ...group, contracts: group.contracts.slice(0, limit) };
        })
        .filter((group) => group.contracts.length > 0);
    },
    // A market listing one contract is one compact row (its question, the contract under it, the
    // price; a yes/no market's question is the row); 2-5 contracts get the question as a heading over
    // their rows. The markets that fit the map's height (and 8 rows) show; the footer counts the rest.
    getCollectionTooltipHtml(point) {
      const markets = this.getCollectionMarkets(point.marketData);
      const chart = point.series && point.series.chart;
      const available =
        chart && chart.plotHeight > 0
          ? chart.plotHeight - TOOLTIP_EDGE_PADDING_TOP - TOOLTIP_EDGE_PADDING_BOTTOM
          : Infinity;
      const questionPx = (market) =>
        COLLECTION_QUESTION_LINE_PX *
        Math.min(2, Math.max(1, Math.ceil(market.name.length / COLLECTION_QUESTION_LINE_CHARS)));
      const marketPx = (market, count) =>
        count === 1
          ? COLLECTION_ROW_PADDING_PX +
            questionPx(market) +
            (market.isSingleOutcome ? 0 : COLLECTION_CONTRACT_LINE_PX)
          : COLLECTION_HEADING_PADDING_PX +
            questionPx(market) +
            count * (COLLECTION_ROW_PADDING_PX + COLLECTION_CONTRACT_LINE_PX);

      // In order: the first market always shows (its rows trimmed to fit); each later one shows whole
      // or, like every market after it, goes to the footer, whose line then needs room too.
      const shown = [];
      let rows = 0;
      let height = COLLECTION_CARD_FRAME_PX;
      for (let index = 0; index < markets.length && index < COLLECTION_MARKET_LIMIT; index += 1) {
        const market = markets[index];
        const footerPx = index + 1 < markets.length ? COLLECTION_FOOTER_PX : 0;
        let count = Math.min(market.contracts.length, COLLECTION_ROW_LIMIT - rows);
        if (index === 0) {
          while (count > 1 && height + marketPx(market, count) + footerPx > available) {
            count -= 1;
          }
        } else if (
          count < market.contracts.length ||
          height + marketPx(market, count) + footerPx > available
        ) {
          break;
        }
        shown.push({ ...market, contracts: market.contracts.slice(0, count) });
        rows += count;
        height += marketPx(market, count);
      }

      const stateColor = getSolidTooltipColor(point.color);
      const marketsHtml = shown
        .map((market) => {
          const hasWinner = market.contracts.some((contract) => this.isCalledContract(contract));
          const rowHtml = (contract, label, isCompact) => {
            const price = hasLastTradePrice(contract)
              ? this.formatPrice(contract.lastTradePrice)
              : '–';
            const priceClass =
              hasWinner && !this.isCalledContract(contract)
                ? ' cms-map-widget__tooltip-generic-price--secondary'
                : '';
            const dotColor =
              contract && contract.color ? getSolidTooltipColor(contract.color) : stateColor;
            const name = escapeTooltipHtml(contract.contractName || contract.name || 'Contract');
            const text = isCompact
              ? `<span class="cms-map-widget__tooltip-collection-text"><span class="cms-map-widget__tooltip-collection-question${market.isSingleOutcome ? ' cms-map-widget__tooltip-collection-question--main' : ''}">${escapeTooltipHtml(label)}</span>${market.isSingleOutcome ? '' : `<span class="cms-map-widget__tooltip-generic-name">${name}</span>`}</span>`
              : `<span class="cms-map-widget__tooltip-generic-name">${name}</span>`;
            return `<div class="cms-map-widget__tooltip-generic-row cms-map-widget__tooltip-collection-row${isCompact ? ' cms-map-widget__tooltip-collection-row--compact' : ''}"${this.getTooltipMarketAttribute(market.marketId, contract && contract.contractId)}><span class="cms-map-widget__tooltip-generic-dot" style="background:${dotColor}"></span>${text}<span class="cms-map-widget__tooltip-generic-data"><span class="cms-map-widget__tooltip-inline-price cms-map-widget__tooltip-generic-price${priceClass}">${price}</span></span>${TOOLTIP_CHEVRON_HTML}</div>`;
          };
          if (market.contracts.length === 1) {
            return rowHtml(market.contracts[0], market.name, true);
          }
          return `<div class="cms-map-widget__tooltip-collection-market"><div class="cms-map-widget__tooltip-collection-heading">${escapeTooltipHtml(market.name)}</div>${market.contracts.map((contract) => rowHtml(contract, '', false)).join('')}</div>`;
        })
        .join('');
      const stateName = escapeTooltipHtml(this.getStateName(point.stateCode, point.stateName));
      const footerHtml =
        shown.length < markets.length
          ? this.getTooltipFooterHtml(`${shown.length} of ${markets.length} markets`, null)
          : '';

      return `<div class="cms-map-widget__tooltip cms-map-widget__tooltip--generic cms-map-widget__tooltip--collection"><div class="cms-map-widget__tooltip-keyline" style="background:${stateColor}"></div><div class="cms-map-widget__tooltip-header"><div class="cms-map-widget__tooltip-title-row"><span class="cms-map-widget__tooltip-title">${stateName}</span><span class="cms-map-widget__tooltip-context">${this.getCollectionCountText(markets.length)}</span></div></div><div class="cms-map-widget__tooltip-generic-rows cms-map-widget__tooltip-collection">${marketsHtml}</div>${footerHtml}</div>`;
    },
    getCollectionCountText(count) {
      return `${count} market${count === 1 ? '' : 's'}`;
    },
    getTooltipMarketAttribute(marketId, contractId) {
      const normalized = Number(marketId);
      if (!(normalized > 0)) {
        return '';
      }

      // Rows also carry their contract so the market page opens with that contract selected.
      const contract = Number(contractId);
      const contractAttribute =
        contract > 0 ? ` ${TOOLTIP_CONTRACT_ID_ATTRIBUTE}="${contract}"` : '';
      return ` ${TOOLTIP_MARKET_ID_ATTRIBUTE}="${normalized}"${contractAttribute}`;
    },
    getMarketDetailUrl(marketId, contractId) {
      const contract = Number(contractId);
      return `/markets/detail/${marketId}${contract > 0 ? `?contractId=${contract}` : ''}`;
    },
    getTooltipHeaderContext(point) {
      const called = this.isCalledContract(this.getLeadingContract(point.marketData))
        ? ' · Called'
        : '';
      if (this.isSenateMap) {
        return `${this.raceLabel.toUpperCase()}${called}`;
      }

      const stateCode = point.stateCode || '';
      const statewide = stateCode === 'ME' || stateCode === 'NE';
      return `${point.stateValue || 0} EV${statewide ? ' · Statewide' : ''}${called}`;
    },
    getTooltipQuestion(marketData, point) {
      return this.getLeadingMarketName(marketData, point.stateName);
    },
    getTooltipRingColor(contract) {
      const accent = this.getPartyAccentColor(contract);
      if (accent) {
        return accent;
      }

      const contractColor = (contract && contract.color) || '';
      return SAFE_TOOLTIP_COLOR.test(contractColor) ? contractColor : NEUTRAL_RING_COLOR;
    },
    getTooltipAvatarHtml(contract, fallbackName) {
      const ringStyle = `box-shadow:0 0 0 2px var(--cmw-surface),0 0 0 3.5px ${this.getTooltipRingColor(contract)}`;
      const imageUrl = getSafeTooltipImageUrl(contract && contract.contractImageUrl);
      if (imageUrl) {
        return `<img class="cms-map-widget__tooltip-avatar" src="${escapeTooltipHtml(imageUrl)}" alt="" style="${ringStyle}">`;
      }

      const initialsSource =
        (contract && (contract.contractName || contract.name)) || fallbackName || '';
      const initials = initialsSource
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((word) => word.charAt(0))
        .join('')
        .toUpperCase();
      return `<span class="cms-map-widget__tooltip-avatar cms-map-widget__tooltip-avatar--initials" style="${ringStyle}">${escapeTooltipHtml(initials)}</span>`;
    },
    getTooltipPriceHtml(contract, isPrimary) {
      const hasPrice = hasLastTradePrice(contract);
      const classes = ['cms-map-widget__tooltip-price'];
      if (!isPrimary) {
        classes.push('cms-map-widget__tooltip-price--secondary');
      }
      if (!hasPrice) {
        classes.push('cms-map-widget__tooltip-price--empty');
      }
      const price = hasPrice ? this.formatPrice(contract.lastTradePrice) : '–';
      return `<span class="${classes.join(' ')}">${price}</span>`;
    },
    getTooltipChangeHtml(contract) {
      if (!hasLastTradePrice(contract) || this.isCalledContract(contract)) {
        return '';
      }

      const change = this.getChangePercent(contract);
      if (change === null) {
        return '';
      }
      if (change === 0) {
        return '<span class="cms-map-widget__tooltip-change cms-map-widget__tooltip-change--flat">N/C</span>';
      }

      const direction = change > 0 ? 'up' : 'down';
      return `<span class="cms-map-widget__tooltip-change cms-map-widget__tooltip-change--${direction}">${getTooltipChangeArrowSvg(direction)}${this.formatPriceChange(change)}</span>`;
    },
    getTooltipLeaderPillHtml(contract) {
      const colors = getLeaderPillColors(this.getTooltipRingColor(contract));
      const style = colors ? ` style="background:${colors.background};color:${colors.color}"` : '';
      const label = this.isCalledContract(contract) ? 'Winner' : 'Leader';
      return `<span class="cms-map-widget__tooltip-pill cms-map-widget__tooltip-leader"${style}>${label}</span>`;
    },
    getTooltipContractRowHtml(entry, options) {
      const contract = entry.contract;
      const rowClasses = ['cms-map-widget__tooltip-row'];
      if (options.isLeader) {
        rowClasses.push('cms-map-widget__tooltip-row--leader');
      }
      const pillHtml = options.showPill ? this.getTooltipLeaderPillHtml(contract) : '';
      const label =
        options.label || (contract && (contract.contractName || contract.name)) || 'Contract';
      const name = escapeTooltipHtml(label);
      const avatarContract = options.label
        ? { ...contract, contractName: options.label }
        : contract;
      return `<div class="${rowClasses.join(' ')}"${this.getTooltipMarketAttribute(entry.marketId, contract && contract.contractId)}>${this.getTooltipAvatarHtml(avatarContract)}<span class="cms-map-widget__tooltip-row-text"><span class="cms-map-widget__tooltip-contract-name">${name}</span>${pillHtml}</span><span class="cms-map-widget__tooltip-row-data">${this.getTooltipPriceHtml(contract, !options.isMuted)}${this.getTooltipChangeHtml(contract)}</span>${TOOLTIP_CHEVRON_HTML}</div>`;
    },
    getTooltipFooterHtml(noteText, marketId) {
      const marketAttribute = this.getTooltipMarketAttribute(marketId);
      const linkHtml = marketAttribute
        ? `<span class="cms-map-widget__tooltip-link"${marketAttribute}>Open market<span class="cms-map-widget__tooltip-link-arrow">&nbsp;&rarr;</span></span>`
        : '';
      return `<div class="cms-map-widget__tooltip-footer"><span class="cms-map-widget__tooltip-footer-note">${escapeTooltipHtml(noteText)}</span>${linkHtml}</div>`;
    },
    getTooltipMarketCountText(totalCount, shownCount) {
      return shownCount < totalCount
        ? `${shownCount} of ${totalCount} markets`
        : `${totalCount} markets`;
    },
    getTooltipContractCountText(marketData, shownCount) {
      const selection = this.getLeadingMarketSelection(marketData);
      const selectedMarket = selection.market || marketData || {};
      const total = Number(selectedMarket.totalContractCount) || 0;
      if (total > shownCount) {
        return `${shownCount} of ${total} contracts`;
      }

      return `${shownCount} contract${shownCount === 1 ? '' : 's'}`;
    },
    // Centers each middle count ("2 Toss-up", "1 Independent") over its slice of the bar, moved
    // only as far as needed to keep clear of the party labels and of each other.
    positionToolbarMiddleLabels() {
      const row = this.$refs.toolbarLabels;
      const labels = [].concat(this.$refs.toolbarMiddleLabel || []).filter(Boolean);
      if (!row || labels.length === 0) {
        return;
      }

      const rowRect = row.getBoundingClientRect();
      const edge = (selector, side) => {
        const element = row.querySelector(selector);
        return element ? element.getBoundingClientRect()[side] - rowRect.left : null;
      };
      const demRight = edge('.cms-map-widget__toolbar-label--democratic', 'right');
      const repLeft = edge('.cms-map-widget__toolbar-label--republican', 'left');
      const widths = labels.map((label) => label.offsetWidth);
      const centers = labels.map((label) => (Number(label.dataset.center) / 100) * rowRect.width);
      const groupWidth =
        widths.reduce((total, width) => total + width, 0) +
        TOOLBAR_MIDDLE_LABEL_GAP * (widths.length - 1);
      let minLeft = demRight === null ? 0 : demRight + TOOLBAR_MIDDLE_LABEL_GAP;
      let maxRight = repLeft === null ? rowRect.width : repLeft - TOOLBAR_MIDDLE_LABEL_GAP;
      // No room between the party labels (narrow widgets): the counts take their own line above
      // the bar, where they can sit anywhere along it.
      if (groupWidth > maxRight - minLeft) {
        row.dataset.stacked = 'true';
        minLeft = 0;
        maxRight = rowRect.width;
      } else {
        delete row.dataset.stacked;
      }
      // Each at its slice's center, pushed right of the one before it where they would touch;
      // then the group is moved back so it stays centered on the slices, and kept inside the room
      // between the party labels.
      const lefts = [];
      centers.forEach((center, index) => {
        const previousRight =
          index > 0 ? lefts[index - 1] + widths[index - 1] + TOOLBAR_MIDDLE_LABEL_GAP : -Infinity;
        lefts.push(Math.max(center - widths[index] / 2, previousRight));
      });
      const average = (values) => values.reduce((total, value) => total + value, 0) / values.length;
      let shift = average(centers) - average(lefts.map((left, index) => left + widths[index] / 2));
      const groupRight = lefts[lefts.length - 1] + widths[widths.length - 1];
      shift = Math.min(shift, maxRight - groupRight);
      shift = Math.max(shift, minLeft - lefts[0]);
      labels.forEach((label, index) => {
        label.style.left = `${Math.round(lefts[index] + shift)}px`;
        label.style.transform = 'none';
      });
    },
    getToolbarHint(counter) {
      return this.getToolbarHintLines(counter).flat().join(' · ');
    },
    // The hint's parts in two groups: settled seats (no election, safe), then leaning ones (likely,
    // lean). The seat bar runs them together; the seat arc gives each group its own line.
    getToolbarHintLines(counter) {
      const settled = [];
      const leaning = [];
      if (counter.predefined > 0) {
        settled.push(
          `${counter.predefined} ${(counter.predefinedLabel || 'No election').toLowerCase()}`,
        );
      }
      if (counter.safe > 0) {
        settled.push(`${counter.safe} safe`);
      }
      if (counter.likely > 0) {
        leaning.push(`${counter.likely} likely`);
      }
      // "Lean" covers both lean tiers (lean and slight lean).
      const lean = (counter.medium || 0) + (counter.low || 0);
      if (lean > 0) {
        leaning.push(`${lean} lean`);
      }
      return [settled, leaning].filter((parts) => parts.length > 0);
    },
    getToolbarSegmentTitle(segment) {
      return segment.label ? `${segment.label} ${segment.displayText}` : segment.displayText;
    },
    getOverviewContract(item) {
      let configuredContract = null;
      (this.mapData || []).some((location) => {
        configuredContract = this.getContractsForLocation(location).find(
          (contract) => Number(contract.contractId) === Number(item.contractId),
        );
        return !!configuredContract;
      });

      return configuredContract || item;
    },
    getMapBorderColor() {
      return this.isDarkTheme ? DARK_MAP_BORDER_COLOR : MAP_BORDER_COLOR;
    },
    // The fill as it shows on the map: generic maps' price shades are translucent, so they are
    // blended onto the map's background (white, or the dark theme's surface).
    getVisibleFillColor(color) {
      const match = typeof color === 'string' ? color.trim().match(RGBA_COLOR) : null;
      if (!match || !this.isDarkTheme) {
        return getSolidTooltipColor(color);
      }

      const hex = `#${[1, 2, 3].map((index) => `0${Math.min(255, Number(match[index])).toString(16)}`.slice(-2)).join('')}`;
      const alpha = match[4] === undefined ? 1 : Math.min(1, Math.max(0, Number(match[4])));
      return mixHexColors(hex, DARK_EXPORT_BACKGROUND, alpha);
    },
    // Pointer entered a state: with no tooltip open it darkens straight away; while a tooltip is
    // open only that tooltip's state stays darkened (it changes when the tooltip does).
    onMapPointHover(point) {
      const tooltip = this.chart && this.chart.tooltip;
      if (!this.activeMapPoint || !tooltip || tooltip.isHidden) {
        this.setActiveMapPoint(point);
      }
    },
    // The state whose tooltip is shown gets the hover styling, and keeps it until its tooltip
    // closes or another state's tooltip opens.
    setActiveMapPoint(point) {
      const next = point || null;
      if (this.activeMapPoint === next) {
        return;
      }
      if (this.activeMapPoint) {
        this.setMapPointDarken(this.activeMapPoint, false);
      }
      this.activeMapPoint = next;
      if (next) {
        this.setMapPointDarken(next, true);
      }
    },
    // Darken: the state's fill eases about 10% darker (no border change).
    setMapPointDarken(point, isActive) {
      const element = point.graphic && point.graphic.element;
      if (!element) {
        return;
      }

      element.style.transition = `fill ${MAP_HOVER_MS}ms ease-out`;
      element.style.fill = isActive
        ? mixHexColors(getSolidTooltipColor(point.color), '#000000', 1 - MAP_HOVER_DARKEN)
        : '';
    },
    applyNoMarketStyle(point, group, emptyColor) {
      const theme = this.isDarkTheme ? 'dark' : 'light';
      if (group.isDecided) {
        const party =
          this.getGroupParty(group) === PARTY_REPUBLICAN ? PARTY_REPUBLICAN : PARTY_DEMOCRATIC;
        point.color = mixHexColors(
          this.getPredefinedGroupColor(group),
          this.isDarkTheme ? DARK_MAP_BORDER_COLOR : '#ffffff',
          SAFE_TINT_WEIGHT,
        );
        point.dataLabels = { style: { color: SAFE_LABEL_COLORS[theme][party] } };
        return;
      }

      point.color = emptyColor;
      point.dataLabels = {
        style: { color: this.isDarkTheme ? DARK_EMPTY_STATE_LABEL_COLOR : EMPTY_STATE_LABEL_COLOR },
      };
    },
    toggleSummaryInfo() {
      this.showSummaryInfo = !this.showSummaryInfo;
    },
    toggleTile(tile) {
      this.activeTileCode = this.activeTileCode === tile.stateCode ? '' : tile.stateCode;
    },
    // Compact preview: as tall as the room left above the preview's Read More cutoff, measured
    // from where the widget starts in the story (a map under a headline gets less).
    fitCompactHeight() {
      if (!this.compactLayout || !this.$el || typeof this.$el.closest !== 'function') {
        return;
      }

      const preview = this.$el.closest(PREVIEW_CONTENT_SELECTOR);
      let height = COMPACT_FALLBACK_HEIGHT;
      if (preview) {
        const top = this.$el.getBoundingClientRect().top - preview.getBoundingClientRect().top;
        height =
          PREVIEW_CUTOFF_HEIGHT -
          PREVIEW_CUTOFF_MARGIN -
          Math.max(0, top) -
          this.getSpaceBelowMap();
      }
      height = Math.floor(height);
      let minHeight = COMPACT_MIN_HEIGHTS[this.compactLayout] || 0;
      // Stacked tiles: as tall as it takes for the tiles to fill the card's width when the map
      // can't fit above the cutoff anyway (it then shows a Read More). With room to spare, the
      // list takes the rows that fit and the tiles grow taller into the rest, up to their cap.
      const tilesHeight =
        this.compactLayout === COMPACT_LAYOUT_STACK ? this.getStackTilesHeight() : 0;
      let listRows = 0;
      let stretch = false;
      if (tilesHeight > 0) {
        minHeight = Math.min(minHeight, tilesHeight);
        if (height < minHeight) {
          height = tilesHeight;
        } else if (height > tilesHeight) {
          const rowsThatFit = Math.floor(
            (height - tilesHeight - COMPACT_LIST_BASE_HEIGHT) / COMPACT_LIST_ROW_HEIGHT,
          );
          listRows = Math.max(0, Math.min(rowsThatFit, this.compactListItems.length));
          const listHeight =
            listRows > 0 ? COMPACT_LIST_BASE_HEIGHT + listRows * COMPACT_LIST_ROW_HEIGHT : 0;
          height = Math.min(
            height,
            this.getStackTilesHeight(COMPACT_TILE_STRETCH_MAX) + listHeight,
          );
          stretch = height > tilesHeight + listHeight;
        }
      }
      this.compactListRows = listRows;
      this.compactTileStretch = stretch;
      height = Math.max(minHeight, height);
      if (height !== this.compactHeight) {
        this.compactHeight = height;
        this.$nextTick(() => this.reflowChart());
        this.fitCompactSummary();
      }
    },
    async fitCompactSummary() {
      // A newer fit (the height or layout changed again) takes over from this one.
      const run = (this.compactSummaryRun || 0) + 1;
      this.compactSummaryRun = run;
      this.compactTight = 0;
      if (this.compactLayout !== COMPACT_LAYOUT_SIDE) {
        return;
      }
      await this.$nextTick();
      while (
        run === this.compactSummaryRun &&
        this.compactTight < COMPACT_TIGHT_MAX &&
        this.isCompactSummaryOverflowing()
      ) {
        this.compactTight += 1;
        await this.$nextTick();
      }
    },
    isCompactSummaryOverflowing() {
      const summary = this.$el && this.$el.querySelector('.cms-map-widget__summary');
      return !!summary && summary.scrollHeight > summary.clientHeight + 1;
    },
    // The stacked widget's height when its tiles fill the card's width: everything but the tiles
    // and the list, plus the tile grid at full width with tiles `stretch` times as tall as wide.
    getStackTilesHeight(stretch = 1) {
      const tiles = this.$el && this.$el.querySelector('.cms-map-widget__tiles');
      // Not while hidden (another carousel story is showing): nothing to measure yet.
      if (!this.isTileMap || !tiles || this.mapTiles.length === 0 || tiles.clientWidth === 0) {
        return 0;
      }
      const style = window.getComputedStyle(tiles);
      const grid = tiles.querySelector('.cms-map-widget__tile-grid');
      const gap = grid ? parseFloat(window.getComputedStyle(grid).rowGap) || 0 : 0;
      const paddingX = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
      const paddingY = (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
      const rows = Math.max(...this.mapTiles.map((tile) => tile.row)) + 1;
      const tileSize =
        (tiles.clientWidth - paddingX - gap * (TILE_MAP_COLUMNS - 1)) / TILE_MAP_COLUMNS;
      const gridHeight = rows * tileSize * stretch + gap * (rows - 1);
      const list = this.$refs.compactList;
      const listHeight = list ? list.offsetHeight : 0;
      return Math.ceil(
        this.$el.offsetHeight - tiles.offsetHeight - listHeight + paddingY + gridHeight,
      );
    },
    // The story's own spacing under the map (its region's padding, up to the story row), which
    // the preview counts toward the cutoff too. Not the row's height: a taller column beside the
    // map would make the map shrink without end.
    getSpaceBelowMap() {
      let space = 0;
      for (let node = this.$el.parentElement; node; node = node.parentElement) {
        const style = window.getComputedStyle(node);
        space +=
          (parseFloat(style.paddingBottom) || 0) + (parseFloat(style.borderBottomWidth) || 0);
        if (node.matches(PREVIEW_STORY_ROW_SELECTOR) || node.matches(PREVIEW_STORY_SELECTOR)) {
          break;
        }
        space += parseFloat(style.marginBottom) || 0;
      }
      return Math.max(0, Math.ceil(space));
    },
    // Content above the map (images, other widgets) can load after it and push it down.
    syncCompactStoryObserver() {
      if (this.compactStoryObserver) {
        this.compactStoryObserver.disconnect();
        this.compactStoryObserver = null;
      }
      const story =
        this.compactLayout && this.$el && typeof this.$el.closest === 'function'
          ? this.$el.closest(PREVIEW_STORY_SELECTOR)
          : null;
      if (story && typeof ResizeObserver !== 'undefined') {
        this.compactStoryObserver = new ResizeObserver(() => this.fitCompactHeight());
        this.compactStoryObserver.observe(story);
      }
    },
    reflowChart() {
      if (this.chart && typeof this.chart.reflow === 'function') {
        this.chart.reflow();
      }
    },
    getTilePrice(point) {
      if (this.isCollectionMap) {
        const count = this.getCollectionMarkets(point.marketData).length;
        return count > 0 ? `${count}` : '';
      }
      const leader = this.getLeadingContract(point.marketData);
      if (!leader) {
        return '';
      }
      if (this.isCalledContract(leader)) {
        return '\u2713';
      }

      return hasLastTradePrice(leader) ? this.formatPrice(leader.lastTradePrice) : '';
    },
    getTileStyle(tile) {
      return {
        gridRow: tile.row + 1,
        gridColumn: tile.column + 1,
        backgroundColor: tile.color,
        color: tile.textColor,
      };
    },
    getTileAriaLabel(tile) {
      const name = this.getStateName(tile.stateCode, tile.stateName);
      if (this.isCollectionMap) {
        const count = this.getCollectionMarkets(tile.marketData).length;
        return count > 0 ? `${name}, ${this.getCollectionCountText(count)}` : name;
      }
      const leader = this.getLeadingContract(tile.marketData);
      if (leader && hasLastTradePrice(leader)) {
        return `${name}, ${leader.contractName || leader.shortName || ''} ${this.formatPrice(leader.lastTradePrice)}`;
      }

      return name;
    },
    // Tooltip rows and "Open market" carry the market (and contract) to open, for the map's
    // tooltip and the tile map's card alike.
    openTooltipMarket(event) {
      const target =
        event.target && typeof event.target.closest === 'function'
          ? event.target.closest(`[${TOOLTIP_MARKET_ID_ATTRIBUTE}]`)
          : null;
      if (!target) {
        return;
      }

      const marketId = Number(target.getAttribute(TOOLTIP_MARKET_ID_ATTRIBUTE));
      if (marketId > 0) {
        event.stopPropagation();
        window.location.href = this.getMarketDetailUrl(
          marketId,
          target.getAttribute(TOOLTIP_CONTRACT_ID_ATTRIBUTE),
        );
      }
    },
    syncSummaryInfoOutsideClose(isOpen) {
      if (typeof document === 'undefined') {
        return;
      }

      if (isOpen && !this.summaryInfoOutsideCloseHandler) {
        this.summaryInfoOutsideCloseHandler = (event) => {
          const info = this.$refs.summaryInfo;
          if (info && !info.contains(event.target)) {
            this.showSummaryInfo = false;
          }
        };
        document.addEventListener('pointerdown', this.summaryInfoOutsideCloseHandler);
      } else if (!isOpen && this.summaryInfoOutsideCloseHandler) {
        document.removeEventListener('pointerdown', this.summaryInfoOutsideCloseHandler);
        this.summaryInfoOutsideCloseHandler = null;
      }
    },
    syncTileOutsideClose(isOpen) {
      if (typeof document === 'undefined') {
        return;
      }

      if (isOpen && !this.tileOutsideCloseHandler) {
        this.tileOutsideCloseHandler = (event) => {
          const tiles = this.$refs.tileMap;
          if (tiles && !tiles.contains(event.target)) {
            this.activeTileCode = '';
          }
        };
        document.addEventListener('pointerdown', this.tileOutsideCloseHandler);
      } else if (!isOpen && this.tileOutsideCloseHandler) {
        document.removeEventListener('pointerdown', this.tileOutsideCloseHandler);
        this.tileOutsideCloseHandler = null;
      }
    },
    openExportMenu() {
      const exporting = this.chart && this.chart.exporting;
      if (!exporting || typeof exporting.contextMenu !== 'function') {
        return;
      }

      const menuItems = this.canShowDataTable
        ? MAP_WIDGET_CONTEXT_MENU_ITEMS
        : DEFAULT_CONTEXT_MENU_ITEMS;
      const size = EXPORT_MENU_ANCHOR_SIZE;
      // Highcharts positions the menu relative to a button object; the header button lives
      // outside the chart, so anchor a stub at the chart's top-right corner instead.
      const anchor = {
        alignOptions: { align: 'right', verticalAlign: 'top' },
        setState() {},
      };
      exporting.contextMenu(
        'highcharts-contextmenu',
        menuItems,
        this.chart.chartWidth - size,
        0,
        size,
        size,
        anchor,
      );
      this.syncFullscreenMenuText();
      this.alignContextMenuToExportButton();
    },
    alignContextMenuToExportButton() {
      const contextMenu = this.$el && this.$el.querySelector('.highcharts-contextmenu');
      const button = this.$el && this.$el.querySelector('.cms-map-widget__export');
      const container = this.chart && this.chart.container;
      if (!contextMenu || !button || !container || contextMenu.style.display === 'none') {
        return;
      }

      const buttonBounds = button.getBoundingClientRect();
      const containerBounds = container.getBoundingClientRect();
      // Highcharts wraps the visible list in a transparent padding box (used as a hover
      // buffer); align the list itself, not the box.
      const menuStyle = window.getComputedStyle(contextMenu);
      const paddingTop = parseFloat(menuStyle.paddingTop) || 0;
      const paddingRight = parseFloat(menuStyle.paddingRight) || 0;
      // The gap below the button matches the gap between the header's controls (%/¢ and ≡).
      const actions = button.parentNode;
      const gap =
        (actions && parseFloat(window.getComputedStyle(actions).columnGap)) ||
        EXPORT_MENU_BUTTON_GAP;
      // The menu is absolutely positioned inside the chart container; without an explicit
      // width it shrink-fits to the space left of the container edge and wraps its items.
      contextMenu.style.width = 'max-content';
      const top = buttonBounds.bottom - containerBounds.top + gap - paddingTop;
      contextMenu.style.top = `${Math.round(top)}px`;
      // Right-align to the button first, then freeze the resulting offset as `left` so the
      // viewport clamp in positionContextMenu() (which only writes top/left) keeps working.
      contextMenu.style.left = 'auto';
      contextMenu.style.right = `${Math.round(containerBounds.right - buttonBounds.right - paddingRight)}px`;
      const alignedLeft = contextMenu.offsetLeft;
      contextMenu.style.right = 'auto';
      contextMenu.style.left = `${Math.round(alignedLeft)}px`;
      this.syncContextMenuState();
    },
    getTooltipKeylineColor(marketData) {
      // Toss-ups (split states included) match the map; the rows keep their party colors.
      if (this.isTossUpLocation(marketData)) {
        return TOSS_UP_COLOR;
      }

      const accent = this.getPartyAccentColor(this.getLeadingContract(marketData));
      if (accent) {
        return accent;
      }

      return getSafeTooltipColor(this.resolveElectionColor(marketData));
    },
    getDecidedRaceTooltipHtml(point) {
      const group = point.predefinedGroup;
      const party = this.getGroupParty(group);
      const partyName = party === PARTY_REPUBLICAN ? 'Republican' : 'Democratic';
      const stateName = escapeTooltipHtml(
        this.getStateName(point.stateCode, point.stateName || point.stateCode || ''),
      );
      const context = escapeTooltipHtml(
        this.isSenateMap ? this.raceLabel.toUpperCase() : `${point.stateValue || 0} EV`,
      );
      const title = escapeTooltipHtml(group.label || `Safe ${partyName}`);
      const keyline = PARTY_ACCENT_COLORS[party] || NEUTRAL_KEYLINE_COLOR;
      return `<div class="cms-map-widget__tooltip cms-map-widget__tooltip--election cms-map-widget__tooltip--predefined"><div class="cms-map-widget__tooltip-keyline" style="background:${keyline}"></div><div class="cms-map-widget__tooltip-header"><div class="cms-map-widget__tooltip-title-row"><span class="cms-map-widget__tooltip-title">${stateName}</span><span class="cms-map-widget__tooltip-context">${context}</span></div></div><div class="cms-map-widget__tooltip-no-election"><div class="cms-map-widget__tooltip-no-election-title">${title}</div><div class="cms-map-widget__tooltip-no-election-text">No market for this race.</div></div></div>`;
    },
    getPredefinedElectionTooltipHtml(point) {
      const predefinedGroup = point && point.predefinedGroup;
      if (predefinedGroup && predefinedGroup.isDecided) {
        return this.getDecidedRaceTooltipHtml(point);
      }
      const label = (predefinedGroup && predefinedGroup.label) || '';
      const isNoElection =
        !!predefinedGroup &&
        (predefinedGroup.isNoElection || label.toLowerCase() === 'no election');
      // A state the map has no race for (no market and no group, e.g. DC on a Senate map) still
      // gets a card, so hovering or tapping it shows why there is nothing there.
      const noteText =
        isNoElection || predefinedGroup
          ? 'This seat is not up in this cycle.'
          : this.getNoRaceText();

      const stateName = escapeTooltipHtml(
        this.getStateName(point.stateCode, point.stateName || point.stateCode || ''),
      );
      const context = this.isSenateMap
        ? ''
        : `<span class="cms-map-widget__tooltip-context">${point.stateValue || 0} EV</span>`;
      const displayLabel = escapeTooltipHtml(label || 'No election');
      return `<div class="cms-map-widget__tooltip cms-map-widget__tooltip--election cms-map-widget__tooltip--predefined"><div class="cms-map-widget__tooltip-keyline" style="background:${NEUTRAL_KEYLINE_COLOR}"></div><div class="cms-map-widget__tooltip-header"><div class="cms-map-widget__tooltip-title-row"><span class="cms-map-widget__tooltip-title">${stateName}</span>${context}</div></div><div class="cms-map-widget__tooltip-no-election"><div class="cms-map-widget__tooltip-no-election-title">${displayLabel}</div><div class="cms-map-widget__tooltip-no-election-text">${escapeTooltipHtml(noteText)}</div></div></div>`;
    },
    getNoRaceText() {
      if (!this.isSenateMap) {
        return 'No market for this state.';
      }

      const race =
        this.raceLabel === DEFAULT_RACE_LABEL ? this.raceLabel : this.raceLabel.toLowerCase();
      return `No ${race} race here this cycle.`;
    },
    getSingleOutcomeTooltipHtml(point) {
      const marketData = point.marketData;
      const entries = this.getTooltipContractEntries(marketData).slice(0, 1);
      const contract = entries.length > 0 ? entries[0].contract : null;
      const question = this.getTooltipQuestion(marketData, point);
      const stateName = escapeTooltipHtml(this.getStateName(point.stateCode, point.stateName));
      const marketId = this.getPointMarketId(point);
      const rowHtml = `<div class="cms-map-widget__tooltip-row cms-map-widget__tooltip-row--leader cms-map-widget__tooltip-row--single"${this.getTooltipMarketAttribute(marketId, contract && contract.contractId)}>${this.getTooltipAvatarHtml(contract, question)}<span class="cms-map-widget__tooltip-row-text"><span class="cms-map-widget__tooltip-title">${stateName}</span><span class="cms-map-widget__tooltip-question">${escapeTooltipHtml(question)}</span></span><span class="cms-map-widget__tooltip-row-data">${this.getTooltipPriceHtml(contract, true)}${this.getTooltipChangeHtml(contract)}</span>${TOOLTIP_CHEVRON_HTML}</div>`;
      const footerHtml = this.getTooltipFooterHtml(this.getTooltipHeaderContext(point), marketId);

      return `<div class="cms-map-widget__tooltip cms-map-widget__tooltip--election cms-map-widget__tooltip--single-outcome"><div class="cms-map-widget__tooltip-keyline" style="background:${this.getTooltipKeylineColor(marketData)}"></div><div class="cms-map-widget__tooltip-rows cms-map-widget__tooltip-rows--single">${rowHtml}</div>${footerHtml}</div>`;
    },
    getElectionTooltipHtml(point) {
      const marketData = point.marketData;
      if (!marketData) {
        return this.getPredefinedElectionTooltipHtml(point);
      }

      if (this.isSupportedSingleOutcomeLocation(marketData)) {
        return this.getSingleOutcomeTooltipHtml(point);
      }

      const stateCode = point.stateCode || '';
      const showSplitStateNote = !this.isSenateMap && (stateCode === 'ME' || stateCode === 'NE');
      const stateName = escapeTooltipHtml(this.getStateName(stateCode, point.stateName));
      const leadingContract = this.getLeadingContract(marketData);
      const entries = this.getTooltipContractEntries(marketData).slice(0, 2);
      // Several single-outcome markets ("Will X win?") each have a lone "Yes" contract, so
      // list one row per market, labelled with the market's name. Separate markets are not
      // one race, so there is no LEADER pill; the highlighted row is the market the map's
      // colour and label come from.
      const isMarketList = this.hasMultipleSingleOutcomeMarkets(marketData);
      const marketCount = this.getDistinctLinkedMarketCount(marketData);
      const question = isMarketList
        ? `${marketCount} markets`
        : escapeTooltipHtml(this.getTooltipQuestion(marketData, point));
      const showPill = !this.isSingleOutcomeMarket(marketData);
      // Only a called race fades its other rows; otherwise the LEADER pill and row tint mark the
      // leader and every row reads the same.
      const isCalledRace = this.isCalledContract(leadingContract);
      const rowsHtml = entries
        .map((entry) =>
          this.getTooltipContractRowHtml(entry, {
            isLeader: entry.contract === leadingContract,
            isMuted: isCalledRace && entry.contract !== leadingContract,
            showPill: showPill && entry.contract === leadingContract,
            label: isMarketList ? entry.marketName : '',
          }),
        )
        .join('');
      const note = showSplitStateNote
        ? '<div class="cms-map-widget__tooltip-note">Congressional districts trade as separate markets.</div>'
        : '';
      const countText = isMarketList
        ? this.getTooltipMarketCountText(marketCount, entries.length)
        : this.getTooltipContractCountText(marketData, entries.length);
      // Each market-list row opens its own market; a footer link would pick one arbitrarily.
      const footerHtml = this.getTooltipFooterHtml(
        countText,
        isMarketList ? null : this.getPointMarketId(point),
      );

      return `<div class="cms-map-widget__tooltip cms-map-widget__tooltip--election"><div class="cms-map-widget__tooltip-keyline" style="background:${this.getTooltipKeylineColor(marketData)}"></div><div class="cms-map-widget__tooltip-header"><div class="cms-map-widget__tooltip-title-row"><span class="cms-map-widget__tooltip-title">${stateName}</span><span class="cms-map-widget__tooltip-context">${escapeTooltipHtml(this.getTooltipHeaderContext(point))}</span></div><div class="cms-map-widget__tooltip-question">${question}</div></div><div class="cms-map-widget__tooltip-rows">${rowsHtml}</div>${note}${footerHtml}</div>`;
    },
    getChangePercent(contract) {
      if (
        !contract ||
        contract.lastClosePrice === null ||
        typeof contract.lastClosePrice === 'undefined'
      ) {
        return null;
      }

      return this.toPercent(Number(contract.lastTradePrice) - Number(contract.lastClosePrice));
    },
    getPointMarketId(point) {
      const marketData = point.marketData;
      if (!marketData) {
        return null;
      }

      const selection = this.getLeadingMarketSelection(marketData);
      return (selection.market && selection.market.marketId) || marketData.marketId || null;
    },
    // Code over the leading contract's price, on election and generic maps alike.
    getDataLabel(point) {
      const stateCode = escapeTooltipHtml(this.getLocationLabel(point.stateCode || ''));
      const codeHtml = `<span class="cms-map-widget__map-label-code">${stateCode}</span>`;
      // A small state's own shape is labelled once its box is half faded out, with the code and
      // price, the code only, or nothing, depending on the room it has. (Labels pop in rather than
      // fade, so they never show before they fit inside the state.)
      if (point.shapeRoom) {
        const chart = point.series && point.series.chart;
        const room = point.shapeRoom * this.getMapPixelsPerUnit(chart);
        if (
          this.getZoomBoxFade(chart) < 0.5 ||
          point.stateCode === 'DC' ||
          room < SHAPE_LABEL_CODE_PX
        ) {
          return '';
        }
        if (room < SHAPE_LABEL_FULL_PX) {
          return codeHtml;
        }
      }
      if (this.isCompactBoxLabel(point)) {
        return codeHtml;
      }

      // Collection maps: the state's market count in the price's place.
      const leadingContract = this.isCollectionMap
        ? null
        : this.getLeadingContract(point.marketData);
      const marketCount = this.isCollectionMap
        ? this.getCollectionMarkets(point.marketData).length
        : 0;
      let priceHtml = '';
      if (marketCount > 0) {
        priceHtml = `<span class="cms-map-widget__map-label-price">${marketCount}</span>`;
      } else if (leadingContract) {
        const price = this.isCalledContract(leadingContract)
          ? CALLED_LABEL_MARK
          : this.formatPrice(leadingContract.lastTradePrice);
        priceHtml = `<span class="cms-map-widget__map-label-price">${price}</span>`;
      } else if (
        !this.isCollectionMap &&
        this.getContractsForLocation(point.marketData).length > 0
      ) {
        priceHtml = '<span class="cms-map-widget__map-label-price">&ndash;</span>';
      }

      const rowWidth = this.getRowTileLabelWidth(point);
      if (rowWidth > 0) {
        // Without a price, an empty (zero-width) price keeps the row as tall as a priced row's
        // and the code on the same baseline, so every box's code sits at the same height.
        const rowPriceHtml =
          priceHtml || '<span class="cms-map-widget__map-label-price">&#8203;</span>';
        return `<span class="cms-map-widget__map-label-row" style="width:${rowWidth}px">${codeHtml}${rowPriceHtml}</span>`;
      }
      return `${codeHtml}${priceHtml}`;
    },
    // Box labels span the box, less a small inset on each side.
    getRowTileLabelWidth(point) {
      const mapView = point.series && point.series.chart && point.series.chart.mapView;
      if (!point.boxRing || !mapView || typeof mapView.projectedUnitsToPixels !== 'function') {
        return 0;
      }

      const xs = point.boxRing.map((corner) => corner[0]);
      const y = point.boxRing[0][1];
      const left = mapView.projectedUnitsToPixels({ x: Math.min(...xs), y }).x;
      const right = mapView.projectedUnitsToPixels({ x: Math.max(...xs), y }).x;
      return Math.max(0, Math.round(Math.abs(right - left) - ROW_TILE_LABEL_INSET_PX * 2));
    },
    // On narrow maps the boxes are too small for a price, so they show the code only (the price
    // stays in the tooltip). Zooming in enlarges the boxes, which brings the price back.
    isCompactBoxLabel(point) {
      const chart = point.series && point.series.chart;
      if (!point.isBoxLabel || !chart) {
        return false;
      }
      const { mapView } = chart;
      const zoomGain =
        mapView && Number.isFinite(mapView.zoom) && Number.isFinite(mapView.minZoom)
          ? 2 ** Math.max(0, mapView.zoom - mapView.minZoom)
          : 1;
      if (chart.plotWidth * zoomGain < BOX_PRICE_MIN_PLOT_WIDTH) {
        return true;
      }
      return !!this.compactLayout && this.getRowTileLabelWidth(point) < BOX_PRICE_MIN_LABEL_ROOM;
    },
    getExportDataLabel(point) {
      // Exports render data labels as SVG text, which drops the HTML label's two-line layout
      // ("IA60%"), so exported labels use an SVG line break instead.
      // Exports always show the boxes, so a small state's own shape stays unlabelled.
      if (point.shapeRoom) {
        return '';
      }
      const stateCode = escapeTooltipHtml(this.getLocationLabel(point.stateCode || ''));
      const marketCount = this.isCollectionMap
        ? this.getCollectionMarkets(point.marketData).length
        : 0;
      const leadingContract = this.isCollectionMap
        ? null
        : this.getLeadingContract(point.marketData);
      if (!leadingContract && !marketCount) {
        return stateCode;
      }

      let price = `${marketCount}`;
      if (leadingContract) {
        price = this.isCalledContract(leadingContract)
          ? CALLED_LABEL_MARK
          : this.formatPrice(leadingContract.lastTradePrice);
      }
      const codeHtml = `<span style="font-family:${BODY_FONT_FAMILY};font-size:9px;font-weight:500">${stateCode}</span>`;
      const priceHtml = `<span style="font-weight:700">${price}</span>`;
      // Small-state and district boxes are one line tall.
      return point.isBoxLabel ? `${codeHtml}  ${priceHtml}` : `${codeHtml}<br>${priceHtml}`;
    },
    getMapDefinition(map) {
      const features = Array.isArray(map && map.features) ? map.features : [];
      const stackFeature = features.find(
        (feature) => feature.properties['hc-key'] === SMALL_STATE_STACK_KEY,
      );
      const stackBox = stackFeature && polygonRingsOf(stackFeature.geometry).find(isStackBox);
      const rowLayout = stackBox ? getRowTileLayout(stackBox) : null;

      const withAnchors = features.map((feature) => {
        const key = feature.properties && feature.properties['hc-key'];
        const rings = polygonRingsOf(feature.geometry);
        if (!key || rings.length === 0) {
          return feature;
        }

        let { geometry } = feature;
        if (rowLayout && rings.some(isStackBox)) {
          geometry = reshapeStackBox(geometry, rowLayout);
        }
        if (key === HAWAII_KEY && stackBox && !rings.some(isStackBox)) {
          const hawaiiBox = rowLayout
            ? rowLayout.box(SMALL_STATE_STACK_SLOTS_ABOVE_DC + 1)
            : stackBox.map(([x, y]) => [x, y - SMALL_STATE_STACK_PITCH]);
          const polygons =
            geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates;
          geometry = { type: 'MultiPolygon', coordinates: polygons.concat([[hawaiiBox]]) };
        }

        if (!labelAnchorCache[key]) {
          labelAnchorCache[key] = computeLabelAnchor(geometry);
        }
        const anchor = labelAnchorCache[key];
        if (!anchor) {
          return feature;
        }

        return {
          ...feature,
          geometry,
          properties: {
            ...feature.properties,
            'hc-middle-x': anchor.x,
            'hc-middle-y': anchor.y,
          },
        };
      });

      const coastX = this.isDistrictMap ? getCoastX(features) : null;
      const districtBoxes =
        this.isDistrictMap && stackBox && coastX !== null
          ? getDistrictBoxFeatures(
              rowLayout ? rowLayout.dcBox : stackBox,
              coastX,
              rowLayout ? rowLayout.pitch : SMALL_STATE_STACK_PITCH,
              rowLayout ? rowLayout.districtTopSlots : DISTRICT_TOP_SLOTS_ABOVE_STACK_BOX,
            )
          : [];
      return { ...map, features: withAnchors.concat(districtBoxes) };
    },
    // The map's features as two layers: the states with only their own shapes (small states
    // labelled at the pole of their largest shape), and the box layer drawn over them: the small
    // states, Hawaii and DC with their boxes, plus the district boxes.
    splitBoxFeatures(mapDefinition) {
      const main = [];
      const boxes = [];
      const districtKeys = DISTRICT_BOXES.map((box) => `us-${box.code.toLowerCase()}`);
      this.boxFadeRoom = 0;
      mapDefinition.features.forEach((feature) => {
        const key = feature.properties && feature.properties['hc-key'];
        if (districtKeys.includes(key)) {
          boxes.push(feature);
          return;
        }
        if (!polygonRingsOf(feature.geometry).some(isStackBox)) {
          main.push(feature);
          return;
        }

        boxes.push(feature);
        const polygons =
          feature.geometry.type === 'MultiPolygon'
            ? feature.geometry.coordinates
            : [feature.geometry.coordinates];
        const shapes = polygons.filter((polygon) => !isStackBox(polygon[0]));
        const geometry =
          shapes.length === 1
            ? { type: 'Polygon', coordinates: shapes[0] }
            : { type: 'MultiPolygon', coordinates: shapes };
        const largest = shapes
          .map((polygon) => polygon[0])
          .reduce((best, ring) => (ringArea(ring) > ringArea(best) ? ring : best));
        const pole = poleOfInaccessibility(largest);
        const anchor = computeLabelAnchor(geometry);
        if (key === BOX_FADE_KEY) {
          this.boxFadeRoom = pole.distance;
        }
        main.push({
          ...feature,
          geometry,
          properties: {
            ...feature.properties,
            'hc-middle-x': anchor.x,
            'hc-middle-y': anchor.y,
            cmsShapeRoom: pole.distance,
            cmsShapePole: { x: pole.x, y: pole.y },
          },
        });
      });
      return {
        main: { ...mapDefinition, features: main },
        boxes: { ...mapDefinition, features: boxes },
      };
    },
    getMapPixelsPerUnit(chart) {
      const mapView = chart && chart.mapView;
      if (!mapView || typeof mapView.projectedUnitsToPixels !== 'function') {
        return 0;
      }
      const a = mapView.projectedUnitsToPixels({ x: 0, y: 0 });
      const b = mapView.projectedUnitsToPixels({ x: 10000, y: 0 });
      return Math.abs(b.x - a.x) / 10000;
    },
    // 0 = boxes fully shown, 1 = faded out: Rhode Island's own room grows from 11px to 14px.
    getZoomBoxFade(chart) {
      if (!this.boxFadeRoom) {
        return 0;
      }
      const room = this.boxFadeRoom * this.getMapPixelsPerUnit(chart);
      return Math.min(
        1,
        Math.max(0, (room - BOX_FADE_START_PX) / (BOX_FADE_END_PX - BOX_FADE_START_PX)),
      );
    },
    updateZoomBoxFade() {
      const chart = this.chart;
      if (!chart || !chart.series || chart.series.length < 2) {
        return;
      }

      const fade = this.getZoomBoxFade(chart);
      const boxes = chart.series[1];
      const hidden = fade >= 0.5;
      [boxes.group, boxes.dataLabelsGroup].forEach((group) => {
        if (group) {
          group.css({ opacity: String(1 - fade), pointerEvents: hidden ? 'none' : '' });
        }
      });
      // HTML labels sit outside the SVG group, so each one fades with its box.
      boxes.points.forEach((point) => {
        const label = point.dataLabel;
        const element = label && (label.div || label.element);
        if (element && element.style) {
          element.style.opacity = String(1 - fade);
          element.style.visibility = fade >= 1 ? 'hidden' : '';
        }
      });
      this.drawDcCallout(hidden);
      this.drawDistrictPips();
    },
    // One pip per Maine/Nebraska district, roughly where the district is on the state, in the
    // district's own color (the same as its box), so a district leaning against the statewide
    // result shows on the map. Same size at every zoom; hovering one shows the district's card.
    drawDistrictPips() {
      const chart = this.chart;
      if (this.districtPips) {
        this.districtPips.destroy();
        this.districtPips = null;
      }
      (chart.series[1] ? chart.series[1].points : []).forEach((point) => {
        delete point.tooltipPos;
      });
      if (!this.isDistrictMap || !chart.mapView) {
        return;
      }

      const mapView = chart.mapView;
      const renderer = chart.renderer;
      const group = renderer.g('cms-map-widget__district-pips').attr({ zIndex: 8 }).add();
      Object.entries(DISTRICT_PIPS).forEach(([code, place]) => {
        const state = chart.series[0].points.find(
          (point) => point.stateCode === place.state && point.geometry,
        );
        const district = chart.series[1].points.find((point) => point.stateCode === code);
        if (!state || !district) {
          return;
        }
        const rings = polygonRingsOf(state.geometry);
        const all = [].concat(...rings);
        const minX = Math.min(...all.map((point) => point[0]));
        const maxX = Math.max(...all.map((point) => point[0]));
        const minY = Math.min(...all.map((point) => point[1]));
        const maxY = Math.max(...all.map((point) => point[1]));
        let unit = { x: minX + place.x * (maxX - minX), y: maxY - place.y * (maxY - minY) };
        // Keep it on the state: step toward the state's label point until inside.
        const largest = rings.reduce((best, ring) =>
          ringArea(ring) > ringArea(best) ? ring : best,
        );
        const pole = poleOfInaccessibility(largest);
        for (
          let step = 0;
          step < 5 && !rings.some((ring) => isPointInRing(unit.x, unit.y, ring));
          step += 1
        ) {
          unit = { x: unit.x + (pole.x - unit.x) * 0.25, y: unit.y + (pole.y - unit.y) * 0.25 };
        }
        const at = mapView.projectedUnitsToPixels(unit);
        if (at.x < 0 || at.y < 0 || at.x > chart.plotWidth || at.y > chart.plotHeight) {
          return;
        }
        // The state's label is a fixed size while the state shrinks with the card, so lift the
        // pip just above the label wherever they would touch (if that still lands on the state).
        const labelRect = getHtmlLabelRect(chart, state);
        const reach = DISTRICT_PIP_RADIUS + DISTRICT_PIP_LABEL_GAP_PX;
        if (
          labelRect &&
          at.x + reach > labelRect.left &&
          at.x - reach < labelRect.right &&
          at.y + reach > labelRect.top &&
          at.y - reach < labelRect.bottom
        ) {
          const lifted = { x: at.x, y: labelRect.top - reach };
          const liftedUnit = mapView.pixelsToProjectedUnits(lifted);
          if (rings.some((ring) => isPointInRing(liftedUnit.x, liftedUnit.y, ring))) {
            at.y = lifted.y;
          }
        }
        const pip = renderer
          .circle(at.x, at.y, DISTRICT_PIP_RADIUS)
          .attr({
            fill: getSolidTooltipColor(district.color),
            stroke: '#ffffff',
            'stroke-width': 1.5,
          })
          .add(group);
        // Anchor the card at the pip: the district's own box may be off the plot when zoomed
        // in, and Highcharts hides a tooltip anchored outside the plot.
        pip.on('mouseover', (event) => {
          district.tooltipPos = [at.x, at.y];
          district.onMouseOver(chart.pointer.normalize(event));
        });
        pip.on('mouseout', () => {
          district.onMouseOut();
        });
        pip.on('click', (event) => {
          district.tooltipPos = [at.x, at.y];
          this.showMapPointCard(district, chart.pointer.normalize(event));
        });
      });
      this.districtPips = group;
    },
    // DC never has room for a label, so with the boxes gone it gets a dot and a leader line out to
    // a label in the Atlantic; hovering either shows its card.
    drawDcCallout(show) {
      const chart = this.chart;
      const wasShown = !!this.dcCallout;
      if (this.dcCallout) {
        this.dcCallout.destroy();
        this.dcCallout = null;
      }
      const dc =
        show && chart.series[0].points.find((point) => point.stateCode === 'DC' && point.shapePole);
      if (!dc || !chart.mapView) {
        return;
      }

      const mapView = chart.mapView;
      const at = mapView.projectedUnitsToPixels(dc.shapePole);
      if (at.x < 0 || at.y < 0 || at.x > chart.plotWidth || at.y > chart.plotHeight) {
        return;
      }
      // The tile sits just off the coast at DC's latitude: past the easternmost edge of
      // Delaware and Maryland (the Delmarva coast).
      const coastX = chart.series[0].points
        .filter((point) => ['DE', 'MD'].includes(point.stateCode) && point.geometry)
        .flatMap((point) => polygonRingsOf(point.geometry))
        .flatMap((ring) => ring)
        .reduce((max, [x, y]) => Math.max(max, mapView.projectedUnitsToPixels({ x, y }).x), at.x);
      const renderer = chart.renderer;
      const fill = getSolidTooltipColor(dc.color);
      const text = getMapLabelColor(this.getVisibleFillColor(dc.color));
      // As on a state's label: the leader's price (✓ when called), or on Collection maps the market count.
      const price = this.getTilePrice(dc);
      const tile = { width: price ? 64 : 36, height: 24 };
      tile.x = coastX + 18;
      // A little below DC's latitude, and low enough that the leader line passes under
      // Delaware's label (centered on its shape's pole, about 12px each side of it).
      let lineEndY = at.y + DC_TILE_DROP_PX;
      const de = chart.series[0].points.find(
        (point) => point.stateCode === 'DE' && point.shapePole,
      );
      if (de) {
        const label = mapView.projectedUnitsToPixels(de.shapePole);
        const left = label.x - DC_LABEL_HALF_PX;
        if (left > at.x && left < tile.x) {
          const clearY = label.y + DC_LABEL_HALF_PX + 4;
          lineEndY = Math.max(lineEndY, at.y + ((clearY - at.y) * (tile.x - at.x)) / (left - at.x));
        }
      }
      tile.y = lineEndY - tile.height / 2;
      const group = renderer.g('cms-map-widget__dc-callout').attr({ zIndex: 8 }).add();
      renderer
        .path(['M', at.x, at.y, 'L', tile.x, lineEndY])
        .attr({
          stroke: this.isDarkTheme ? DARK_EMPTY_STATE_LABEL_COLOR : EMPTY_STATE_LABEL_COLOR,
          'stroke-width': 1,
        })
        .add(group);
      renderer
        .circle(at.x, at.y, 3.5)
        .attr({ fill, stroke: this.getMapBorderColor(), 'stroke-width': 1.5 })
        .add(group);
      renderer.rect(tile.x, tile.y, tile.width, tile.height, 2).attr({ fill }).add(group);
      renderer
        .text('DC', tile.x + 6, tile.y + 15.5)
        .css({
          color: text,
          fill: text,
          fontSize: '9px',
          fontWeight: '500',
          fontFamily: BODY_FONT_FAMILY,
        })
        .add(group);
      if (price) {
        renderer
          .text(price, tile.x + tile.width - 6, tile.y + 16)
          .attr({ align: 'right' })
          .css({
            color: text,
            fill: text,
            fontSize: '11px',
            fontWeight: '700',
            fontFamily: NUMBERS_FONT_FAMILY,
          })
          .add(group);
      }
      group.on('mouseover', (event) => {
        dc.onMouseOver(chart.pointer.normalize(event));
      });
      group.on('mouseout', () => {
        dc.onMouseOut();
      });
      group.on('click', (event) => {
        this.showMapPointCard(dc, chart.pointer.normalize(event));
      });
      // It eases in when it first appears, like the labels. It is redrawn on every view change
      // (one zoom step redraws it twice), so a redraw picks the fade up where it was.
      const now = Date.now();
      if (!wasShown) {
        this.dcCalloutShownAt = now;
      }
      const elapsed = now - (this.dcCalloutShownAt || 0);
      if (elapsed < MAP_LABEL_FADE_IN_MS) {
        group
          .attr({ opacity: elapsed / MAP_LABEL_FADE_IN_MS })
          .animate({ opacity: 1 }, { duration: MAP_LABEL_FADE_IN_MS - elapsed });
      }
      this.dcCallout = group;
    },
    // The Maine/Nebraska card's district list: each district's leader and price (a link to its
    // market), or its safe party.
    getDistrictSectionHtml(stateCode) {
      const codes = DISTRICT_STATES[stateCode];
      if (!this.isDistrictMap || !codes) {
        return '';
      }

      const grouped = this.groupLocationsByStateCode();
      const rows = codes.map((code) => {
        const label = escapeTooltipHtml(this.getLocationLabel(code));
        const location = this.getRepresentativeLocation(grouped[code] || []);
        const leader = location ? this.getLeadingContract(location) : null;
        if (leader) {
          const party = this.getContractParty(leader);
          const color =
            PARTY_ACCENT_COLORS[party] || getSafeTooltipColor(this.resolveElectionColor(location));
          const name = escapeTooltipHtml(
            party
              ? party.charAt(0).toUpperCase() + party.slice(1)
              : leader.contractName || leader.name || '',
          );
          const price = this.isCalledContract(leader)
            ? 'Called'
            : this.formatPrice(leader.lastTradePrice);
          const market = this.getLeadingMarketSelection(location).market;
          const marketId = (market && market.marketId) || location.marketId;
          return `<div class="cms-map-widget__tooltip-district"${this.getTooltipMarketAttribute(marketId, leader.contractId)}><span class="cms-map-widget__tooltip-district-code">${label}</span><span class="cms-map-widget__tooltip-generic-dot" style="background:${color}"></span><span class="cms-map-widget__tooltip-district-name">${name}</span><span class="cms-map-widget__tooltip-district-price">${price}</span></div>`;
        }
        const group = this.getPredefinedGroupForState(code);
        const party = group
          ? this.getGroupParty(group) === PARTY_REPUBLICAN
            ? 'Republican'
            : 'Democratic'
          : '';
        const text = group
          ? escapeTooltipHtml(group.isDecided ? `Safe ${party}` : group.label || 'No election')
          : 'No market';
        const color = group
          ? PARTY_ACCENT_COLORS[this.getGroupParty(group)]
          : NEUTRAL_KEYLINE_COLOR;
        return `<div class="cms-map-widget__tooltip-district cms-map-widget__tooltip-district--static"><span class="cms-map-widget__tooltip-district-code">${label}</span><span class="cms-map-widget__tooltip-generic-dot" style="background:${color}"></span><span class="cms-map-widget__tooltip-district-name">${text}</span><span class="cms-map-widget__tooltip-district-price"></span></div>`;
      });
      return `<div class="cms-map-widget__tooltip-districts"><div class="cms-map-widget__tooltip-districts-title">Districts</div>${rows.join('')}</div>`;
    },
    getChartData(mapShapes) {
      const groupedByStateCode = this.groupLocationsByStateCode();
      const emptyColor = this.getEmptyColor();

      return mapShapes.map((shape) => {
        const stateCode = (shape.properties['hc-a2'] || '').toUpperCase();
        shape.stateName = (shape.name || '').toUpperCase();
        const locations = groupedByStateCode[stateCode] || [];
        const representative = this.getRepresentativeLocation(locations);
        const configuredGroup = this.getPredefinedGroupForState(stateCode);
        const predefinedGroup =
          configuredGroup && (configuredGroup.isLocked || !representative) ? configuredGroup : null;
        const marketData = predefinedGroup ? null : representative;
        const isEmptyState = !marketData && !predefinedGroup;
        const chartPoint = {
          stateName: shape.stateName,
          stateCode,
          stateValue: this.getStateValue(stateCode, marketData),
          marketData,
          predefinedGroup,
          className: marketData
            ? 'cms-map-widget__map-point--linked'
            : 'cms-map-widget__map-point--informational',
          color: predefinedGroup
            ? this.getPredefinedGroupColor(predefinedGroup)
            : this.resolveColor(marketData, emptyColor),
          value: marketData || predefinedGroup ? 1 : 0,
        };
        if (isEmptyState) {
          chartPoint.dataLabels = {
            style: {
              color: this.isDarkTheme ? DARK_EMPTY_STATE_LABEL_COLOR : EMPTY_STATE_LABEL_COLOR,
            },
          };
        } else if (marketData) {
          chartPoint.dataLabels = {
            style: { color: getMapLabelColor(this.getVisibleFillColor(chartPoint.color)) },
          };
        }
        if (predefinedGroup) {
          this.applyNoMarketStyle(chartPoint, predefinedGroup, emptyColor);
        }
        const anchor = MAP_LABEL_ANCHORS[stateCode];
        if (anchor) {
          chartPoint.middleX = anchor.x;
          chartPoint.middleY = anchor.y;
        }
        // Small-state stack, Hawaii and district boxes: labelled inside a fixed-size box.
        chartPoint.isBoxLabel = polygonRingsOf(shape.geometry).some(isStackBox);
        if (chartPoint.isBoxLabel) {
          chartPoint.boxRing = polygonRingsOf(shape.geometry).find(isStackBox);
        }
        // A small state's own shape, labelled once its box fades out (its label eases in).
        if (shape.properties && shape.properties.cmsShapeRoom) {
          chartPoint.shapeRoom = shape.properties.cmsShapeRoom;
          chartPoint.shapePole = shape.properties.cmsShapePole;
          chartPoint.dataLabels = {
            ...chartPoint.dataLabels,
            className: 'cms-map-widget__map-label cms-map-widget__map-label--shape',
          };
        }
        return chartPoint;
      });
    },
    buildToolbarCounter(party) {
      const counter = {
        total: 0,
        widthTotal: 0,
        low: 0,
        medium: 0,
        high: 0,
        full: 0,
        predefined: 0,
        lowWidth: 0,
        mediumWidth: 0,
        highWidth: 0,
        fullWidth: 0,
        predefinedWidth: 0,
        predefinedLabel: 'No election',
        predefinedDisplayText: '',
        // For the hints: safe = the full bucket (decided, called, baseline), likely = high (markets).
        safe: 0,
        likely: 0,
      };

      this.toolbarBaselines
        .filter(
          (baseline) =>
            this.getBaselineBucket(baseline) !== BUCKET_TOSS_UP &&
            this.getGroupParty(baseline) === party,
        )
        .forEach((baseline) => {
          // A "high" baseline is a party's settled seats (no market behind them).
          const baselineBucket = this.getBaselineBucket(baseline);
          const bucket = baselineBucket === 'high' ? BUCKET_FULL : baselineBucket;
          const count = Number(baseline.count) || 0;
          const reservedCount =
            baseline.reservedCount === null || typeof baseline.reservedCount === 'undefined'
              ? count
              : Number(baseline.reservedCount) || 0;
          counter[bucket] += count;
          counter[`${bucket}Width`] += reservedCount;
          counter.total += count;
          counter.widthTotal += reservedCount;
          if (bucket === BUCKET_FULL) {
            counter.safe += count;
          }
        });

      // Toss-ups sit in their own middle segment and count for neither party.
      this.electionRows
        .filter((row) => row.party === party && !row.isTossUp)
        .forEach((row) => {
          const bucket = row.isCalled
            ? BUCKET_FULL
            : this.getPaletteBucket(row.leadingContract.lastTradePrice);
          const value = row.stateValue || 1;
          counter[bucket] += value;
          counter[`${bucket}Width`] += value;
          counter.total += value;
          counter.widthTotal += value;
          if (row.isCalled) {
            counter.safe += value;
          } else if (bucket === 'high') {
            counter.likely += value;
          }
        });

      this.predefinedGroups
        .filter((group) => this.getGroupParty(group) === party)
        .forEach((group) => {
          const fallbackCount = Array.isArray(group.locationCodes) ? group.locationCodes.length : 0;
          const seatCount =
            group.seatCount === null || typeof group.seatCount === 'undefined'
              ? fallbackCount
              : Number(group.seatCount) || 0;
          const reservedSeatCount =
            group.reservedSeatCount === null || typeof group.reservedSeatCount === 'undefined'
              ? seatCount
              : Number(group.reservedSeatCount) || 0;
          if (group.isDecided) {
            // Races decided without a market count as safe seats, not as no-election seats.
            counter.full += seatCount;
            counter.fullWidth += reservedSeatCount;
            counter.total += seatCount;
            counter.widthTotal += reservedSeatCount;
            counter.safe += seatCount;
            return;
          }
          counter.predefined += seatCount;
          counter.predefinedWidth += reservedSeatCount;
          counter.total += seatCount;
          counter.widthTotal += reservedSeatCount;
          if (group.label) {
            counter.predefinedLabel = group.label;
          }
          if (group.displayText) {
            counter.predefinedDisplayText = group.displayText;
          }
        });

      if (!counter.predefinedDisplayText) {
        counter.predefinedDisplayText = `${counter.predefined}`;
      }

      return counter;
    },
    getGroupParty(group) {
      const party = ((group && group.party) || '').toLowerCase();
      if (party.includes('rep')) {
        return PARTY_REPUBLICAN;
      }
      return PARTY_DEMOCRATIC;
    },
    // Baseline buckets: "high" (likely/safe, the default), "medium" (lean), "low" (slight lean),
    // "tossup" (counted in the middle segment, whatever its party).
    getBaselineBucket(baseline) {
      const requestedBucket = ((baseline && baseline.bucket) || '')
        .toLowerCase()
        .replace(/[^a-z]/g, '');
      return ['low', 'medium', 'high', BUCKET_TOSS_UP].includes(requestedBucket)
        ? requestedBucket
        : 'high';
    },
    // The bar's middle segments: toss-ups (any party) and independent-led states, each counted
    // for neither party. Toss-up baselines count in the toss-up segment.
    buildMiddleToolbarCounter(bucket) {
      const counter = { total: 0, widthTotal: 0, [bucket]: 0, [`${bucket}Width`]: 0 };
      const add = (count, width) => {
        counter.total += count;
        counter.widthTotal += width;
        counter[bucket] += count;
        counter[`${bucket}Width`] += width;
      };
      this.toolbarBaselines
        .filter(
          (baseline) =>
            bucket === BUCKET_TOSS_UP && this.getBaselineBucket(baseline) === BUCKET_TOSS_UP,
        )
        .forEach((baseline) => {
          const count = Number(baseline.count) || 0;
          const reservedCount =
            baseline.reservedCount === null || typeof baseline.reservedCount === 'undefined'
              ? count
              : Number(baseline.reservedCount) || 0;
          add(count, reservedCount);
        });
      this.electionRows
        .filter((row) =>
          bucket === BUCKET_TOSS_UP
            ? row.isTossUp
            : !row.isTossUp && row.party === PARTY_INDEPENDENT,
        )
        .forEach((row) => {
          const value = row.stateValue || 1;
          add(value, value);
        });
      return counter;
    },
    createToolbarSegment(counter, bucket, party) {
      // No-election ("predefined") seats: solid party color like the safe seats, with the group's
      // own label in the tooltip.
      const isPredefined = bucket === 'predefined';
      const value = counter[bucket] || 0;
      const widthValue = counter[`${bucket}Width`];
      const width = roundToTwo(
        ((typeof widthValue === 'number' ? widthValue : value) / this.toolbarTotalWidth) * 100,
      );
      return {
        key: `${party}-${bucket}`,
        party,
        bucket,
        value,
        width,
        color: this.getBucketColor(party, bucket),
        label: isPredefined
          ? counter.predefinedLabel
          : MIDDLE_SEGMENT_LABELS[bucket] || PARTY_SEGMENT_LABELS[bucket] || '',
        displayText: isPredefined ? counter.predefinedDisplayText : `${value}`,
        showText: width >= WIDTH_PERCENTAGE_THRESHOLD,
      };
    },
    trackTooltipPointer(event) {
      const pointer = this.chart && this.chart.pointer;
      if (!pointer || typeof pointer.normalize !== 'function') {
        return;
      }

      const normalized = pointer.normalize(event);
      this.tooltipPointer = { x: normalized.chartX, y: normalized.chartY };
      const now = Date.now();
      this.tooltipPointerTrail = this.tooltipPointerTrail
        .filter((entry) => now - entry.time <= TOOLTIP_TRAIL_MS)
        .concat({ ...this.tooltipPointer, time: now });
    },
    // 'left' / 'right' for the card from the pointer's recent horizontal travel; '' when unclear.
    getTooltipCardSide() {
      // The travel that brought the pointer here: the last TOOLTIP_TRAIL_PATH_PX of its path
      // (within TOOLTIP_TRAIL_MS of its last move, since the card opens after the hover intent
      // delay, when the pointer has usually stopped), so slow moves count as well as fast ones.
      const trail = this.tooltipPointerTrail;
      const last = trail[trail.length - 1];
      if (!last) {
        return '';
      }

      let first = last;
      let path = 0;
      for (let index = trail.length - 2; index >= 0 && path < TOOLTIP_TRAIL_PATH_PX; index--) {
        const entry = trail[index];
        if (last.time - entry.time > TOOLTIP_TRAIL_MS) {
          break;
        }
        path += Math.hypot(trail[index + 1].x - entry.x, trail[index + 1].y - entry.y);
        first = entry;
      }

      const dx = last.x - first.x;
      const dy = last.y - first.y;
      if (Math.abs(dx) < TOOLTIP_TRAIL_MIN_PX || Math.abs(dx) < Math.abs(dy)) {
        return '';
      }

      return dx > 0 ? 'left' : 'right';
    },
    clearTooltipIntent() {
      if (this.tooltipIntentTimer) {
        clearTimeout(this.tooltipIntentTimer);
        this.tooltipIntentTimer = null;
      }
    },
    isPointerOnTooltip(tooltip) {
      if (
        tooltip &&
        typeof tooltip.isStickyOnContact === 'function' &&
        tooltip.isStickyOnContact()
      ) {
        return true;
      }

      const element =
        tooltip && tooltip.label && tooltip.label.div
          ? tooltip.label.div
          : tooltip && tooltip.label && tooltip.label.element;
      return !!(element && typeof element.matches === 'function' && element.matches(':hover'));
    },
    installTooltipIntent() {
      const chart = this.chart;
      const tooltip = chart && chart.tooltip;
      if (!tooltip || typeof tooltip.refresh !== 'function' || tooltip.cmsIntentInstalled) {
        return;
      }

      // Highcharts' own stick-on-contact tracking never sees the HTML card (its label div is
      // 0x0), so a hide started while the pointer crossed a neighbour or a gap on its way to the
      // card still faded the card out under the pointer. Keep the card while it is hovered,
      // cancel a pending hide when the pointer reaches it, and hide normally when it leaves.
      const originalHide = tooltip.hide.bind(tooltip);
      tooltip.hide = (delay) => {
        if (!tooltip.isHidden && this.isPointerOnTooltip(tooltip)) {
          return;
        }
        originalHide(delay);
        // The active state lets go once the tooltip has actually hidden (a refresh cancels it).
        clearTimeout(this.activePointReleaseTimer);
        const wait =
          (typeof delay === 'number' ? delay : Number(tooltip.options.hideDelay) || 0) + 40;
        this.activePointReleaseTimer = setTimeout(() => {
          // Still on the state (its tooltip hid for some other reason): keep the styling; leaving
          // the state hides the tooltip again, which releases it.
          const stillHovered =
            !!this.chart && !!this.activeMapPoint && this.chart.hoverPoint === this.activeMapPoint;
          if (tooltip.isHidden && !stillHovered) {
            this.setActiveMapPoint(null);
          }
        }, wait);
      };
      const isInTooltipCard = (node) =>
        !!(node && typeof node.closest === 'function' && node.closest('.cms-map-widget__tooltip'));
      const getNodePoint = (node) => {
        for (
          let current = node;
          current && current !== chart.container;
          current = current.parentNode
        ) {
          if (current.point) {
            return current.point;
          }
        }
        return null;
      };
      const onTooltipContact = (event) => {
        if (event.type === 'mouseover' && isInTooltipCard(event.target)) {
          clearTimeout(tooltip.hideTimer);
        } else if (
          event.type === 'mouseout' &&
          isInTooltipCard(event.target) &&
          !isInTooltipCard(event.relatedTarget)
        ) {
          // Back on the state the card belongs to: keep it. This also covers a still pointer that
          // the card slid across while moving to its new place (the browser reports that as the
          // pointer entering and leaving the card).
          if (
            !tooltip.isHidden &&
            this.tooltipShownPoint &&
            getNodePoint(event.relatedTarget) === this.tooltipShownPoint
          ) {
            return;
          }
          tooltip.hide();
        }
      };
      if (chart.container) {
        chart.container.addEventListener('mouseover', onTooltipContact);
        chart.container.addEventListener('mouseout', onTooltipContact);
      }

      const originalRefresh = tooltip.refresh.bind(tooltip);
      const show = (pointOrPoints, event, point, isTouch) => {
        const fallback =
          event && typeof event.chartX === 'number' ? { x: event.chartX, y: event.chartY } : null;
        // Re-anchor only when a different state (or a hidden tooltip) is shown, so briefly
        // crossing a neighbour and coming back does not nudge the tooltip. A tap anchors at
        // the tap itself; the tracked mouse position may be left over from an earlier tap.
        if (point !== this.tooltipShownPoint || tooltip.isHidden || !this.tooltipAnchor) {
          this.tooltipAnchor = isTouch
            ? fallback || this.tooltipPointer
            : this.tooltipPointer || fallback;
          // Fixed with the anchor, so the card doesn't flip while the pointer moves to it.
          this.tooltipCardSide = isTouch ? '' : this.getTooltipCardSide();
        }
        this.tooltipShownPoint = point;
        originalRefresh(pointOrPoints, event);
        this.setActiveMapPoint(point);
      };

      const isTouchEvent = (event) =>
        !!event && (event.pointerType === 'touch' || (event.type || '').startsWith('touch'));
      // A click or tap shows the card straight away, without the hover intent delay.
      tooltip.cmsShowNow = (point, event) => {
        this.clearTooltipIntent();
        show(point, event, point, isTouchEvent(event));
      };

      tooltip.cmsIntentInstalled = true;
      tooltip.refresh = (pointOrPoints, event) => {
        const point = Array.isArray(pointOrPoints) ? pointOrPoints[0] : pointOrPoints;
        const isTouch = isTouchEvent(event);
        this.clearTooltipIntent();

        if (isTouch || (point === this.tooltipShownPoint && !tooltip.isHidden)) {
          show(pointOrPoints, event, point, isTouch);
          return;
        }

        this.tooltipIntentTimer = setTimeout(() => {
          this.tooltipIntentTimer = null;
          if (!this.chart || this.chart.tooltip !== tooltip || this.chart.hoverPoint !== point) {
            return;
          }
          if (!tooltip.isHidden && this.isPointerOnTooltip(tooltip)) {
            return;
          }
          show(pointOrPoints, event, point);
        }, TOOLTIP_INTENT_DELAY);
      };
    },
    // Clicking or tapping a state (or a district pip or the DC tile) opens its card; the card's
    // rows and "Open market" link go to the market. A card already open for it stays open.
    showMapPointCard(point, event) {
      const tooltip = this.chart && this.chart.tooltip;
      if (!point || !tooltip || typeof tooltip.cmsShowNow !== 'function') {
        return;
      }
      if (point === this.tooltipShownPoint && !tooltip.isHidden) {
        return;
      }
      tooltip.cmsShowNow(point, event);
    },
    // The chart-pixel rectangle (plus a margin) of the hovered state's shape on the map, which its
    // tooltip must leave visible; `onBox` when the pointer is on its small-state or district box.
    // District boxes have no shape elsewhere on the map, so there is nothing to keep clear.
    getTooltipAvoidRect(chart, point, anchor) {
      const mapView = chart && chart.mapView;
      if (
        !anchor ||
        !point ||
        !point.geometry ||
        !mapView ||
        typeof mapView.projectedUnitsToPixels !== 'function'
      ) {
        return null;
      }

      const rings = polygonRingsOf(point.geometry);
      const box = rings.find(isStackBox);
      const shapeRings = rings.filter((ring) => ring !== box);
      if (shapeRings.length === 0) {
        return null;
      }

      const toRect = (ringList) =>
        ringList.reduce(
          (rect, ring) =>
            ring.reduce((acc, [px, py]) => {
              const pixel = mapView.projectedUnitsToPixels({ x: px, y: py });
              return {
                left: Math.min(acc.left, pixel.x),
                right: Math.max(acc.right, pixel.x),
                top: Math.min(acc.top, pixel.y),
                bottom: Math.max(acc.bottom, pixel.y),
              };
            }, rect),
          { left: Infinity, right: -Infinity, top: Infinity, bottom: -Infinity },
        );
      const boxRect = box ? toRect([box]) : null;
      const onBox =
        !!boxRect &&
        anchor.x >= boxRect.left &&
        anchor.x <= boxRect.right &&
        anchor.y >= boxRect.top &&
        anchor.y <= boxRect.bottom;
      const shape = toRect(shapeRings);
      return {
        onBox,
        left: shape.left - TOOLTIP_AVOID_MARGIN,
        right: shape.right + TOOLTIP_AVOID_MARGIN,
        top: shape.top - TOOLTIP_AVOID_MARGIN,
        bottom: shape.bottom + TOOLTIP_AVOID_MARGIN,
      };
    },
    getTooltipPosition(chart, labelWidth, labelHeight, point) {
      const chartWidth = chart.chartWidth;
      const chartHeight = chart.chartHeight;
      const anchor = this.tooltipAnchor || {
        x: (point.plotX || 0) + (chart.plotLeft || 0),
        y: (point.plotY || 0) + (chart.plotTop || 0),
      };

      // Prefer the side the pointer came from (right when unclear), extending down; switch sides /
      // flip upwards when the tooltip would leave the chart, then clamp so it stays inside it.
      const rightX = anchor.x + TOOLTIP_OFFSET_X;
      const leftX = anchor.x - TOOLTIP_OFFSET_X - labelWidth;
      const fitsRight = rightX + labelWidth <= chartWidth - TOOLTIP_EDGE_PADDING_X;
      const fitsLeft = leftX >= TOOLTIP_EDGE_PADDING_X;
      let x = rightX;
      if (this.tooltipCardSide === 'left' ? fitsLeft : !fitsRight) {
        x = leftX;
      }
      let y = anchor.y - TOOLTIP_OFFSET_Y;
      if (y + labelHeight > chartHeight - TOOLTIP_EDGE_PADDING_BOTTOM) {
        y = anchor.y + TOOLTIP_OFFSET_Y - labelHeight;
      }

      const maxX = Math.max(
        TOOLTIP_EDGE_PADDING_X,
        chartWidth - labelWidth - TOOLTIP_EDGE_PADDING_X,
      );
      const maxY = Math.max(
        TOOLTIP_EDGE_PADDING_TOP,
        chartHeight - labelHeight - TOOLTIP_EDGE_PADDING_BOTTOM,
      );
      const clamp = (position) => ({
        x: Math.min(Math.max(position.x, TOOLTIP_EDGE_PADDING_X), maxX),
        y: Math.min(Math.max(position.y, TOOLTIP_EDGE_PADDING_TOP), maxY),
      });
      const position = clamp({ x, y });

      // Never cover the state's own shape: when the usual spot would, use the clear spot nearest
      // the pointer - beside, below or above the shape (for a small-state box: beside the box,
      // just above or below the shape). (The positioner's `point` is only a position stub; the
      // shown point carries the geometry.)
      const avoid = this.getTooltipAvoidRect(
        chart,
        this.tooltipShownPoint || chart.hoverPoint,
        anchor,
      );
      const overlap = (rect) => {
        if (!avoid) {
          return 0;
        }
        const width = Math.min(rect.x + labelWidth, avoid.right) - Math.max(rect.x, avoid.left);
        const height = Math.min(rect.y + labelHeight, avoid.bottom) - Math.max(rect.y, avoid.top);
        return width > 0 && height > 0 ? width * height : 0;
      };
      // With a known direction, a card wholly on the side the pointer is heading toward is "ahead"
      // of it; it is used only when no other clear spot exists.
      const preferredSide = this.tooltipCardSide;
      const isAhead = (rect) => {
        if (preferredSide === 'left') {
          return rect.x >= anchor.x;
        }
        if (preferredSide === 'right') {
          return rect.x + labelWidth <= anchor.x;
        }
        return false;
      };
      if (overlap(position) === 0 && (!isAhead(position) || !avoid)) {
        return position;
      }

      const besideY = anchor.y - TOOLTIP_OFFSET_Y;
      const centeredX = anchor.x - labelWidth / 2;
      const candidates = (
        avoid.onBox
          ? [
              { x: position.x, y: avoid.top - labelHeight },
              { x: position.x, y: avoid.bottom },
              { x: avoid.left - labelWidth, y: position.y },
            ]
          : [
              { x: avoid.right, y: besideY, side: 'right' },
              { x: avoid.left - labelWidth, y: besideY, side: 'left' },
              { x: centeredX, y: avoid.bottom },
              { x: centeredX, y: avoid.top - labelHeight },
            ]
      ).map((rect) => ({ ...clamp(rect), side: rect.side }));
      const distance = (rect) => {
        const dx = Math.max(rect.x - anchor.x, 0, anchor.x - (rect.x + labelWidth));
        const dy = Math.max(rect.y - anchor.y, 0, anchor.y - (rect.y + labelHeight));
        return Math.sqrt(dx * dx + dy * dy);
      };
      // Nearest clear spot that isn't ahead of the pointer (one ahead only when nothing else is
      // clear); the spot beside the shape on the side the pointer came from wins when it is
      // about as near as that.
      const clear = candidates
        .filter((rect) => overlap(rect) === 0)
        .sort((a, b) => distance(a) - distance(b));
      const behind = clear.filter((rect) => !isAhead(rect));
      if (behind.length > 0) {
        const preferred = behind.find((rect) => rect.side && rect.side === preferredSide);
        return preferred && distance(preferred) - distance(behind[0]) <= TOOLTIP_SIDE_TIE_PX
          ? preferred
          : behind[0];
      }
      if (overlap(position) === 0) {
        return position;
      }
      if (clear.length > 0) {
        return clear[0];
      }

      // No clear spot (a very large state on a narrow map): cover as little of it as possible.
      return candidates.concat(position).sort((a, b) => overlap(a) - overlap(b))[0];
    },
    renderChart() {
      const mapKey = 'countries/us/custom/us-small';
      if (
        !this.$refs.chart ||
        typeof Highcharts === 'undefined' ||
        !Highcharts.maps ||
        !Highcharts.maps[mapKey]
      ) {
        this.hasError = true;
        return;
      }

      if (Highcharts.AST && Array.isArray(Highcharts.AST.allowedAttributes)) {
        [TOOLTIP_MARKET_ID_ATTRIBUTE, TOOLTIP_CONTRACT_ID_ATTRIBUTE].forEach((attribute) => {
          if (!Highcharts.AST.allowedAttributes.includes(attribute)) {
            Highcharts.AST.allowedAttributes.push(attribute);
          }
        });
      }

      const mapDefinition = this.getMapDefinition(Highcharts.maps[mapKey]);
      // The boxes are their own layer over the states, so they can fade out when zoomed in.
      const layers = this.splitBoxFeatures(mapDefinition);
      const mapShapes = Highcharts.geojson(layers.main);
      const data = this.getChartData(mapShapes);
      const boxShapes = Highcharts.geojson(layers.boxes);
      const boxData = this.getChartData(boxShapes);
      const vm = this;
      const seriesOptions = (seriesMapData, seriesData) => ({
        mapData: seriesMapData,
        data: seriesData,
        joinBy: 'stateName',
        tooltip: {
          followPointer: false,
        },
        borderColor: this.getMapBorderColor(),
        // Hover styling follows the state whose tooltip is shown (setActiveMapPoint), not
        // Highcharts' per-state hover, so states crossed on the way to a tooltip stay unchanged.
        // (Inactive off: hovering one layer would dim the other.)
        states: { hover: { enabled: false }, inactive: { enabled: false } },
        point: {
          events: {
            mouseOver: function darkenOnHover() {
              vm.onMapPointHover(this);
            },
          },
        },
        dataLabels: {
          enabled: true,
          allowOverlap: true,
          formatter: function dataLabelFormatter() {
            return vm.getDataLabel(this.point);
          },
          useHTML: true,
          className: 'cms-map-widget__map-label',
          style: {
            textOutline: 0,
            fontSize: '11px',
            fontFamily: NUMBERS_FONT_FAMILY,
            color: '#ffffff',
          },
        },
      });

      this.dcCallout = null;
      this.districtPips = null;
      this.clearTooltipIntent();
      this.tooltipShownPoint = null;
      this.closeZoomPanel();
      this.zoomFactor = 1;
      clearTimeout(this.activePointReleaseTimer);
      this.activeMapPoint = null;
      if (this.chart) {
        this.chart.destroy();
      }

      // markRaw: a reactive proxy around the chart would hand back proxied points and
      // tooltips, breaking identity checks against the raw objects Highcharts passes in.
      this.chart = markRaw(
        Highcharts.mapChart(this.$refs.chart, {
          chart: {
            backgroundColor: 'transparent',
            spacing: [0, 0, 0, 0],
            margin: [0, 0, 0, 0],
            events: {
              // Districts exist only in the box layer, which the data export leaves out: list
              // them after the states, one row each, as before the layers were split.
              exportData: function addBoxLayerRows(event) {
                const names = new Set(this.series[0].points.map((point) => point.name));
                this.series[1].points
                  .filter((point) => !names.has(point.name))
                  .forEach((point) => {
                    event.dataRows.push([point.name, point.value]);
                  });
              },
              // Print uses the live chart, which has no watermark of its own (the on-page one
              // is HTML outside the chart), so draw one just for printing.
              beforePrint: function addPrintWatermark() {
                this.cmsPrintWatermark = addChartWatermark(this, vm.isDarkTheme);
              },
              afterPrint: function removePrintWatermark() {
                if (this.cmsPrintWatermark) {
                  this.cmsPrintWatermark.destroy();
                  this.cmsPrintWatermark = null;
                }
              },
            },
            style: {
              fontFamily: BODY_FONT_FAMILY,
            },
          },
          title: {
            text: '',
          },
          // The district column sits at the map's left edge; on narrow maps its labels are a little
          // wider than the boxes, so leave room for them (no effect when the map is height-bound).
          mapView: {
            padding: [0, 0, 0, this.isDistrictMap ? DISTRICT_EDGE_PADDING : 0],
          },
          legend: {
            enabled: false,
          },
          credits: {
            enabled: false,
          },
          navigation: {
            menuStyle: {
              padding: '6px 0',
              border: '1px solid var(--cmw-border)',
              borderRadius: '8px',
              background: 'var(--cmw-surface)',
              boxShadow: '0 12px 28px rgba(20, 30, 50, 0.14), 0 2px 6px rgba(20, 30, 50, 0.08)',
            },
            menuItemStyle: {
              padding: '8px 14px',
              color: 'var(--cmw-text)',
              fontFamily: BODY_FONT_FAMILY,
              fontSize: '13px',
              fontWeight: '400',
              whiteSpace: 'nowrap',
              transition: 'background 0.18s cubic-bezier(0.2, 0, 0, 1)',
            },
            menuItemHoverStyle: {
              background: 'var(--cmw-surface-muted)',
              color: 'var(--cmw-text)',
            },
          },
          exporting: {
            enabled: this.getMapFlag('showExports', true),
            chartOptions: {
              chart: {
                // The on-page chart is transparent over the card; JPEG has no alpha, so a transparent
                // export renders black (hiding the watermark). Exports follow the page theme.
                backgroundColor: this.isDarkTheme ? DARK_EXPORT_BACKGROUND : '#ffffff',
                events: {
                  load: function prepareExportChart() {
                    this.series.forEach((series) => {
                      series.update(
                        {
                          dataLabels: {
                            useHTML: false,
                            formatter: function exportDataLabelFormatter() {
                              return vm.getExportDataLabel(this.point);
                            },
                          },
                        },
                        false,
                      );
                    });
                    this.redraw(false);
                    addChartWatermark(this, vm.isDarkTheme);
                  },
                },
              },
            },
            menuItemDefinitions: {
              viewFullscreen: {
                textKey: 'viewFullscreen',
                onclick() {
                  vm.toggleWidgetFullscreen();
                },
              },
              viewMapDataTable: {
                text: 'View data table',
                onclick() {
                  vm.toggleDataTable();
                },
              },
            },
            buttons: {
              contextButton: {
                enabled: false,
                menuItems: this.canShowDataTable
                  ? MAP_WIDGET_CONTEXT_MENU_ITEMS
                  : DEFAULT_CONTEXT_MENU_ITEMS,
              },
            },
          },
          mapNavigation: {
            enabled: true,
            enableButtons: false,
            enableDoubleClickZoom: false,
            enableDoubleClickZoomTo: false,
            enableMouseWheelZoom: false,
            enableTouchZoom: true,
          },
          tooltip: {
            useHTML: true,
            borderRadius: 0,
            borderWidth: 0,
            shadow: false,
            padding: 0,
            hideDelay: 400,
            stickOnContact: true,
            positioner: function tooltipPositioner(labelWidth, labelHeight, point) {
              return vm.getTooltipPosition(this.chart, labelWidth, labelHeight, point);
            },
            backgroundColor: 'rgba(255, 255, 255, 0)',
            style: {
              pointerEvents: 'auto',
            },
            formatter: function formatter() {
              return vm.getTooltipHtml(this.point);
            },
          },
          plotOptions: {
            series: {
              point: {
                events: {
                  click: function onClick(event) {
                    vm.showMapPointCard(this, event);
                  },
                },
              },
            },
          },
          // The box layer stays out of the CSV/XLS export (it repeats the small states and would add
          // a second column); its districts are added back in `exportData`.
          series: [
            seriesOptions(mapShapes, data),
            { ...seriesOptions(boxShapes, boxData), includeInDataExport: false },
          ],
        }),
      );
      applyChartTheme(this.chart);

      this.installTooltipIntent();
      if (this.chart.mapView) {
        this.applyMapZoomLimit();
        Highcharts.addEvent(this.chart.mapView, 'afterSetView', () => {
          // A lifted copy is drawn for the old view; let it go when the map moves.
          this.setActiveMapPoint(null);
          this.applyMapZoomLimit();
          this.syncMapZoom();
          this.updateZoomBoxFade();
        });
      }
      Highcharts.addEvent(this.chart, 'redraw', () => this.updateZoomBoxFade());
      this.updateZoomBoxFade();
      this.startContextMenuObserver();

      if (this.shouldShowPinchHint) {
        this.showPinchHint = true;
      }
    },
    fetchMapWidgetData() {
      // V2 returns the whole map in one call. Fall back to the V1 pair when the API has no V2
      // endpoint yet (the backend and frontend can deploy at different times).
      return api.map
        .getMapWidget(this.mapName)
        .then((response) => adaptMapWidgetResponse(response.data))
        .catch((error) => {
          if (!error || !error.response || error.response.status !== 404) {
            throw error;
          }

          return Promise.all([
            api.map.getMapData(this.mapName, false),
            api.map.getMapLegend(this.mapName, false),
          ]).then(([mapResponse, legendResponse]) => ({
            mapData: Array.isArray(mapResponse.data) ? mapResponse.data : [],
            legendData: Array.isArray(legendResponse.data) ? legendResponse.data : [],
            mapSettings: null,
            unlinkedLocations: [],
          }));
        });
    },
    loadMapData() {
      this.isLoading = true;
      this.hasError = false;
      this.showDataTable = false;

      return this.fetchMapWidgetData()
        .then(({ mapData, legendData, mapSettings, unlinkedLocations }) => {
          this.mapData = mapData;
          this.legendData = legendData;
          this.mapSettings = mapSettings;
          this.unlinkedLocations = unlinkedLocations;
          return new Promise((resolve) => {
            this.$nextTick(() => {
              if (this.showMapArea) {
                this.renderChart();
              }
              resolve();
            });
          });
        })
        .catch((error) => {
          log.error('Failed to load CMS map widget data.', error);
          this.hasError = true;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
  watch: {
    widgetWidth() {
      this.$nextTick(() => this.positionToolbarMiddleLabels());
    },
    compactLayout() {
      this.$nextTick(() => {
        this.syncCompactStoryObserver();
        this.fitCompactHeight();
        this.fitCompactSummary();
        this.reflowChart();
        this.positionToolbarMiddleLabels();
      });
    },
    resolvedTheme() {
      if (this.chart && this.mapData && this.mapData.length > 0) {
        this.renderChart();
        return;
      }
      applyChartTheme(this.chart);
    },
    showDataTable(isOpen) {
      this.syncDataTableOutsideClose(isOpen);
    },
    showSummaryInfo(isOpen) {
      this.syncSummaryInfoOutsideClose(isOpen);
    },
    activeTileCode(code) {
      // Where the tiles start in a compact widget: the card for a lower tile moves up over the
      // summary (measured before the card renders, so it opens in place).
      if (code && this.compactLayout && this.$refs.tileMap && this.$el) {
        this.compactTilesTop = Math.round(
          this.$refs.tileMap.getBoundingClientRect().top - this.$el.getBoundingClientRect().top,
        );
      }
      this.syncTileOutsideClose(!!code);
      if (code) {
        this.$nextTick(() => {
          const card = this.$refs.tileCard;
          if (card && typeof card.scrollIntoView === 'function') {
            card.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          }
        });
      }
    },
    isTileMap(isTiles) {
      if (!isTiles) {
        this.activeTileCode = '';
      }
      this.$nextTick(() => this.fitCompactHeight());
    },
    // The list's rows (and so the tiles' stretch) depend on how many states it can list.
    'compactListItems.length'() {
      this.$nextTick(() => this.fitCompactHeight());
    },
  },
  mounted() {
    try {
      const savedUnit = localStorage.getItem(PRICE_UNIT_STORAGE_KEY);
      if (savedUnit === PRICE_UNIT_PERCENT || savedUnit === PRICE_UNIT_CENTS) {
        this.priceUnit = savedUnit;
      }
    } catch (error) {
      log.info('cms-map-widget: localStorage unavailable, could not read the price unit.', error);
    }

    try {
      const dismissed = localStorage.getItem(PINCH_HINT_STORAGE_KEY) === 'true';
      this.shouldShowPinchHint = this.isMobileLayout() && !dismissed;
    } catch (error) {
      log.info(
        'cms-map-widget: localStorage unavailable, could not get PINCH_HINT_STORAGE_KEY state.',
        error,
      );
      this.shouldShowPinchHint = this.isMobileLayout();
    }

    // Non-reactive tooltip state (Highcharts point objects must not become reactive).
    this.tooltipIntentTimer = null;
    this.tooltipPointer = null;
    this.tooltipPointerTrail = [];
    this.tooltipAnchor = null;
    this.tooltipCardSide = '';
    this.tooltipShownPoint = null;

    this.loadMapData();

    // The tile map depends on the widget's own width (CMS regions can be narrow on desktop too).
    this.tileOutsideCloseHandler = null;
    this.summaryInfoOutsideCloseHandler = null;
    this.widgetResizeObserver = null;
    this.compactStoryObserver = null;
    if (this.$el && typeof this.$el.getBoundingClientRect === 'function') {
      this.widgetWidth = Math.round(this.$el.getBoundingClientRect().width);
      if (typeof ResizeObserver !== 'undefined') {
        this.widgetResizeObserver = new ResizeObserver((entries) => {
          const entry = entries[0];
          this.widgetWidth = entry ? Math.round(entry.contentRect.width) : 0;
          // Also when a hidden carousel story is shown (its widgets measured 0 until then).
          this.fitCompactHeight();
        });
        this.widgetResizeObserver.observe(this.$el);
      }
    }

    if (this.$refs.chart) {
      this.tooltipPointerHandler = (event) => this.trackTooltipPointer(event);
      this.$refs.chart.addEventListener('mousemove', this.tooltipPointerHandler);
    }

    if (typeof window !== 'undefined') {
      this.zoomPanelDismissHandler = (event) => {
        const control = this.$refs.zoomControl;
        if (!this.isZoomPanelOpen || !control) {
          return;
        }

        // Clicking Reset removes that button before this runs, so check the event path
        // rather than whether the (now detached) target is still inside the control.
        const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
        if (path.indexOf(control) < 0 && !control.contains(event.target)) {
          this.closeZoomPanel();
        }
      };
      document.addEventListener('click', this.zoomPanelDismissHandler);

      this.fullscreenChangeHandler = () => this.onFullscreenChange();
      document.addEventListener('fullscreenchange', this.fullscreenChangeHandler);
      document.addEventListener('webkitfullscreenchange', this.fullscreenChangeHandler);
    }

    this.$nextTick(() => {
      if (this.$refs.chart) {
        this.interactionDismissHandler = () => this.dismissPinchHint();
        this.$refs.chart.addEventListener('touchstart', this.interactionDismissHandler, {
          passive: true,
        });
        this.$refs.chart.addEventListener('mousedown', this.interactionDismissHandler);
        this.$refs.chart.addEventListener('wheel', this.interactionDismissHandler, {
          passive: true,
        });

        this.tooltipClickHandler = (event) => this.openTooltipMarket(event);
        this.$refs.chart.addEventListener('click', this.tooltipClickHandler);
      }
      this.positionToolbarMiddleLabels();
    });
  },
  updated() {
    this.positionToolbarMiddleLabels();
  },
  beforeUnmount() {
    this.stopContextMenuObserver();
    this.syncDataTableOutsideClose(false);
    this.syncTileOutsideClose(false);
    this.syncSummaryInfoOutsideClose(false);
    if (this.widgetResizeObserver) {
      this.widgetResizeObserver.disconnect();
      this.widgetResizeObserver = null;
    }
    if (this.compactStoryObserver) {
      this.compactStoryObserver.disconnect();
      this.compactStoryObserver = null;
    }
    this.clearTooltipIntent();

    if (this.$refs.chart && this.tooltipPointerHandler) {
      this.$refs.chart.removeEventListener('mousemove', this.tooltipPointerHandler);
      this.tooltipPointerHandler = null;
    }

    if (typeof document !== 'undefined' && this.zoomPanelDismissHandler) {
      document.removeEventListener('click', this.zoomPanelDismissHandler);
      this.zoomPanelDismissHandler = null;
    }

    if (typeof document !== 'undefined' && this.fullscreenChangeHandler) {
      document.removeEventListener('fullscreenchange', this.fullscreenChangeHandler);
      document.removeEventListener('webkitfullscreenchange', this.fullscreenChangeHandler);
      this.fullscreenChangeHandler = null;
    }

    if (this.$refs.chart && this.interactionDismissHandler) {
      this.$refs.chart.removeEventListener('touchstart', this.interactionDismissHandler);
      this.$refs.chart.removeEventListener('mousedown', this.interactionDismissHandler);
      this.$refs.chart.removeEventListener('wheel', this.interactionDismissHandler);
    }

    if (this.$refs.chart && this.tooltipClickHandler) {
      this.$refs.chart.removeEventListener('click', this.tooltipClickHandler);
    }

    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  },
};
